import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, Modal } from '../components/Common';
import ApiKeyManager from '../components/ApiKeyManager';

// Proxy always runs locally — SaaS is for management, local for testing
// Port mapping: Dev=9xxx, Local Client=8xxx, Internal Docker=8xxx
function getProxyApiBase() {
  const port = window.location.port;
  if (port === '3081') return 'http://localhost:9200/api'; // Dev environment
  return '/proxy-api/api';                               // Production + Docker (nginx → proxy:8200)
}
function getOrchApiBase() {
  const port = window.location.port;
  if (port === '3081') return 'http://localhost:9100/api';  // Dev environment
  return '/orchestrator-api/api';                          // Production + Docker (nginx → orchestrator:8100)
}

const PROXY_API = getProxyApiBase();
const ORCH_API = getOrchApiBase();

// Helper to call proxy API with auth
function useProxyFetch() {
  const { user } = useAuth();
  return useCallback(async (path, opts = {}) => {
    const token = sessionStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const base = getProxyApiBase();
    return fetch(`${base}${path}`, { ...opts, headers });
  }, []);
}

// ─── SSE Stream Consumer ───
// Reusable helper for consuming Server-Sent Events from the AI orchestrator.
async function consumeSSEStream(url, body, token, { onToken, onToolCall, onToolResult, onDone, onError }, signal) {
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => `HTTP ${res.status}`);
      onError?.(errText);
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      let eventType = null;
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith('data: ') && eventType) {
          try {
            const data = JSON.parse(line.slice(6));
            if (eventType === 'token') onToken?.(data.text);
            else if (eventType === 'tool_call') onToolCall?.(data);
            else if (eventType === 'tool_result') onToolResult?.(data);
            else if (eventType === 'done') onDone?.(data);
            else if (eventType === 'error') onError?.(data.message);
          } catch {}
          eventType = null;
        }
      }
    }
  } catch (err) {
    if (err.name !== 'AbortError') onError?.(err.message || 'Stream failed');
  }
}

// ─── Status Badge Colors ───
const STATUS_COLORS = {
  1: '#6366f1', 2: '#22c55e', 3: '#f59e0b', 4: '#ef4444', 5: '#dc2626',
};
const METHOD_COLORS = {
  GET: '#3b82f6', POST: '#22c55e', PUT: '#f59e0b', DELETE: '#ef4444',
  PATCH: '#8b5cf6', OPTIONS: '#6b7280', HEAD: '#6b7280',
};
const SEVERITY_COLORS = {
  critical: '#dc2626', high: '#ef4444', medium: '#f59e0b', low: '#3b82f6', info: '#6b7280',
};

function StatusBadge({ status }) {
  if (!status) return <span style={{ color: 'var(--text-secondary)' }}>-</span>;
  const color = STATUS_COLORS[Math.floor(status / 100)] || '#6b7280';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
      background: `${color}20`, color,
    }}>
      {status}
    </span>
  );
}

function MethodBadge({ method }) {
  const color = METHOD_COLORS[method] || '#6b7280';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
      background: `${color}20`, color, fontFamily: 'monospace',
    }}>
      {method}
    </span>
  );
}

function SeverityBadge({ severity }) {
  const color = SEVERITY_COLORS[(severity || '').toLowerCase()] || '#6b7280';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
      background: `${color}20`, color, textTransform: 'capitalize',
    }}>
      {severity || 'unknown'}
    </span>
  );
}

// ─── Shared Styles ───
const S = {
  panel: {
    background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 12,
    overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%',
  },
  panelHeader: {
    padding: '12px 16px', borderBottom: '1px solid #2a2a4a',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#16213e',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #2a2a4a',
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8888aa',
    letterSpacing: 0.5, position: 'sticky', top: 0, background: '#1a1a2e', zIndex: 1,
  },
  td: { padding: '6px 12px', borderBottom: '1px solid #2a2a4a', verticalAlign: 'middle', color: '#e2e8f0' },
  row: (selected) => ({
    cursor: 'pointer', transition: 'background 0.1s',
    background: selected ? 'rgba(59,130,246,0.1)' : 'transparent',
  }),
  toolbar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  input: {
    padding: '6px 12px', borderRadius: 6, border: '1px solid #2a2a4a',
    background: '#16213e', color: '#e2e8f0', fontSize: 13, width: 200,
  },
  select: {
    padding: '6px 10px', borderRadius: 6, border: '1px solid #2a2a4a',
    background: '#16213e', color: '#e2e8f0', fontSize: 13,
  },
  btn: (variant) => ({
    padding: '6px 14px', borderRadius: 6, border: variant === 'danger' ? '1px solid #ef4444' : '1px solid #2a2a4a',
    background: variant === 'primary' ? '#3b82f6' : variant === 'danger' ? '#ef444420' : '#16213e',
    color: variant === 'primary' ? '#fff' : variant === 'danger' ? '#ef4444' : '#e2e8f0',
    fontSize: 13, cursor: 'pointer', fontWeight: 500, transition: 'opacity 0.2s',
  }),
  stat: {
    padding: '4px 12px', borderRadius: 6, background: '#16213e',
    fontSize: 12, color: '#8888aa', display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  emptyState: {
    textAlign: 'center', padding: 60, color: '#8888aa',
  },
  section: { marginBottom: 16 },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8888aa',
    marginBottom: 8, letterSpacing: 0.5,
  },
  card: {
    background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 10,
    padding: 16, marginBottom: 12,
  },
  tag: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11,
    fontWeight: 600, background: `${color}20`, color, marginRight: 4,
  }),
  contextMenu: {
    position: 'fixed', zIndex: 9999, background: '#1a1a2e', border: '1px solid #2a2a4a',
    borderRadius: 8, padding: 4, minWidth: 180, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  contextMenuItem: {
    padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderRadius: 4,
    color: '#e2e8f0', display: 'block', width: '100%', textAlign: 'left',
    background: 'transparent', border: 'none',
  },
  textarea: {
    width: '100%', minHeight: 200, padding: 12, borderRadius: 6,
    border: '1px solid #2a2a4a', background: '#0f3460', color: '#e2e8f0',
    fontSize: 12, fontFamily: 'monospace', lineHeight: 1.6, resize: 'vertical',
  },
  tabBar: {
    display: 'flex', borderBottom: '1px solid #2a2a4a', background: '#16213e',
    overflowX: 'auto',
  },
  tab: (active) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400,
    borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
    color: active ? '#3b82f6' : '#8888aa',
    background: 'transparent', border: 'none', borderBottomWidth: 2,
    borderBottomStyle: 'solid', borderBottomColor: active ? '#3b82f6' : 'transparent',
    whiteSpace: 'nowrap', transition: 'all 0.2s',
  }),
  monoBody: {
    flex: 1, overflow: 'auto', padding: 16, fontFamily: 'monospace', fontSize: 12,
    lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#e2e8f0',
  },
};

// ─── Repeater Helpers ───
function buildRawRequest(detail) {
  const qs = detail.query_string ? `?${detail.query_string}` : '';
  let raw = `${detail.method} ${detail.path || '/'}${qs} HTTP/1.1\r\nHost: ${detail.host}\r\n`;
  const headers = (detail.request && detail.request.headers) || detail.request_headers || {};
  if (headers && typeof headers === 'object') {
    Object.entries(headers).forEach(([k, v]) => {
      if (k.toLowerCase() !== 'host') raw += `${k}: ${v}\r\n`;
    });
  }
  const body = (detail.request && detail.request.body) || detail.request_body || '';
  if (body) raw += `\r\n${body}`;
  return raw;
}

function parseRawRequest(raw) {
  const lines = raw.split(/\r?\n/);
  if (!lines.length) return { method: 'GET', path: '/', queryString: null, headers: {}, body: null };
  const firstLine = lines[0].trim();
  const parts = firstLine.split(' ');
  const method = parts[0] || 'GET';
  const fullPath = parts[1] || '/';
  const qIdx = fullPath.indexOf('?');
  const path = qIdx >= 0 ? fullPath.substring(0, qIdx) : fullPath;
  const queryString = qIdx >= 0 ? fullPath.substring(qIdx + 1) : null;
  const headers = {};
  let i = 1;
  for (; i < lines.length; i++) {
    if (lines[i].trim() === '') { i++; break; }
    const colonIdx = lines[i].indexOf(':');
    if (colonIdx > 0) {
      headers[lines[i].substring(0, colonIdx).trim()] = lines[i].substring(colonIdx + 1).trim();
    }
  }
  const body = lines.slice(i).join('\n').trim() || null;
  return { method, path, queryString, headers, body };
}

// ─── Context Menu ───
function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div ref={ref} style={{ ...S.contextMenu, left: x, top: y }}>
      {items.map((item, i) => item.separator ? (
        <div key={i} style={{ borderTop: '1px solid #2a2a4a', margin: '4px 0' }} />
      ) : (
        <button key={i} style={S.contextMenuItem}
          onMouseOver={e => e.currentTarget.style.background = '#0f3460'}
          onMouseOut={e => e.currentTarget.style.background = 'transparent'}
          onClick={() => { item.onClick(); onClose(); }}>
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ─── Helpers: parse query string, cookies, body params ───
function parseQueryParams(qs) {
  if (!qs) return [];
  return qs.split('&').filter(Boolean).map(pair => {
    const eqIdx = pair.indexOf('=');
    if (eqIdx < 0) return { name: decodeURIComponent(pair), value: '' };
    return { name: decodeURIComponent(pair.substring(0, eqIdx)), value: decodeURIComponent(pair.substring(eqIdx + 1)) };
  });
}

function parseCookies(headers) {
  if (!headers) return [];
  const cookies = [];
  // Request cookies from Cookie header
  const cookieStr = headers['Cookie'] || headers['cookie'] || '';
  if (cookieStr) {
    cookieStr.split(';').map(c => c.trim()).filter(Boolean).forEach(c => {
      const eqIdx = c.indexOf('=');
      if (eqIdx > 0) cookies.push({ name: c.substring(0, eqIdx).trim(), value: c.substring(eqIdx + 1).trim(), source: 'request' });
      else cookies.push({ name: c.trim(), value: '', source: 'request' });
    });
  }
  return cookies;
}

function parseResponseCookies(headers) {
  if (!headers) return [];
  const cookies = [];
  // set-cookie may appear as single string or array
  const setCookies = headers['Set-Cookie'] || headers['set-cookie'] || '';
  const items = Array.isArray(setCookies) ? setCookies : (setCookies ? [setCookies] : []);
  items.forEach(sc => {
    const parts = sc.split(';');
    const mainPart = parts[0] || '';
    const eqIdx = mainPart.indexOf('=');
    const name = eqIdx > 0 ? mainPart.substring(0, eqIdx).trim() : mainPart.trim();
    const value = eqIdx > 0 ? mainPart.substring(eqIdx + 1).trim() : '';
    const attrs = parts.slice(1).map(p => p.trim()).filter(Boolean);
    cookies.push({ name, value, attrs, source: 'response' });
  });
  return cookies;
}

function parseBodyParams(body, contentType) {
  if (!body) return [];
  if (contentType && contentType.includes('application/x-www-form-urlencoded')) {
    return parseQueryParams(body);
  }
  if (contentType && contentType.includes('application/json')) {
    try {
      const obj = JSON.parse(body);
      if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
        return Object.entries(obj).map(([k, v]) => ({ name: k, value: typeof v === 'string' ? v : JSON.stringify(v) }));
      }
    } catch { /* ignore */ }
  }
  return [];
}

// ─── Key-Value Table Component ───
function KVTable({ items, nameLabel = 'Name', valueLabel = 'Value', extraCols, onTamperParam }) {
  const [ctxMenu, setCtxMenu] = useState(null);
  const [subMenu, setSubMenu] = useState(null); // { attack: 'xss'|'sqli'|'fuzz' }

  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => { setCtxMenu(null); setSubMenu(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [ctxMenu]);

  const INTENSITIES = [
    { key: 'quick', label: '\u{26A1} Quick Scan', desc: '10 best payloads', color: '#22c55e' },
    { key: 'moderate', label: '\u{1F504} Moderate Scan', desc: '100 payloads', color: '#f59e0b' },
    { key: 'full', label: '\u{1F4A5} Full Scan', desc: 'All from KB', color: '#ef4444' },
  ];

  const ATTACKS = [
    { label: '\u{1F916} Tamper with AI', desc: 'AI chooses attack', attack: null, hasIntensity: false },
    { label: '\u{26A1} Test for XSS', desc: 'Cross-site scripting', attack: 'xss', hasIntensity: true },
    { label: '\u{1F489} Test for SQLi', desc: 'SQL injection', attack: 'sqli', hasIntensity: true },
    { label: '\u{1F50D} Fuzz parameter', desc: 'Common payloads', attack: 'fuzz', hasIntensity: true },
  ];

  if (!items || items.length === 0) return <div style={{ color: '#8888aa', fontSize: 12, padding: '8px 0' }}>None</div>;
  return (
    <>
      <table style={{ ...S.table, fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...S.th, fontSize: 10, padding: '4px 8px' }}>{nameLabel}</th>
            <th style={{ ...S.th, fontSize: 10, padding: '4px 8px' }}>{valueLabel}</th>
            {extraCols && extraCols.map(col => (
              <th key={col} style={{ ...S.th, fontSize: 10, padding: '4px 8px' }}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}
              onContextMenu={onTamperParam ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                setSubMenu(null);
                setCtxMenu({ x: e.clientX, y: e.clientY, param: item });
              } : undefined}
              style={{ cursor: onTamperParam ? 'context-menu' : undefined }}
            >
              <td style={{ ...S.td, padding: '3px 8px', fontFamily: 'monospace', color: '#8b5cf6', maxWidth: 200, wordBreak: 'break-all' }}>{item.name}</td>
              <td style={{ ...S.td, padding: '3px 8px', fontFamily: 'monospace', maxWidth: 400, wordBreak: 'break-all' }}>{item.value}</td>
              {extraCols && extraCols.map(col => (
                <td key={col} style={{ ...S.td, padding: '3px 8px', fontSize: 11, color: '#8888aa' }}>{item[col.toLowerCase()] || ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Parameter context menu with intensity sub-menu */}
      {ctxMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }} onClick={() => { setCtxMenu(null); setSubMenu(null); }} />
          <div style={{
            position: 'fixed', left: ctxMenu.x, top: ctxMenu.y, zIndex: 10000,
            background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,.5)', padding: '4px 0', minWidth: 220,
          }}>
            <div style={{ padding: '4px 12px', fontSize: 10, color: '#8888aa', fontFamily: 'monospace', borderBottom: '1px solid #2a2a4a', marginBottom: 2 }}>
              {ctxMenu.param.name} = {(ctxMenu.param.value || '').slice(0, 40)}{(ctxMenu.param.value || '').length > 40 ? '...' : ''}
            </div>
            {ATTACKS.map((opt, i) => (
              <div key={i} style={{ position: 'relative' }}
                onMouseEnter={() => opt.hasIntensity && setSubMenu({ attack: opt.attack })}
                onMouseLeave={() => opt.hasIntensity && setSubMenu(null)}
              >
                <div
                  style={{
                    padding: '6px 12px', fontSize: 12, color: '#e2e8f0', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: subMenu?.attack === opt.attack ? '#16213e' : 'transparent',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#16213e'}
                  onMouseOut={e => { if (subMenu?.attack !== opt.attack) e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => {
                    if (!opt.hasIntensity) {
                      setCtxMenu(null); setSubMenu(null);
                      onTamperParam(ctxMenu.param, opt.attack, null);
                    }
                  }}
                >
                  <span>{opt.label}</span>
                  <span style={{ fontSize: 9, color: '#8888aa' }}>
                    {opt.hasIntensity ? '\u25B6' : opt.desc}
                  </span>
                </div>

                {/* Intensity sub-menu */}
                {subMenu?.attack === opt.attack && (
                  <div style={{
                    position: 'absolute', left: '100%', top: -4, zIndex: 10001,
                    background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,.5)', padding: '4px 0', minWidth: 200,
                  }}>
                    <div style={{ padding: '4px 12px', fontSize: 9, color: '#8888aa', borderBottom: '1px solid #2a2a4a', marginBottom: 2 }}>
                      Scan Intensity
                    </div>
                    {INTENSITIES.map((int, j) => (
                      <div key={j}
                        style={{ padding: '6px 12px', fontSize: 12, color: '#e2e8f0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        onMouseOver={e => e.currentTarget.style.background = '#16213e'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCtxMenu(null); setSubMenu(null);
                          onTamperParam(ctxMenu.param, opt.attack, int.key);
                        }}
                      >
                        <span>{int.label}</span>
                        <span style={{ fontSize: 9, color: int.color }}>{int.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ─── Reusable Request Detail Panel ───
// Shows query params, body params, cookies, request headers, response headers, attributes
// Can be used in History, Repeater, and Intercept
function RequestDetailPanel({ detail, responseData, compact, onTamperParam }) {
  const [subTab, setSubTab] = useState('params');

  // Parse from detail (full request detail from API) or from raw parsed data
  const reqHeaders = detail?.request?.headers || detail?.request_headers || detail?.headers || {};
  const resHeaders = responseData?.response_headers || detail?.response?.headers || {};
  // Try query_string field, fall back to parsing from URL
  let queryString = detail?.query_string || '';
  if (!queryString && detail?.url) {
    const qIdx = detail.url.indexOf('?');
    if (qIdx >= 0) queryString = detail.url.substring(qIdx + 1);
  }
  if (!queryString && detail?.path) {
    const qIdx = detail.path.indexOf('?');
    if (qIdx >= 0) queryString = detail.path.substring(qIdx + 1);
  }
  const reqBody = detail?.request?.body || detail?.request_body || detail?.body || '';
  const contentType = detail?.request?.content_type || detail?.request_content_type || reqHeaders['Content-Type'] || reqHeaders['content-type'] || '';
  const resContentType = detail?.response?.content_type || responseData?.response_content_type || resHeaders['Content-Type'] || resHeaders['content-type'] || '';

  const queryParams = parseQueryParams(queryString);
  const bodyParams = parseBodyParams(reqBody, contentType);
  const requestCookies = parseCookies(reqHeaders);
  const responseCookies = parseResponseCookies(resHeaders);
  const allCookies = [...requestCookies, ...responseCookies];

  // Attributes: method, URL, scheme, host, port, content-type, response status, etc.
  const attributes = [];
  if (detail?.method) attributes.push({ name: 'Method', value: detail.method });
  if (detail?.scheme) attributes.push({ name: 'Scheme', value: detail.scheme });
  if (detail?.host) attributes.push({ name: 'Host', value: detail.host });
  if (detail?.port) attributes.push({ name: 'Port', value: String(detail.port) });
  if (detail?.path) attributes.push({ name: 'Path', value: detail.path });
  if (queryString) attributes.push({ name: 'Query String', value: queryString });
  if (detail?.is_tls !== undefined) attributes.push({ name: 'TLS', value: detail.is_tls ? 'Yes' : 'No' });
  if (contentType) attributes.push({ name: 'Content-Type', value: contentType });
  if (detail?.source) attributes.push({ name: 'Source', value: detail.source });
  if (detail?.duration_ms != null) attributes.push({ name: 'Duration', value: `${detail.duration_ms}ms` });
  const respStatus = responseData?.response_status || detail?.response?.status || detail?.response_status_code;
  if (respStatus) attributes.push({ name: 'Response Status', value: String(respStatus) });
  if (resContentType) attributes.push({ name: 'Response Content-Type', value: resContentType });
  const respLen = responseData?.content_length || detail?.response?.content_length || detail?.response_content_length;
  if (respLen != null) attributes.push({ name: 'Response Length', value: respLen > 1024 ? `${(respLen / 1024).toFixed(1)} KB` : `${respLen} B` });

  const tabs = [
    { key: 'params', label: 'Params', count: queryParams.length + bodyParams.length },
    { key: 'cookies', label: 'Cookies', count: allCookies.length },
    { key: 'attributes', label: 'Attributes', count: attributes.length },
    { key: 'reqHeaders', label: 'Req Headers', count: Object.keys(reqHeaders).length },
    { key: 'resHeaders', label: 'Res Headers', count: Object.keys(resHeaders).length },
  ];

  const dpStyle = {
    container: {
      background: '#0d1117', borderRadius: 8, border: '1px solid #2a2a4a',
      overflow: 'hidden', fontSize: 12,
    },
    tabBar: {
      display: 'flex', borderBottom: '1px solid #2a2a4a', background: '#16213e',
      overflowX: 'auto', flexWrap: 'nowrap',
    },
    tab: (active) => ({
      padding: '6px 12px', cursor: 'pointer', fontSize: 11, fontWeight: active ? 600 : 400,
      color: active ? '#3b82f6' : '#8888aa', background: 'transparent', border: 'none',
      borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
      whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4,
    }),
    count: (active) => ({
      fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
      background: active ? '#3b82f620' : '#2a2a4a', color: active ? '#3b82f6' : '#8888aa',
    }),
    body: {
      padding: 8, maxHeight: compact ? 200 : 300, overflow: 'auto',
    },
  };

  const renderHeadersTable = (headers, location) => {
    if (!headers || Object.keys(headers).length === 0)
      return <div style={{ color: '#8888aa', fontSize: 12, padding: '8px 0' }}>None</div>;
    const items = Object.entries(headers).map(([k, v]) => ({ name: k, value: String(v) }));
    return <KVTable items={items} nameLabel="Header" valueLabel="Value"
      onTamperParam={onTamperParam && location === 'request' ? (param, attack, intensity) => onTamperParam({ ...param, location: 'header' }, attack, intensity) : undefined}
    />;
  };

  return (
    <div style={dpStyle.container}>
      <div style={dpStyle.tabBar}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={dpStyle.tab(subTab === t.key)}>
            {t.label}
            {t.count > 0 && <span style={dpStyle.count(subTab === t.key)}>{t.count}</span>}
          </button>
        ))}
      </div>
      <div style={dpStyle.body}>
        {subTab === 'params' && (
          <>
            {queryParams.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ ...S.sectionTitle, marginBottom: 4 }}>Query Parameters</div>
                <KVTable items={queryParams} onTamperParam={onTamperParam ? (param, attack, intensity) => onTamperParam({ ...param, location: 'query' }, attack, intensity) : undefined} />
              </div>
            )}
            {bodyParams.length > 0 && (
              <div>
                <div style={{ ...S.sectionTitle, marginBottom: 4 }}>Body Parameters</div>
                <KVTable items={bodyParams} onTamperParam={onTamperParam ? (param, attack, intensity) => onTamperParam({ ...param, location: 'body' }, attack, intensity) : undefined} />
              </div>
            )}
            {queryParams.length === 0 && bodyParams.length === 0 && (
              <div style={{ color: '#8888aa', fontSize: 12, padding: '8px 0' }}>No parameters</div>
            )}
          </>
        )}
        {subTab === 'cookies' && (
          <>
            {requestCookies.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ ...S.sectionTitle, marginBottom: 4 }}>Request Cookies</div>
                <KVTable items={requestCookies} />
              </div>
            )}
            {responseCookies.length > 0 && (
              <div>
                <div style={{ ...S.sectionTitle, marginBottom: 4 }}>Set-Cookie (Response)</div>
                <KVTable items={responseCookies} extraCols={['Attrs']}
                  // Re-map attrs to a flat string for display
                />
                {responseCookies.map((c, i) => c.attrs && c.attrs.length > 0 ? (
                  <div key={i} style={{ fontSize: 10, color: '#8888aa', marginTop: 2, marginLeft: 8 }}>
                    {c.name}: {c.attrs.join('; ')}
                  </div>
                ) : null)}
              </div>
            )}
            {allCookies.length === 0 && (
              <div style={{ color: '#8888aa', fontSize: 12, padding: '8px 0' }}>No cookies</div>
            )}
          </>
        )}
        {subTab === 'attributes' && <KVTable items={attributes} nameLabel="Attribute" />}
        {subTab === 'reqHeaders' && renderHeadersTable(reqHeaders, 'request')}
        {subTab === 'resHeaders' && renderHeadersTable(resHeaders)}
      </div>
    </div>
  );
}

// ─── Request Viewer Panel (used in History) ───
function RequestViewer({ request, onClose, proxyFetch, onTamperParam }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [headerAnalysis, setHeaderAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  useEffect(() => {
    if (!request) return;
    setLoading(true);
    setHeaderAnalysis(null);
    proxyFetch(`/requests/${request.id}`)
      .then(r => {
        if (!r.ok) { console.error('Request detail fetch failed:', r.status, r.statusText); return null; }
        return r.json();
      })
      .then(d => { setDetail(d); setLoading(false); })
      .catch(e => { console.error('Request detail error:', e); setLoading(false); });
  }, [request, proxyFetch]);

  useEffect(() => {
    if (activeTab !== 'security' || !request || headerAnalysis) return;
    setAnalysisLoading(true);
    proxyFetch(`/requests/${request.id}/header-analysis`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setHeaderAnalysis(d); setAnalysisLoading(false); })
      .catch(() => setAnalysisLoading(false));
  }, [activeTab, request, proxyFetch, headerAnalysis]);

  if (!request) return null;

  const tabs = ['details', 'request', 'response', 'security'];

  const renderHeaders = (headers) => {
    if (!headers) return <span style={{ color: '#8888aa' }}>No headers</span>;
    return Object.entries(headers).map(([k, v]) => (
      <div key={k} style={{ display: 'flex', gap: 4, marginBottom: 2 }}>
        <span style={{ color: '#3b82f6', fontWeight: 600 }}>{k}:</span>
        <span style={{ color: '#e2e8f0' }}> {v}</span>
      </div>
    ));
  };

  const renderSecurityTab = () => {
    if (analysisLoading) return <div style={S.emptyState}>Analyzing headers...</div>;
    if (!headerAnalysis) return <div style={S.emptyState}>No analysis available</div>;
    return (
      <div>
        {headerAnalysis.missing_headers && headerAnalysis.missing_headers.length > 0 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Missing Security Headers</div>
            {headerAnalysis.missing_headers.map((h, i) => (
              <div key={i} style={{ ...S.card, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#ef4444' }}>{h.header || h}</span>
                {h.severity && <SeverityBadge severity={h.severity} />}
              </div>
            ))}
          </div>
        )}
        {headerAnalysis.present_headers && headerAnalysis.present_headers.length > 0 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Present Security Headers</div>
            {headerAnalysis.present_headers.map((h, i) => (
              <div key={i} style={{ ...S.card, padding: '8px 12px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#22c55e' }}>{h.header || h}</div>
                {h.value && <div style={{ fontSize: 11, color: '#8888aa', marginTop: 2 }}>{h.value}</div>}
              </div>
            ))}
          </div>
        )}
        {headerAnalysis.issues && headerAnalysis.issues.length > 0 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Issues</div>
            {headerAnalysis.issues.map((issue, i) => (
              <div key={i} style={{ ...S.card, padding: '8px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#e2e8f0' }}>{issue.title || issue.description || issue}</span>
                  {issue.severity && <SeverityBadge severity={issue.severity} />}
                </div>
                {issue.recommendation && <div style={{ fontSize: 11, color: '#8888aa', marginTop: 4 }}>{issue.recommendation}</div>}
              </div>
            ))}
          </div>
        )}
        {headerAnalysis.score != null && (
          <div style={{ padding: '8px 12px', background: '#16213e', borderRadius: 6, fontSize: 13 }}>
            Security Score: <strong style={{ color: headerAnalysis.score >= 70 ? '#22c55e' : headerAnalysis.score >= 40 ? '#f59e0b' : '#ef4444' }}>{headerAnalysis.score}/100</strong>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={S.panel}>
      <div style={S.panelHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
          <MethodBadge method={request.method} />
          <StatusBadge status={request.response_status} />
          <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#e2e8f0' }}>
            {request.url || `${request.host}${request.path}`}
          </span>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#8888aa', padding: 4,
        }}>&#x2715;</button>
      </div>

      <div style={S.tabBar}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={S.tab(activeTab === t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div style={S.monoBody}>
        {loading ? (
          <div style={S.emptyState}>Loading...</div>
        ) : !detail ? (
          <div style={S.emptyState}>Failed to load request details</div>
        ) : activeTab === 'details' ? (
          <>
            <RequestDetailPanel detail={detail} onTamperParam={onTamperParam ? (param, attack, intensity) => onTamperParam({ ...param, request: detail }, attack, intensity) : undefined} />
          </>
        ) : activeTab === 'request' ? (
          <>
            <div style={S.section}>
              <div style={S.sectionTitle}>Request Line</div>
              <div>{detail.method} {detail.path}{detail.query_string ? '?' + detail.query_string : ''} HTTP/1.1</div>
              <div>Host: {detail.host}</div>
            </div>
            <div style={S.section}>
              <div style={S.sectionTitle}>Request Headers</div>
              {renderHeaders(detail.request?.headers)}
            </div>
            {detail.request?.body && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Request Body ({detail.request.content_type || 'unknown'})</div>
                <div style={{ background: '#16213e', padding: 12, borderRadius: 6 }}>{detail.request.body}</div>
              </div>
            )}
          </>
        ) : activeTab === 'response' ? (
          <>
            <div style={S.section}>
              <div style={S.sectionTitle}>Status</div>
              <div>HTTP/1.1 {detail.response?.status} {detail.response?.reason}</div>
            </div>
            <div style={S.section}>
              <div style={S.sectionTitle}>Response Headers</div>
              {renderHeaders(detail.response?.headers)}
            </div>
            {detail.response?.body && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Response Body ({detail.response.content_type || 'unknown'})</div>
                <div style={{ background: '#16213e', padding: 12, borderRadius: 6, maxHeight: 500, overflow: 'auto' }}>{detail.response.body}</div>
              </div>
            )}
          </>
        ) : renderSecurityTab()}
        {detail && (
          <div style={{ marginTop: 16, padding: '8px 12px', background: '#16213e', borderRadius: 6, fontSize: 11, color: '#8888aa' }}>
            Duration: {detail.duration_ms ?? '-'}ms | TLS: {detail.is_tls ? 'Yes' : 'No'} | Source: {detail.source || 'proxy'}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Session Selector ───
function SessionSelector({ sessions, activeSession, onSelect, onCreate, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <select
        value={activeSession || ''}
        onChange={e => onSelect(e.target.value)}
        style={S.select}
      >
        <option value="">Select session...</option>
        {sessions.map(s => (
          <option key={s.id} value={s.id}>
            {s.name} ({s.request_count} reqs){s.is_active ? ' *' : ''}
          </option>
        ))}
      </select>
      {showCreate ? (
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="Session name..."
            style={{ ...S.input, width: 160 }}
            onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) { onCreate(newName.trim()); setNewName(''); setShowCreate(false); } }}
            autoFocus
          />
          <button style={S.btn()} onClick={() => { if (newName.trim()) { onCreate(newName.trim()); setNewName(''); setShowCreate(false); } }}>+</button>
          <button style={S.btn()} onClick={() => setShowCreate(false)}>&#x2715;</button>
        </div>
      ) : (
        <button style={S.btn()} onClick={() => setShowCreate(true)}>+ New Session</button>
      )}
      <button style={S.btn()} onClick={onRefresh}>&#x21bb;</button>
    </div>
  );
}

// ─── Engagement Selector ───
function EngagementSelector({ proxyFetch, activeSessionId, onScopeChange }) {
  const [engagements, setEngagements] = useState([]);
  const [selectedEngagement, setSelectedEngagement] = useState('');
  const [scopeRules, setScopeRules] = useState([]);
  const [scopeEnforced, setScopeEnforced] = useState(true);

  useEffect(() => {
    proxyFetch('/scope/engagements')
      .then(r => r.ok ? r.json() : [])
      .then(d => setEngagements(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [proxyFetch]);

  // Load scope rules and engagement binding from session
  useEffect(() => {
    if (!activeSessionId) return;
    let loadedRules = [];
    let loadedEngId = '';
    let loadedEnforced = true;

    // Load both session details and scope rules, then fire one consistent onScopeChange
    const loadSession = proxyFetch(`/sessions/${activeSessionId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          if (d.engagement_id) {
            loadedEngId = d.engagement_id;
            setSelectedEngagement(d.engagement_id);
          }
          // Default to true if server doesn't have a value yet
          loadedEnforced = d.scope_enforced !== false;
          setScopeEnforced(loadedEnforced);
        }
      })
      .catch(() => {});

    const loadScope = proxyFetch(`/sessions/${activeSessionId}/scope`)
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        loadedRules = Array.isArray(d) ? d : [];
        setScopeRules(loadedRules);
      })
      .catch(() => {});

    Promise.all([loadSession, loadScope]).then(async () => {
      // If we have scope rules and enforcement isn't set on server, enable it
      if (loadedRules.length > 0 && loadedEnforced) {
        await proxyFetch(`/sessions/${activeSessionId}/scope-enforced?enabled=true`, { method: 'PUT' }).catch(() => {});
      }
      onScopeChange && onScopeChange({
        hasScope: loadedRules.length > 0,
        enforced: loadedRules.length > 0 ? loadedEnforced : false,
        engagementId: loadedEngId,
      });
    });
  }, [proxyFetch, activeSessionId]);

  const handleChange = async (engId) => {
    setSelectedEngagement(engId);
    if (activeSessionId && engId) {
      const res = await proxyFetch(`/sessions/${activeSessionId}/engagement`, {
        method: 'PUT',
        body: JSON.stringify({ engagement_id: engId }),
      });
      if (res.ok) {
        // Reload scope rules after binding
        const scopeRes = await proxyFetch(`/sessions/${activeSessionId}/scope`);
        if (scopeRes.ok) {
          const rules = await scopeRes.json();
          const rulesList = Array.isArray(rules) ? rules : [];
          setScopeRules(rulesList);
          // Auto-enforce scope when engagement is selected
          setScopeEnforced(true);
          await proxyFetch(`/sessions/${activeSessionId}/scope-enforced?enabled=true`, { method: 'PUT' });
          const engName = (engagements.find(e => e.id === engId) || {}).name || '';
          onScopeChange && onScopeChange({ hasScope: rulesList.length > 0, enforced: true, engagementId: engId, engagementName: engName });
        }
      }
    } else if (activeSessionId && !engId) {
      // Unbinding — disable scope enforcement
      setScopeEnforced(false);
      setScopeRules([]);
      await proxyFetch(`/sessions/${activeSessionId}/scope-enforced?enabled=false`, { method: 'PUT' });
      onScopeChange && onScopeChange({ hasScope: false, enforced: false, engagementId: '' });
    }
  };

  const toggleEnforcement = async () => {
    const newVal = !scopeEnforced;
    setScopeEnforced(newVal);
    if (activeSessionId) {
      await proxyFetch(`/sessions/${activeSessionId}/scope-enforced?enabled=${newVal}`, { method: 'PUT' });
    }
    onScopeChange && onScopeChange({ hasScope: scopeRules.length > 0, enforced: newVal, engagementId: selectedEngagement });
  };

  // Extract unique host patterns from scope rules for display
  const hostPatterns = scopeRules.filter(r => r.target === 'host' && r.rule_type === 'include');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 12, color: '#8888aa' }}>Engagement:</span>
      <select style={S.select} value={selectedEngagement} onChange={e => handleChange(e.target.value)}>
        <option value="">None</option>
        {engagements.map(eng => (
          <option key={eng.id} value={eng.id}>{eng.name || eng.title}</option>
        ))}
      </select>
      {scopeRules.length > 0 && (
        <>
          <span style={{ fontSize: 11, color: '#22c55e' }}>
            Scope: {scopeRules.length} rule{scopeRules.length !== 1 ? 's' : ''}
            {hostPatterns.length > 0 && ` (${hostPatterns.length} host${hostPatterns.length !== 1 ? 's' : ''})`}
          </span>
          <button
            style={{
              ...S.btn(scopeEnforced ? 'primary' : undefined),
              padding: '3px 10px', fontSize: 11,
            }}
            onClick={toggleEnforcement}
            title={scopeEnforced ? 'Scope enforced — only in-scope traffic shown' : 'Scope not enforced — all traffic shown'}
          >
            {scopeEnforced ? '🔒 Scope On' : '🔓 Scope Off'}
          </button>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 1: HISTORY
// ═══════════════════════════════════════════════════════════
function HistoryTab({ proxyFetch, activeSessionId, wsRef, scopeEnforced, sendToRepeater, sendToIntruder, engagementId, onAskAI }) {
  const [history, setHistory] = useState([]);
  const [aiAnalysisDetail, setAiAnalysisDetail] = useState(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [contextMenu, setContextMenu] = useState(null);

  // Use refs for filter values so loadHistory doesn't re-create on every keystroke
  const filtersRef = useRef({ search: '', method: '', status: '' });

  const loadHistory = useCallback(async () => {
    if (!activeSessionId) return;
    setHistoryLoading(true);
    const f = filtersRef.current;
    try {
      let url = `/history?session_id=${activeSessionId}&limit=200`;
      if (f.search) url += `&search=${encodeURIComponent(f.search)}`;
      if (f.method) url += `&method=${f.method}`;
      if (f.status === '2xx') url += '&status_min=200&status_max=299';
      else if (f.status === '3xx') url += '&status_min=300&status_max=399';
      else if (f.status === '4xx') url += '&status_min=400&status_max=499';
      else if (f.status === '5xx') url += '&status_min=500&status_max=599';
      if (scopeEnforced) url += '&in_scope=true';
      const res = await proxyFetch(url);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.items || []);
        setHistoryTotal(data.total || 0);
      }
    } catch {}
    setHistoryLoading(false);
  }, [proxyFetch, activeSessionId, scopeEnforced]);

  // Load on mount, session change, or scope change
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Apply filters: snapshot current UI state into ref then reload
  const applyFilters = useCallback(() => {
    filtersRef.current = { search: searchTerm, method: methodFilter, status: statusFilter };
    loadHistory();
  }, [searchTerm, methodFilter, statusFilter, loadHistory]);

  // Reset filters: reset UI state, clear ref, reload with no filters
  const resetFilters = useCallback(async () => {
    setSearchTerm('');
    setMethodFilter('');
    setStatusFilter('');
    filtersRef.current = { search: '', method: '', status: '' };
    if (!activeSessionId) return;
    setHistoryLoading(true);
    try {
      let url = `/history?session_id=${activeSessionId}&limit=200`;
      if (scopeEnforced) url += '&in_scope=true';
      const res = await proxyFetch(url);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.items || []);
        setHistoryTotal(data.total || 0);
      }
    } catch {}
    setHistoryLoading(false);
  }, [proxyFetch, activeSessionId, scopeEnforced]);

  // Clear history: delete ALL requests from the session
  const clearHistory = useCallback(async () => {
    if (!activeSessionId) return;
    if (!window.confirm('Delete ALL history for this session? This cannot be undone.')) return;
    try {
      const res = await proxyFetch(`/history?session_id=${activeSessionId}`, { method: 'DELETE' });
      if (res.ok) {
        setHistory([]);
        setHistoryTotal(0);
        setSelectedRequest(null);
      }
    } catch (e) { console.error('Clear history failed:', e); }
  }, [proxyFetch, activeSessionId]);

  // Listen for new_request WS messages
  useEffect(() => {
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'new_request') {
          // If scope is enforced, only show in-scope requests
          if (scopeEnforced && msg.data.in_scope === false) return;
          setHistory(prev => [msg.data, ...prev].slice(0, 500));
          setHistoryTotal(prev => prev + 1);
        }
      } catch {}
    };
    const ws = wsRef.current;
    if (ws) ws.addEventListener('message', handler);
    return () => { if (ws) ws.removeEventListener('message', handler); };
  }, [wsRef.current, scopeEnforced]);

  const startCrawlFromRequest = async (req) => {
    const scheme = req.is_tls ? 'https' : 'http';
    const port = req.port && req.port !== 80 && req.port !== 443 ? `:${req.port}` : '';
    const baseUrl = `${scheme}://${req.host}${port}${req.path || '/'}`;
    try {
      const res = await proxyFetch(`/crawl/start?session_id=${activeSessionId}`, {
        method: 'POST',
        body: JSON.stringify({ base_urls: [baseUrl], max_depth: 3, max_pages: 100, concurrency: 5 }),
      });
      if (res.ok) {
        const data = await res.json();
        console.log(`Crawl started from ${baseUrl}: job ${data.job_id}`);
      }
    } catch (err) { console.error('startCrawl error:', err); }
  };

  const handleContextMenu = (e, req) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX, y: e.clientY,
      items: [
        { label: 'View Details', onClick: () => setSelectedRequest(req) },
        { label: 'Copy URL', onClick: () => navigator.clipboard.writeText(`${req.is_tls ? 'https' : 'http'}://${req.host}${req.path}${req.query_string ? '?' + req.query_string : ''}`) },
        { separator: true },
        { label: 'Scan for SQLi', onClick: () => triggerScan(req, 'sqli') },
        { label: 'Scan for XSS', onClick: () => triggerScan(req, 'xss') },
        { separator: true },
        { label: 'Send to Repeater', onClick: () => sendToRepeater && sendToRepeater(req) },
        { label: 'Send to Intruder', onClick: () => sendToIntruder && sendToIntruder(req) },
        { label: 'Send to Intercept', onClick: () => {} },
        { separator: true },
        { label: 'Crawl from URL', onClick: () => startCrawlFromRequest(req) },
        { separator: true },
        { label: '\u{1F916} Analyze with AI', onClick: () => analyzeWithAI(req) },
      ],
    });
  };

  const analyzeWithAI = async (req) => {
    try {
      const res = await proxyFetch(`/requests/${req.id}`);
      if (res.ok) {
        const detail = await res.json();
        setAiAnalysisDetail(detail);
      }
    } catch {}
  };

  const triggerScan = async (req, scanType) => {
    try {
      const res = await proxyFetch(`/scanner/request?session_id=${activeSessionId}`, {
        method: 'POST',
        body: JSON.stringify({ request_id: req.id, test_types: [scanType] }),
      });
      if (res.ok) {
        const data = await res.json();
        console.log(`Scan started: job ${data.job_id}, ${data.parameters_found} params found`);
      }
    } catch (err) { console.error('triggerScan error:', err); }
  };

  const splitStyle = {
    display: 'flex', gap: 16, height: 'calc(100vh - 340px)', minHeight: 400,
  };

  return (
    <div>
      <div style={S.toolbar}>
        <input
          style={S.input}
          placeholder="Search host or path..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
        />
        <select style={S.select} value={methodFilter} onChange={e => { setMethodFilter(e.target.value); }}>
          <option value="">All Methods</option>
          {['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'].map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select style={S.select} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); }}>
          <option value="">All Status</option>
          <option value="2xx">2xx Success</option>
          <option value="3xx">3xx Redirect</option>
          <option value="4xx">4xx Client Error</option>
          <option value="5xx">5xx Server Error</option>
        </select>
        <button style={S.btn()} onClick={applyFilters}>Filter</button>
        <button style={S.btn()} onClick={resetFilters}>Reset</button>
        <span style={{ flex: 1 }} />
        <span style={S.stat}>{historyTotal} requests</span>
        <button style={S.btn('danger')} onClick={clearHistory}>Clear History</button>
      </div>

      <div style={splitStyle}>
        <div style={{
          flex: selectedRequest ? '0 0 55%' : 1, overflow: 'auto',
          background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a',
        }}>
          {historyLoading && history.length === 0 ? (
            <div style={S.emptyState}>Loading history...</div>
          ) : history.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>No requests captured yet.</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Configure your browser to use <code>localhost:8083</code> as the proxy.</div>
            </div>
          ) : (
            <>
            {/* Input indicator legend */}
            <div style={{ display: 'flex', gap: 12, padding: '4px 8px', fontSize: 10, color: '#8888aa' }}>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#f59e0b33', borderLeft: '2px solid #f59e0b', marginRight: 4 }} />Query params</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#3b82f633', borderLeft: '2px solid #3b82f6', marginRight: 4 }} />Body input (POST/PUT)</span>
            </div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Method</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Host</th>
                  <th style={S.th}>Path</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Size</th>
                  <th style={S.th}>Time</th>
                </tr>
              </thead>
              <tbody>
                {history.map((req, i) => {
                  const hasParams = !!(req.query_string);
                  const hasBody = ['POST', 'PUT', 'PATCH'].includes(req.method);
                  const hasInput = hasParams || hasBody;
                  // Left border: orange for params, blue for POST body, none for plain GET
                  const inputBorder = hasParams ? '2px solid #f59e0b33' : hasBody ? '2px solid #3b82f633' : '2px solid transparent';
                  return (
                  <tr
                    key={req.id}
                    style={{ ...S.row(selectedRequest?.id === req.id), borderLeft: inputBorder }}
                    onClick={() => setSelectedRequest(req)}
                    onContextMenu={(e) => handleContextMenu(e, req)}
                    onMouseOver={e => { if (selectedRequest?.id !== req.id) e.currentTarget.style.background = '#16213e'; }}
                    onMouseOut={e => { if (selectedRequest?.id !== req.id) e.currentTarget.style.background = hasInput ? (hasParams ? '#f59e0b05' : '#3b82f605') : 'transparent'; }}
                  >
                    <td style={{ ...S.td, color: '#8888aa', fontSize: 11, width: 40 }}>{historyTotal - i}</td>
                    <td style={S.td}><MethodBadge method={req.method} /></td>
                    <td style={S.td}><StatusBadge status={req.response_status} /></td>
                    <td style={{ ...S.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={req.host}>{req.host}</td>
                    <td style={{ ...S.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }} title={req.path + (req.query_string ? '?' + req.query_string : '')}>
                      {req.path}{req.query_string ? '?' + req.query_string : ''}
                    </td>
                    <td style={{ ...S.td, fontSize: 11, color: '#8888aa', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(req.response_content_type || '').split(';')[0].replace('application/', '').replace('text/', '')}
                    </td>
                    <td style={{ ...S.td, fontSize: 11, color: '#8888aa', whiteSpace: 'nowrap' }}>
                      {req.response_content_length > 1024 ? `${(req.response_content_length / 1024).toFixed(1)}K` : `${req.response_content_length || 0}B`}
                    </td>
                    <td style={{ ...S.td, fontSize: 11, color: '#8888aa', whiteSpace: 'nowrap' }}>
                      {req.duration_ms != null ? `${req.duration_ms}ms` : '-'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </>
          )}
        </div>

        {selectedRequest && (
          <div style={{ flex: '0 0 44%', minWidth: 0 }}>
            <RequestViewer request={selectedRequest} onClose={() => setSelectedRequest(null)} proxyFetch={proxyFetch}
              onTamperParam={onAskAI ? (paramInfo, attack, intensity) => {
                const req = paramInfo.request || selectedRequest;
                const url = `${req?.is_tls || req?.scheme === 'https' ? 'https' : 'http'}://${req?.host || ''}${req?.path || '/'}${req?.query_string ? '?' + req.query_string : ''}`;
                const attackLabel = attack === 'xss' ? 'XSS' : attack === 'sqli' ? 'SQL injection' : attack === 'fuzz' ? 'fuzzing' : 'vulnerability';
                const intensityTag = intensity ? ` [intensity: ${intensity}]` : '';
                const prompt = attack
                  ? `Test the "${paramInfo.name}" ${paramInfo.location || 'parameter'} at ${url} for ${attackLabel}${intensityTag}. Current value: "${(paramInfo.value || '').slice(0, 100)}"`
                  : `Analyze the "${paramInfo.name}" ${paramInfo.location || 'parameter'} at ${url} and suggest the best attack approach. Current value: "${(paramInfo.value || '').slice(0, 100)}"`;
                onAskAI({ url, path: req?.path, paramName: paramInfo.name, paramValue: paramInfo.value, paramLocation: paramInfo.location, attack, intensity, suggestedPrompt: prompt });
              } : undefined}
            />
          </div>
        )}
      </div>

      {contextMenu && (
        <ContextMenu x={contextMenu.x} y={contextMenu.y} items={contextMenu.items} onClose={() => setContextMenu(null)} />
      )}

      {aiAnalysisDetail && (
        <AIRequestAnalysisModal
          requestDetail={aiAnalysisDetail}
          engagementId={engagementId}
          onClose={() => setAiAnalysisDetail(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 2: INTERCEPT
// ═══════════════════════════════════════════════════════════
function InterceptTab({ proxyFetch, activeSessionId, wsRef }) {
  const [enabled, setEnabled] = useState(false);
  const [queue, setQueue] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [editedRequest, setEditedRequest] = useState('');
  const [loading, setLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await proxyFetch('/intercept/status');
      if (res.ok) {
        const data = await res.json();
        setEnabled(data.enabled || false);
      }
    } catch {}
  }, [proxyFetch]);

  const loadQueue = useCallback(async () => {
    try {
      const res = await proxyFetch('/intercept/queue');
      if (res.ok) {
        const data = await res.json();
        setQueue(Array.isArray(data) ? data : data.items || []);
      }
    } catch {}
  }, [proxyFetch]);

  useEffect(() => { loadStatus(); loadQueue(); }, [loadStatus, loadQueue]);

  // WS listener for intercept events
  useEffect(() => {
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'intercept_held') {
          setQueue(prev => [...prev, msg.data]);
        } else if (msg.type === 'intercept_released') {
          setQueue(prev => prev.filter(f => f.id !== msg.data?.id));
          if (selectedFlow?.id === msg.data?.id) {
            setSelectedFlow(null);
            setEditedRequest('');
          }
        }
      } catch {}
    };
    const ws = wsRef.current;
    if (ws) ws.addEventListener('message', handler);
    return () => { if (ws) ws.removeEventListener('message', handler); };
  }, [wsRef.current, selectedFlow]);

  const toggleIntercept = async () => {
    const newState = !enabled;
    try {
      await proxyFetch(`/intercept/toggle?enabled=${newState}`, { method: 'PUT' });
      setEnabled(newState);
    } catch {}
  };

  const selectFlow = (flow) => {
    setSelectedFlow(flow);
    const rawReq = flow.raw_request || flow.request_raw || '';
    if (rawReq) {
      setEditedRequest(rawReq);
    } else {
      let built = `${flow.method || 'GET'} ${flow.path || '/'}${flow.query_string ? '?' + flow.query_string : ''} HTTP/1.1\r\nHost: ${flow.host || 'unknown'}\r\n`;
      if (flow.headers) {
        Object.entries(flow.headers).forEach(([k, v]) => { built += `${k}: ${v}\r\n`; });
      }
      if (flow.body) built += `\r\n${flow.body}`;
      setEditedRequest(built);
    }
  };

  const forwardFlow = async () => {
    if (!selectedFlow) return;
    setLoading(true);
    try {
      await proxyFetch(`/intercept/${selectedFlow.id}/forward`, {
        method: 'POST',
        body: JSON.stringify({ raw_request: editedRequest }),
      });
      setQueue(prev => prev.filter(f => f.id !== selectedFlow.id));
      setSelectedFlow(null);
      setEditedRequest('');
    } catch {}
    setLoading(false);
  };

  const dropFlow = async () => {
    if (!selectedFlow) return;
    setLoading(true);
    try {
      await proxyFetch(`/intercept/${selectedFlow.id}/drop`, { method: 'POST' });
      setQueue(prev => prev.filter(f => f.id !== selectedFlow.id));
      setSelectedFlow(null);
      setEditedRequest('');
    } catch {}
    setLoading(false);
  };

  const forwardAll = async () => {
    setLoading(true);
    try {
      await proxyFetch('/intercept/forward-all', { method: 'POST' });
      setQueue([]);
      setSelectedFlow(null);
      setEditedRequest('');
    } catch {}
    setLoading(false);
  };

  return (
    <div>
      <div style={S.toolbar}>
        <button
          style={{
            ...S.btn(enabled ? 'danger' : 'primary'),
            minWidth: 140, fontWeight: 700,
          }}
          onClick={toggleIntercept}
        >
          {enabled ? 'Intercept ON' : 'Intercept OFF'}
        </button>
        <span style={{ fontSize: 12, color: enabled ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
          {enabled ? 'Requests are being held for review' : 'Requests pass through freely'}
        </span>
        <div style={{ flex: 1 }} />
        {queue.length > 0 && (
          <button style={S.btn('danger')} onClick={forwardAll} disabled={loading}>
            Forward All ({queue.length})
          </button>
        )}
        <span style={S.stat}>{queue.length} held</span>
      </div>

      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 340px)', minHeight: 400 }}>
        {/* Queue list */}
        <div style={{ flex: '0 0 40%', overflow: 'auto', background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a' }}>
          {queue.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                {enabled ? 'Waiting for requests...' : 'Enable intercept to hold requests'}
              </div>
              <div style={{ fontSize: 12 }}>
                {enabled ? 'Browse your target to capture requests' : 'Click "Intercept OFF" to start intercepting'}
              </div>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Method</th>
                  <th style={S.th}>Host</th>
                  <th style={S.th}>Path</th>
                </tr>
              </thead>
              <tbody>
                {queue.map(flow => (
                  <tr
                    key={flow.id}
                    style={S.row(selectedFlow?.id === flow.id)}
                    onClick={() => selectFlow(flow)}
                    onMouseOver={e => { if (selectedFlow?.id !== flow.id) e.currentTarget.style.background = '#16213e'; }}
                    onMouseOut={e => { if (selectedFlow?.id !== flow.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={S.td}><MethodBadge method={flow.method} /></td>
                    <td style={{ ...S.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{flow.host}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {flow.path}{flow.query_string ? '?' + flow.query_string : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Editor + Details */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {selectedFlow ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
                Edit Request — {selectedFlow.method} {selectedFlow.host}{selectedFlow.path}
              </div>
              <textarea
                style={{ ...S.textarea, flex: 1 }}
                value={editedRequest}
                onChange={e => setEditedRequest(e.target.value)}
              />
              {/* Parsed detail panel for the intercepted request */}
              {(() => {
                const parsed = parseRawRequest(editedRequest);
                const host = parsed.headers['Host'] || parsed.headers['host'] || selectedFlow.host || '';
                const detailObj = {
                  method: parsed.method,
                  scheme: selectedFlow.is_tls ? 'https' : 'http',
                  host,
                  port: selectedFlow.port,
                  path: parsed.path,
                  query_string: parsed.queryString || '',
                  is_tls: selectedFlow.is_tls,
                  request_headers: parsed.headers,
                  request_body: parsed.body,
                  request_content_type: parsed.headers['Content-Type'] || parsed.headers['content-type'] || '',
                  source: 'intercept',
                };
                return <RequestDetailPanel detail={detailObj} compact />;
              })()}
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.btn('primary')} onClick={forwardFlow} disabled={loading}>
                  Forward
                </button>
                <button style={S.btn('danger')} onClick={dropFlow} disabled={loading}>
                  Drop
                </button>
              </div>
            </>
          ) : (
            <div style={{ ...S.emptyState, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Select a held request to edit
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB: REPEATER (Burp Suite-style request replay)
// ═══════════════════════════════════════════════════════════
function RepeaterTab({ proxyFetch, activeSessionId, repeaterTabs, setRepeaterTabs }) {
  const [activeRepeaterId, setActiveRepeaterId] = useState(repeaterTabs[0]?.id || null);
  const [responseTab, setResponseTab] = useState('body');

  // Sync active tab when tabs change
  useEffect(() => {
    if (repeaterTabs.length > 0 && !repeaterTabs.find(t => t.id === activeRepeaterId)) {
      setActiveRepeaterId(repeaterTabs[repeaterTabs.length - 1].id);
    }
  }, [repeaterTabs, activeRepeaterId]);

  const activeInstance = repeaterTabs.find(t => t.id === activeRepeaterId);

  const updateTab = (tabId, updates) => {
    setRepeaterTabs(prev => prev.map(t => t.id === tabId ? { ...t, ...updates } : t));
  };

  const addNewTab = () => {
    const id = crypto.randomUUID();
    const newTab = {
      id,
      name: `Tab ${repeaterTabs.length + 1}`,
      target: { scheme: 'https', host: '', port: 443, path: '/', query_string: '', is_tls: true },
      rawRequest: 'GET / HTTP/1.1\r\nHost: example.com\r\n',
      response: null,
      history: [],
      historyIndex: -1,
      followRedirects: false,
      sending: false,
    };
    setRepeaterTabs(prev => [...prev, newTab]);
    setActiveRepeaterId(id);
  };

  const closeTab = (tabId) => {
    setRepeaterTabs(prev => {
      const next = prev.filter(t => t.id !== tabId);
      if (activeRepeaterId === tabId && next.length > 0) {
        setActiveRepeaterId(next[next.length - 1].id);
      }
      return next;
    });
  };

  const handleSend = async () => {
    if (!activeInstance || activeInstance.sending || !activeSessionId) return;
    const parsed = parseRawRequest(activeInstance.rawRequest);
    const host = parsed.headers['Host'] || parsed.headers['host'] || activeInstance.target.host;

    updateTab(activeInstance.id, { sending: true, response: null });

    try {
      const res = await proxyFetch(`/repeater/send?session_id=${activeSessionId}`, {
        method: 'POST',
        body: JSON.stringify({
          method: parsed.method,
          scheme: activeInstance.target.scheme || 'https',
          host: host,
          port: activeInstance.target.port || 443,
          path: parsed.path,
          query_string: parsed.queryString,
          headers: parsed.headers,
          body: parsed.body,
          follow_redirects: activeInstance.followRedirects,
        }),
      });
      const data = await res.json();

      const historyEntry = {
        request: activeInstance.rawRequest,
        response: data,
        timestamp: new Date().toISOString(),
      };
      updateTab(activeInstance.id, {
        sending: false,
        response: data,
        history: [...activeInstance.history, historyEntry],
        historyIndex: activeInstance.history.length,
        // Update target from parsed Host header
        target: { ...activeInstance.target, host },
      });
    } catch (err) {
      updateTab(activeInstance.id, {
        sending: false,
        response: { error: err.message || 'Request failed' },
      });
    }
  };

  const loadHistoryEntry = (idx) => {
    if (!activeInstance || idx < 0 || idx >= activeInstance.history.length) return;
    const entry = activeInstance.history[idx];
    updateTab(activeInstance.id, {
      rawRequest: entry.request,
      response: entry.response,
      historyIndex: idx,
    });
  };

  if (repeaterTabs.length === 0) {
    return (
      <div style={{ ...S.emptyState, padding: 60 }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>Repeater</div>
        <div style={{ fontSize: 14, color: '#8888aa', marginBottom: 20 }}>
          Send requests from History (right-click &rarr; "Send to Repeater") or create a new tab.
        </div>
        <button style={S.btn('primary')} onClick={addNewTab}>+ New Repeater Tab</button>
      </div>
    );
  }

  return (
    <div>
      {/* Sub-tab bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 8, overflowX: 'auto' }}>
        {repeaterTabs.map(tab => (
          <div key={tab.id} style={{
            display: 'flex', alignItems: 'center', gap: 0,
            background: activeRepeaterId === tab.id ? '#1a1a2e' : 'transparent',
            borderBottom: activeRepeaterId === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
            borderRadius: '6px 6px 0 0',
          }}>
            <button
              style={{
                ...S.tab(activeRepeaterId === tab.id),
                maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                paddingRight: 4,
              }}
              onClick={() => setActiveRepeaterId(tab.id)}
              title={tab.name}
            >
              {tab.name}
            </button>
            <button
              style={{
                background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer',
                fontSize: 12, padding: '4px 6px', lineHeight: 1,
              }}
              onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
              title="Close tab"
            >&times;</button>
          </div>
        ))}
        <button
          style={{
            background: 'none', border: '1px dashed #2a2a4a', borderRadius: 4,
            color: '#8888aa', cursor: 'pointer', padding: '4px 10px', fontSize: 14,
            marginLeft: 4,
          }}
          onClick={addNewTab}
          title="New repeater tab"
        >+</button>
      </div>

      {activeInstance && (
        <div>
          {/* Target + controls bar */}
          <div style={{ ...S.toolbar, marginBottom: 8 }}>
            <span style={{ ...S.stat, fontFamily: 'monospace' }}>
              {activeInstance.target.scheme}://{activeInstance.target.host}
              {activeInstance.target.port && activeInstance.target.port !== 443 && activeInstance.target.port !== 80
                ? `:${activeInstance.target.port}` : ''}
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#8888aa', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={activeInstance.followRedirects}
                onChange={e => updateTab(activeInstance.id, { followRedirects: e.target.checked })}
              />
              Follow Redirects
            </label>
            {activeInstance.history.length > 0 && (
              <select
                style={{ ...S.select, maxWidth: 250 }}
                value={activeInstance.historyIndex ?? -1}
                onChange={e => loadHistoryEntry(parseInt(e.target.value))}
              >
                <option value={-1}>History ({activeInstance.history.length})</option>
                {activeInstance.history.map((entry, idx) => {
                  const p = parseRawRequest(entry.request);
                  const status = entry.response?.response_status || entry.response?.error || '?';
                  return (
                    <option key={idx} value={idx}>
                      #{idx + 1} {p.method} {p.path} → {status} ({entry.response?.duration_ms || '?'}ms)
                    </option>
                  );
                })}
              </select>
            )}
            <div style={{ flex: 1 }} />
            <button
              style={S.btn('primary')}
              onClick={handleSend}
              disabled={activeInstance.sending || !activeSessionId}
            >
              {activeInstance.sending ? 'Sending...' : 'Send'}
            </button>
          </div>

          {/* Split pane: Request | Response */}
          <div style={{ display: 'flex', gap: 12, height: 'calc(100vh - 400px)', minHeight: 350 }}>
            {/* Request editor + details */}
            <div style={{
              flex: '0 0 48%', display: 'flex', flexDirection: 'column', gap: 8,
              overflow: 'hidden',
            }}>
              <div style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a', overflow: 'hidden',
              }}>
                <div style={{
                  padding: '8px 12px', borderBottom: '1px solid #2a2a4a',
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8888aa',
                  letterSpacing: 0.5,
                }}>
                  Request
                </div>
                <textarea
                  style={{
                    ...S.textarea,
                    flex: 1, border: 'none', borderRadius: 0, resize: 'none',
                    minHeight: 0, height: '100%',
                  }}
                  value={activeInstance.rawRequest}
                  onChange={e => updateTab(activeInstance.id, { rawRequest: e.target.value })}
                  spellCheck={false}
                />
              </div>
              {/* Request detail panel — parsed from raw request */}
              {(() => {
                const parsed = parseRawRequest(activeInstance.rawRequest);
                const host = parsed.headers['Host'] || parsed.headers['host'] || activeInstance.target.host;
                const detailObj = {
                  method: parsed.method,
                  scheme: activeInstance.target.scheme || 'https',
                  host,
                  port: activeInstance.target.port,
                  path: parsed.path,
                  query_string: parsed.queryString || '',
                  is_tls: activeInstance.target.scheme === 'https',
                  request_headers: parsed.headers,
                  request_body: parsed.body,
                  request_content_type: parsed.headers['Content-Type'] || parsed.headers['content-type'] || '',
                  source: 'repeater',
                };
                return <RequestDetailPanel detail={detailObj} responseData={activeInstance.response} compact />;
              })()}
            </div>

            {/* Response viewer */}
            <div style={{
              flex: '0 0 50%', display: 'flex', flexDirection: 'column',
              background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a', overflow: 'hidden',
            }}>
              <div style={{
                padding: '6px 12px', borderBottom: '1px solid #2a2a4a',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8888aa',
                  letterSpacing: 0.5,
                }}>
                  Response
                </span>
                {activeInstance.response && !activeInstance.response.error && (
                  <>
                    <StatusBadge status={activeInstance.response.response_status} />
                    <span style={{ fontSize: 11, color: '#8888aa' }}>
                      {activeInstance.response.duration_ms}ms
                    </span>
                    <span style={{ fontSize: 11, color: '#8888aa' }}>
                      {activeInstance.response.content_length > 1024
                        ? `${(activeInstance.response.content_length / 1024).toFixed(1)}KB`
                        : `${activeInstance.response.content_length || 0}B`}
                    </span>
                  </>
                )}
                <div style={{ flex: 1 }} />
                {activeInstance.response && !activeInstance.response.error && (
                  <div style={{ display: 'flex', gap: 0 }}>
                    <button style={S.tab(responseTab === 'body')} onClick={() => setResponseTab('body')}>Body</button>
                    <button style={S.tab(responseTab === 'headers')} onClick={() => setResponseTab('headers')}>Headers</button>
                  </div>
                )}
              </div>

              <div style={{ flex: 1, overflow: 'auto' }}>
                {activeInstance.sending ? (
                  <div style={{ ...S.emptyState, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <div>
                      <div style={{ fontSize: 14, marginBottom: 4, color: '#3b82f6' }}>Sending request...</div>
                      <div style={{ fontSize: 12, color: '#8888aa' }}>{activeInstance.target.host}</div>
                    </div>
                  </div>
                ) : !activeInstance.response ? (
                  <div style={{ ...S.emptyState, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <div>
                      <div style={{ fontSize: 14, marginBottom: 4 }}>No response yet</div>
                      <div style={{ fontSize: 12, color: '#8888aa' }}>Edit the request and click Send</div>
                    </div>
                  </div>
                ) : activeInstance.response.error ? (
                  <div style={{ padding: 16 }}>
                    <div style={{
                      padding: 16, borderRadius: 8,
                      background: '#ef444410', border: '1px solid #ef444430',
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#ef4444', marginBottom: 4 }}>
                        Error
                      </div>
                      <div style={{ fontSize: 12, color: '#f87171', fontFamily: 'monospace' }}>
                        {activeInstance.response.error}
                      </div>
                      {activeInstance.response.duration_ms != null && (
                        <div style={{ fontSize: 11, color: '#8888aa', marginTop: 8 }}>
                          Duration: {activeInstance.response.duration_ms}ms
                        </div>
                      )}
                    </div>
                  </div>
                ) : responseTab === 'headers' ? (
                  <div style={{ padding: 12 }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#3b82f6', marginBottom: 12 }}>
                      HTTP/1.1 {activeInstance.response.response_status} {activeInstance.response.response_reason || ''}
                    </div>
                    {activeInstance.response.response_headers && Object.entries(activeInstance.response.response_headers).map(([k, v], i) => (
                      <div key={i} style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.8, color: '#e2e8f0' }}>
                        <span style={{ color: '#8b5cf6' }}>{k}</span>: {v}
                      </div>
                    ))}
                  </div>
                ) : (
                  <pre style={S.monoBody}>
                    {activeInstance.response.response_body || '(empty body)'}
                  </pre>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 3: SITE MAP (with attack suggestions)
// ═══════════════════════════════════════════════════════════

// Attack suggestion logic — analyzes node data to suggest relevant security tests
function generateAttackSuggestions(node, name, depth, inputs, parameters) {
  const suggestions = [];
  const methods = node.methods || [];
  const contentTypes = node.content_types || [];
  const fullPath = node.full_path || '';
  const reqCount = node.request_count || 0;

  // Check if this path has detected input fields
  const pathInputs = inputs.filter(inp =>
    (inp.url && inp.url.includes(fullPath)) || (inp.form_action && inp.form_action.includes(fullPath))
  );
  // Check if this path has detected parameters
  const pathParams = parameters.filter(p =>
    p.endpoints && p.endpoints.some(ep => ep.includes(fullPath))
  );

  // Combine inputs and params into a unified param list for AI testing
  const allTestableParams = [
    ...pathParams.map(p => ({ name: p.param_name, type: p.param_type || 'body', value: (p.example_values || [])[0] || '', source: 'param' })),
    ...pathInputs.filter(inp => !pathParams.some(p => p.param_name === inp.field_name))
      .map(inp => ({ name: inp.field_name, type: 'body', value: '', source: 'input', classification: inp.form_classification })),
  ];

  // AI-driven XSS test
  if (allTestableParams.length > 0) {
    const paramNames = allTestableParams.map(p => p.name).filter(Boolean).slice(0, 4);
    suggestions.push({
      type: 'ai_test',
      severity: 'High',
      color: '#a855f7',
      icon: '🤖',
      title: 'AI Security Test',
      detail: `${allTestableParams.length} testable parameter${allTestableParams.length > 1 ? 's' : ''}: ${paramNames.join(', ')}${allTestableParams.length > 4 ? '...' : ''}`,
      action: 'ai_test',
      params: allTestableParams,
      suggested_test: 'all',
      fullPath,
    });
  }

  // Still show if only inputs detected (no params yet)
  if (pathInputs.length > 0 && pathParams.length === 0) {
    const fieldNames = pathInputs.map(inp => inp.field_name).filter(Boolean).slice(0, 3);
    suggestions.push({
      type: 'xss',
      severity: 'High',
      color: '#f97316',
      icon: '⚡',
      title: 'XSS Test — Input Fields',
      detail: `${pathInputs.length} input field${pathInputs.length > 1 ? 's' : ''} detected${fieldNames.length > 0 ? ': ' + fieldNames.join(', ') : ''}. Run AI test to check for XSS.`,
      action: 'ai_test',
      params: pathInputs.map(inp => ({ name: inp.field_name, type: 'body', value: '', source: 'input' })),
      suggested_test: 'xss',
      fullPath,
    });
  }

  // HTML pages accepting POST — likely have forms
  if (methods.includes('POST')) {
    const hasHtml = contentTypes.some(ct => ct && (ct.includes('html') || ct.includes('json')));
    if (hasHtml) {
      if (!pathInputs.length) {
        suggestions.push({
          type: 'xss',
          severity: 'Medium',
          color: '#f97316',
          icon: '📝',
          title: 'Form Analysis — POST Endpoint',
          detail: 'This endpoint accepts POST requests. Run input detection to discover form fields for XSS/SQLi testing.',
          action: 'detect_inputs',
        });
      }
      suggestions.push({
        type: 'csrf',
        severity: 'Medium',
        color: '#eab308',
        icon: '🔄',
        title: 'CSRF Check — State-Changing Endpoint',
        detail: 'POST endpoint detected. Verify CSRF token protection is implemented.',
        action: 'check_csrf',
      });
    }
  }

  // Authentication pages
  const authKeywords = ['login', 'signin', 'auth', 'register', 'signup', 'password', 'reset', 'forgot', 'oauth', 'token', 'session'];
  const nameLower = (name || '').toLowerCase();
  const pathLower = fullPath.toLowerCase();
  if (authKeywords.some(kw => nameLower.includes(kw) || pathLower.includes(kw))) {
    suggestions.push({
      type: 'auth',
      severity: 'High',
      color: '#f97316',
      icon: '🔐',
      title: 'Authentication Testing',
      detail: 'Authentication-related endpoint. Test for brute force protection, credential stuffing, session fixation, and insecure password policies.',
      action: 'check_auth',
    });
  }

  // API endpoints
  const apiKeywords = ['api', 'graphql', 'rest', 'v1', 'v2', 'v3'];
  if (apiKeywords.some(kw => nameLower.includes(kw) || pathLower.includes(kw))) {
    if (contentTypes.some(ct => ct && ct.includes('json'))) {
      suggestions.push({
        type: 'api',
        severity: 'Medium',
        color: '#8b5cf6',
        icon: '🔗',
        title: 'API Security Check',
        detail: 'API endpoint detected. Test for IDOR, broken access control, mass assignment, and rate limiting.',
        action: 'check_api',
      });
    }
  }

  // File upload endpoints
  const uploadKeywords = ['upload', 'file', 'import', 'attach', 'media'];
  if (uploadKeywords.some(kw => nameLower.includes(kw) || pathLower.includes(kw))) {
    suggestions.push({
      type: 'upload',
      severity: 'High',
      color: '#f97316',
      icon: '📎',
      title: 'File Upload Testing',
      detail: 'Possible file upload endpoint. Test for unrestricted file upload, path traversal, and malicious file execution.',
      action: 'check_upload',
    });
  }

  // Admin/config endpoints
  const adminKeywords = ['admin', 'config', 'settings', 'manage', 'dashboard', 'console', 'debug'];
  if (adminKeywords.some(kw => nameLower.includes(kw) || pathLower.includes(kw))) {
    suggestions.push({
      type: 'access',
      severity: 'High',
      color: '#f97316',
      icon: '🛡️',
      title: 'Access Control Check',
      detail: 'Administrative/sensitive endpoint. Verify proper authorization and access controls.',
      action: 'check_access',
    });
  }

  // Pages with query strings — parameter tampering
  if (pathParams.length === 0 && reqCount > 0) {
    // Generic suggestion for paths that haven't had parameter detection run yet
    if (contentTypes.some(ct => ct && ct.includes('html')) && depth > 0) {
      suggestions.push({
        type: 'recon',
        severity: 'Info',
        color: '#3b82f6',
        icon: '🔍',
        title: 'Run Input Detection',
        detail: 'Scan this page for hidden form fields, input elements, and injectable parameters.',
        action: 'detect_inputs',
      });
    }
  }

  return suggestions;
}

// Merge a single request into the existing site map tree (client-side)
function mergeRequestIntoTree(tree, req) {
  const port = req.port;
  let hostKey = `${req.scheme}://${req.host}`;
  if (port && port !== 80 && port !== 443) hostKey += `:${port}`;

  const newTree = { ...tree };

  if (!newTree[hostKey]) {
    newTree[hostKey] = {
      host: req.host,
      scheme: req.scheme,
      port: port,
      children: {},
      request_count: 0,
      methods: [],
      statuses: [],
    };
  }
  const hostNode = { ...newTree[hostKey] };
  newTree[hostKey] = hostNode;
  hostNode.request_count = (hostNode.request_count || 0) + 1;
  if (req.method && !hostNode.methods.includes(req.method)) {
    hostNode.methods = [...hostNode.methods, req.method];
  }
  if (req.response_status && !hostNode.statuses.includes(req.response_status)) {
    hostNode.statuses = [...hostNode.statuses, req.response_status].sort();
  }

  const path = req.path || '/';
  const segments = path.split('/').filter(Boolean);

  if (segments.length === 0) {
    // Root path
    hostNode.children = { ...hostNode.children };
    if (!hostNode.children['/']) {
      hostNode.children['/'] = { name: '/', children: {}, methods: [], statuses: [], content_types: [], request_count: 0, full_path: '/' };
    }
    const root = { ...hostNode.children['/'] };
    hostNode.children['/'] = root;
    root.request_count = (root.request_count || 0) + 1;
    if (req.method && !root.methods.includes(req.method)) root.methods = [...root.methods, req.method];
    if (req.response_status && !root.statuses.includes(req.response_status)) root.statuses = [...root.statuses, req.response_status].sort();
    if (req.response_content_type) {
      const ct = req.response_content_type.split(';')[0].trim();
      if (!root.content_types.includes(ct)) root.content_types = [...root.content_types, ct];
    }
  } else {
    let currentParent = hostNode;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      currentParent.children = { ...currentParent.children };
      if (!currentParent.children[seg]) {
        currentParent.children[seg] = {
          name: seg, children: {}, methods: [], statuses: [], content_types: [],
          request_count: 0, full_path: '/' + segments.slice(0, i + 1).join('/'),
        };
      }
      const leaf = { ...currentParent.children[seg] };
      currentParent.children[seg] = leaf;
      leaf.request_count = (leaf.request_count || 0) + 1;
      if (req.method && !leaf.methods.includes(req.method)) leaf.methods = [...leaf.methods, req.method];
      if (req.response_status && !leaf.statuses.includes(req.response_status)) leaf.statuses = [...leaf.statuses, req.response_status].sort();
      if (req.response_content_type) {
        const ct = req.response_content_type.split(';')[0].trim();
        if (!leaf.content_types.includes(ct)) leaf.content_types = [...leaf.content_types, ct];
      }
      currentParent = leaf;
    }
  }

  return newTree;
}

function SiteMapTab({ proxyFetch, activeSessionId, scopeEnforced, wsRef, onAskAI }) {
  const [treeData, setTreeData] = useState({});
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodeData, setSelectedNodeData] = useState(null);
  const [inputs, setInputs] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [submittedSuggestions, setSubmittedSuggestions] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [aiTestDialog, setAiTestDialog] = useState(null); // { suggestion, index }

  const loadSiteMap = useCallback(async () => {
    if (!activeSessionId) return;
    setLoading(true);
    try {
      let sitemapUrl = `/sitemap?session_id=${activeSessionId}`;
      if (scopeEnforced) sitemapUrl += '&in_scope_only=true';
      const res = await proxyFetch(sitemapUrl);
      if (res.ok) {
        const data = await res.json();
        // Use the tree from the backend directly (it's already structured)
        setTreeData(data.tree || {});
      }
    } catch {}
    setLoading(false);
  }, [proxyFetch, activeSessionId, scopeEnforced]);

  // Load detected inputs and parameters for attack suggestions
  const loadInputsAndParams = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const [inputsRes, paramsRes] = await Promise.all([
        proxyFetch(`/inputs?session_id=${activeSessionId}&limit=500`),
        proxyFetch(`/parameters?session_id=${activeSessionId}&limit=500`),
      ]);
      if (inputsRes.ok) {
        const d = await inputsRes.json();
        setInputs(d.items || d || []);
      }
      if (paramsRes.ok) {
        const d = await paramsRes.json();
        setParameters(d.items || d || []);
      }
    } catch {}
  }, [proxyFetch, activeSessionId]);

  useEffect(() => { loadSiteMap(); }, [loadSiteMap]);
  useEffect(() => { loadInputsAndParams(); }, [loadInputsAndParams]);

  // Real-time: merge new requests into tree as they arrive via WebSocket
  useEffect(() => {
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'new_request') {
          const req = msg.data;
          // Only add in-scope requests when scope is enforced
          if (scopeEnforced && req.in_scope === false) return;
          setTreeData(prev => mergeRequestIntoTree(prev, req));
        }
      } catch {}
    };
    const ws = wsRef?.current;
    if (ws) ws.addEventListener('message', handler);
    return () => { if (ws) ws.removeEventListener('message', handler); };
  }, [wsRef?.current, scopeEnforced]);

  const toggleExpand = (path) => {
    setExpanded(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const countRequests = (node) => {
    let count = node.request_count || 0;
    if (node.children) {
      Object.values(node.children).forEach(child => { count += countRequests(child); });
    }
    return count;
  };

  const renderNode = (name, node, path, depth) => {
    const fullPath = path ? `${path}/${name}` : name;
    const hasChildren = node.children && Object.keys(node.children).length > 0;
    const isExpanded = expanded[fullPath];
    const reqCount = countRequests(node);
    const suggestions = showSuggestions ? generateAttackSuggestions(node, name, depth, inputs, parameters) : [];
    const hasSuggestions = suggestions.length > 0;

    return (
      <div key={fullPath}>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
            paddingLeft: depth * 20 + 8, cursor: 'pointer', fontSize: 13,
            background: selectedNode === fullPath ? 'rgba(59,130,246,0.1)' : 'transparent',
            borderRadius: 4, color: '#e2e8f0',
          }}
          onClick={() => {
            if (hasChildren) toggleExpand(fullPath);
            setSelectedNode(fullPath);
            setSelectedNodeData({ name, node, suggestions, depth });
            setSubmittedSuggestions({});
          }}
          onContextMenu={e => {
            e.preventDefault();
            // Build the full URL for this node
            let crawlUrl = '';
            if (depth === 0) {
              crawlUrl = name; // host-level: "https://example.com"
            } else {
              // Extract host from the path
              const parts = fullPath.split('/');
              const hostPart = parts[0]; // "https://example.com"
              const pathPart = node.full_path || `/${name}`;
              crawlUrl = hostPart + pathPart;
            }
            setContextMenu({ x: e.clientX, y: e.clientY, fullPath, name, node, depth, crawlUrl });
          }}
          onMouseOver={e => { if (selectedNode !== fullPath) e.currentTarget.style.background = '#16213e'; }}
          onMouseOut={e => { if (selectedNode !== fullPath) e.currentTarget.style.background = selectedNode === fullPath ? 'rgba(59,130,246,0.1)' : 'transparent'; }}
        >
          <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#8888aa', width: 16, textAlign: 'center' }}>
            {hasChildren ? (isExpanded ? '\u25BC' : '\u25B6') : '\u2022'}
          </span>
          <span style={{ fontFamily: 'monospace', fontWeight: depth === 0 ? 700 : 400, color: depth === 0 ? '#3b82f6' : '#e2e8f0' }}>
            {depth === 0 ? name : `/${name}`}
          </span>
          {/* Inline attack suggestion badges */}
          {hasSuggestions && (
            <span style={{ display: 'flex', gap: 3, marginLeft: 4 }}>
              {suggestions.slice(0, 3).map((s, i) => (
                <span key={i} title={s.title} style={{
                  fontSize: 9, padding: '1px 5px', borderRadius: 3,
                  background: `${s.color}18`, color: s.color, fontWeight: 600,
                }}>
                  {s.icon}
                </span>
              ))}
              {suggestions.length > 3 && (
                <span style={{ fontSize: 9, color: '#8888aa' }}>+{suggestions.length - 3}</span>
              )}
            </span>
          )}
          <span style={{ fontSize: 10, color: '#8888aa', marginLeft: 'auto' }}>
            {reqCount} req{reqCount !== 1 ? 's' : ''}
          </span>
        </div>
        {isExpanded && hasChildren && Object.entries(node.children).sort(([a], [b]) => a.localeCompare(b)).map(([childName, childNode]) =>
          renderNode(childName, childNode, fullPath, depth + 1)
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={S.toolbar}>
        <button style={S.btn()} onClick={() => { loadSiteMap(); loadInputsAndParams(); }}>Refresh Site Map</button>
        <button style={S.btn()} onClick={() => setExpanded({})}>Collapse All</button>
        <button style={S.btn()} onClick={() => {
          const allPaths = {};
          const walk = (node, path) => {
            Object.entries(node.children || {}).forEach(([name, child]) => {
              const full = path ? `${path}/${name}` : name;
              allPaths[full] = true;
              walk(child, full);
            });
          };
          Object.entries(treeData).forEach(([host, node]) => {
            allPaths[host] = true;
            walk(node, host);
          });
          setExpanded(allPaths);
        }}>Expand All</button>
        <button
          style={S.btn(showSuggestions ? 'primary' : undefined)}
          onClick={() => setShowSuggestions(!showSuggestions)}
        >
          {showSuggestions ? '💡 Suggestions On' : '💡 Suggestions Off'}
        </button>
        {Object.keys(treeData).length > 0 && (
          <button style={S.btn('danger')} onClick={async () => {
            if (!confirm('Delete ALL site map data? This removes proxy history and crawl pages for this session.')) return;
            await proxyFetch(`/sitemap?session_id=${activeSessionId}`, { method: 'DELETE' });
            setTreeData({});
            setSelectedNode(null);
            setSelectedNodeData(null);
          }}>Delete All</button>
        )}
        <span style={S.stat}>{Object.keys(treeData).length} hosts</span>
      </div>

      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 340px)', minHeight: 400 }}>
        {/* Tree panel */}
        <div style={{
          flex: selectedNodeData ? '0 0 55%' : 1, overflow: 'auto',
          background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a', padding: 8,
        }}>
          {loading ? (
            <div style={S.emptyState}>Loading site map...</div>
          ) : Object.keys(treeData).length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize: 14, marginBottom: 8 }}>No site map data</div>
              <div style={{ fontSize: 12 }}>Browse some targets to populate the site map.</div>
            </div>
          ) : (
            Object.entries(treeData).sort(([a], [b]) => a.localeCompare(b)).map(([host, node]) =>
              renderNode(host, node, '', 0)
            )
          )}
        </div>

        {/* Detail & Suggestions panel */}
        {selectedNodeData && (
          <div style={{
            flex: '0 0 44%', overflow: 'auto',
            background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a', padding: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0', fontFamily: 'monospace' }}>
                {selectedNodeData.depth === 0 ? selectedNodeData.name : selectedNodeData.node.full_path || `/${selectedNodeData.name}`}
              </div>
              <button
                style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer', fontSize: 16 }}
                onClick={() => { setSelectedNode(null); setSelectedNodeData(null); }}
              >×</button>
            </div>

            {/* Node info */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {(selectedNodeData.node.methods || []).map(m => (
                <MethodBadge key={m} method={m} />
              ))}
              {(selectedNodeData.node.content_types || []).map(ct => (
                <span key={ct} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#16213e', color: '#8888aa' }}>{ct}</span>
              ))}
            </div>

            {/* Attack Suggestions */}
            {selectedNodeData.suggestions.length > 0 ? (
              <div>
                <div style={{ ...S.sectionTitle, marginTop: 8 }}>Attack Suggestions</div>
                {selectedNodeData.suggestions.map((s, i) => (
                  <div key={i} style={{
                    background: `${s.color}08`, border: `1px solid ${s.color}30`,
                    borderRadius: 8, padding: 12, marginBottom: 8,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{ fontSize: 14 }}>{s.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: s.color }}>{s.title}</span>
                      <span style={{
                        fontSize: 9, padding: '2px 6px', borderRadius: 3, marginLeft: 'auto',
                        background: `${s.color}20`, color: s.color, fontWeight: 700,
                      }}>{s.severity}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#b0b0cc', lineHeight: 1.5 }}>{s.detail}</div>
                    {s.action === 'ai_test' && (() => {
                      const key = `ai_test_${i}`;
                      const submitted = submittedSuggestions[key];
                      return (
                        <button
                          style={{ ...S.btn(submitted ? undefined : 'primary'), marginTop: 8, padding: '4px 12px', fontSize: 11, opacity: submitted ? 0.7 : 1 }}
                          disabled={submitted}
                          onClick={() => {
                            setAiTestDialog({ suggestion: s, index: i });
                          }}
                        >
                          {submitted ? '✓ Test Running' : `🤖 AI Test (${(s.params || []).length} params)`}
                        </button>
                      );
                    })()}
                    {s.action === 'detect_inputs' && (() => {
                      const key = `detect_${i}`;
                      const submitted = submittedSuggestions[key];
                      return (
                        <button
                          style={{ ...S.btn(submitted ? undefined : undefined), marginTop: 8, padding: '4px 12px', fontSize: 11, opacity: submitted ? 0.7 : 1 }}
                          disabled={submitted}
                          onClick={async () => {
                            setSubmittedSuggestions(prev => ({ ...prev, [key]: true }));
                            try {
                              await proxyFetch(`/inputs/detect?session_id=${activeSessionId}`, { method: 'POST' });
                              await proxyFetch(`/parameters/detect?session_id=${activeSessionId}`, { method: 'POST' });
                              loadInputsAndParams();
                            } catch {}
                          }}
                        >
                          {submitted ? '✓ Detection Submitted' : 'Detect Inputs & Parameters'}
                        </button>
                      );
                    })()}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#8888aa', fontSize: 12, textAlign: 'center', padding: 20 }}>
                No attack suggestions for this node. Run input detection to discover testable parameters.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9990 }} onClick={() => setContextMenu(null)} onContextMenu={e => { e.preventDefault(); setContextMenu(null); }} />
          <div style={{
            position: 'fixed', left: contextMenu.x, top: contextMenu.y, zIndex: 9991,
            background: '#1a1a2e', border: '1px solid #3b82f650', borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)', padding: 4, minWidth: 180,
          }}>
            <div style={{ padding: '4px 12px', fontSize: 10, color: '#8888aa', fontFamily: 'monospace', borderBottom: '1px solid #2a2a4a', marginBottom: 2 }}>
              {contextMenu.depth === 0 ? contextMenu.name : (contextMenu.node?.full_path || `/${contextMenu.name}`)}
            </div>
            {/* Crawl from here */}
            <div
              style={{ padding: '6px 12px', fontSize: 12, color: '#22c55e', cursor: 'pointer', borderRadius: 4 }}
              onMouseOver={e => e.currentTarget.style.background = '#16213e'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              onClick={async () => {
                const url = contextMenu.crawlUrl;
                setContextMenu(null);
                try {
                  const crawlRes = await proxyFetch(`/crawl/start?session_id=${activeSessionId}`, {
                    method: 'POST',
                    body: JSON.stringify({ base_urls: [url] }),
                  });
                  if (crawlRes.ok) {
                    alert(`Crawl started from: ${url}`);
                  } else {
                    const errText = await crawlRes.text().catch(() => '');
                    alert(`Crawl failed: ${errText}`);
                  }
                } catch {}
              }}
            >&#x1f577;&#xfe0f; Crawl from here</div>
            {/* Ask AI about this path */}
            {onAskAI && (
              <div
                style={{ padding: '6px 12px', fontSize: 12, color: '#a855f7', cursor: 'pointer', borderRadius: 4 }}
                onMouseOver={e => e.currentTarget.style.background = '#16213e'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => {
                  const url = contextMenu.crawlUrl;
                  const path = contextMenu.node?.full_path || `/${contextMenu.name}`;
                  setContextMenu(null);
                  onAskAI({ url, path, node: contextMenu.node, depth: contextMenu.depth });
                }}
              >&#x1f916; AI Copilot</div>
            )}
            {/* Delete this path */}
            <div
              style={{ padding: '6px 12px', fontSize: 12, color: '#ef4444', cursor: 'pointer', borderRadius: 4 }}
              onMouseOver={e => e.currentTarget.style.background = '#16213e'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              onClick={async () => {
                const node = contextMenu.node;
                const pathPrefix = node?.full_path || `/${contextMenu.name}`;
                const host = contextMenu.depth === 0 ? contextMenu.name.replace(/^https?:\/\//, '') : '';
                setContextMenu(null);
                const params = new URLSearchParams({ session_id: activeSessionId });
                if (host) params.append('host', host);
                if (pathPrefix && contextMenu.depth > 0) params.append('path_prefix', pathPrefix);
                await proxyFetch(`/sitemap?${params}`, { method: 'DELETE' });
                loadSiteMap();
              }}
            >&#x1f5d1;&#xfe0f; Delete {contextMenu.depth === 0 ? 'host' : 'path'}</div>
          </div>
        </>
      )}

      {/* AI Test Confirmation Dialog */}
      {aiTestDialog && (() => {
        const s = aiTestDialog.suggestion;
        const allParams = s.params || [];
        return (
          <AITestConfirmDialog
            params={allParams}
            path={s.fullPath || ''}
            suggestedTest={s.suggested_test || 'all'}
            onClose={() => setAiTestDialog(null)}
            onRun={async (selectedParams, testType, mode) => {
              const key = `ai_test_${aiTestDialog.index}`;
              setSubmittedSuggestions(prev => ({ ...prev, [key]: true }));
              setAiTestDialog(null);

              // Dispatch each selected param as an AI test
              for (const param of selectedParams) {
                const prompt = `Test the "${param.name}" ${param.type} parameter for ${testType === 'all' ? 'XSS and SQL injection' : testType}`;
                if (onAskAI) {
                  onAskAI({
                    url: s.fullPath,
                    path: s.fullPath,
                    paramName: param.name,
                    paramLocation: param.type,
                    attack: testType === 'all' ? null : testType,
                    suggestedPrompt: prompt,
                  });
                  break; // Open sidebar with first param, the direct dispatch handles the rest
                }
              }
            }}
          />
        );
      })()}
    </div>
  );
}


function AITestConfirmDialog({ params, path, suggestedTest, onClose, onRun }) {
  const [selected, setSelected] = useState(() => new Set(
    params.filter(p => !['csrf', 'token', '_token', 'csrfmiddlewaretoken'].includes(p.name?.toLowerCase())).map((_, i) => i)
  ));
  const [testType, setTestType] = useState(suggestedTest || 'all');
  const [mode, setMode] = useState('smart');

  const toggle = (i) => setSelected(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });
  const toggleAll = () => setSelected(prev => prev.size === params.length ? new Set() : new Set(params.map((_, i) => i)));

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: '#0d1117', border: '1px solid #2a2a4a', borderRadius: 12,
        width: 500, maxHeight: '80vh', overflow: 'hidden', zIndex: 10000,
        boxShadow: '0 8px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #2a2a4a', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🤖</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>AI Security Test</div>
            <div style={{ fontSize: 11, color: '#8888aa', fontFamily: 'monospace' }}>{path}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8888aa', fontSize: 16, cursor: 'pointer' }}>&times;</button>
        </div>

        {/* Controls */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #1a1a2a', display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 11, color: '#8888aa' }}>Test:</label>
          <select value={testType} onChange={e => setTestType(e.target.value)}
            style={{ padding: '4px 8px', background: '#16213e', color: '#e2e8f0', border: '1px solid #2a2a4a', borderRadius: 6, fontSize: 11 }}>
            <option value="all">All (XSS + SQLi)</option>
            <option value="xss">XSS Only</option>
            <option value="sqli">SQLi Only</option>
          </select>
          <label style={{ fontSize: 11, color: '#8888aa' }}>Mode:</label>
          <select value={mode} onChange={e => setMode(e.target.value)}
            style={{ padding: '4px 8px', background: '#16213e', color: '#e2e8f0', border: '1px solid #2a2a4a', borderRadius: 6, fontSize: 11 }}>
            <option value="smart">Smart (top payloads)</option>
            <option value="aggressive">Aggressive (all payloads)</option>
          </select>
        </div>

        {/* Parameter List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <input type="checkbox" checked={selected.size === params.length} onChange={toggleAll}
              style={{ accentColor: '#a855f7' }} />
            <span style={{ fontSize: 11, color: '#8888aa' }}>Select All ({params.length} parameters)</span>
          </div>
          {params.map((p, i) => {
            const isCsrf = ['csrf', 'token', '_token', 'csrfmiddlewaretoken'].includes(p.name?.toLowerCase());
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                borderBottom: '1px solid #1a1a2a', opacity: selected.has(i) ? 1 : 0.5,
              }}>
                <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)}
                  style={{ accentColor: '#a855f7' }} />
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#a855f7', minWidth: 120 }}>{p.name}</span>
                <span style={{
                  fontSize: 9, padding: '1px 6px', borderRadius: 4,
                  background: p.type === 'body' ? '#3b82f620' : p.type === 'query' ? '#22c55e20' : '#f59e0b20',
                  color: p.type === 'body' ? '#3b82f6' : p.type === 'query' ? '#22c55e' : '#f59e0b',
                }}>{p.type}</span>
                {p.value && <span style={{ fontSize: 10, color: '#8888aa', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>= {p.value}</span>}
                {isCsrf && <span style={{ fontSize: 9, color: '#f59e0b', fontStyle: 'italic' }}>CSRF token</span>}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #2a2a4a', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={S.btn()} onClick={onClose}>Cancel</button>
          <button
            style={{ ...S.btn('primary'), background: '#a855f7' }}
            disabled={selected.size === 0}
            onClick={() => onRun(params.filter((_, i) => selected.has(i)), testType, mode)}
          >
            Run Test ({selected.size} param{selected.size !== 1 ? 's' : ''})
          </button>
        </div>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════
// TAB 4: CRAWL
// ═══════════════════════════════════════════════════════════
function CrawlTab({ proxyFetch, activeSessionId, wsRef }) {
  const [engagements, setEngagements] = useState([]);
  const [selectedEngagement, setSelectedEngagement] = useState('');
  const [credentials, setCredentials] = useState([]);
  const [selectedCredential, setSelectedCredential] = useState('');
  const [crawlUrl, setCrawlUrl] = useState('');
  const [maxDepth, setMaxDepth] = useState(3);
  const [maxPages, setMaxPages] = useState(100);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [pages, setPages] = useState([]);
  const [crawling, setCrawling] = useState(false);
  const [compareSessionA, setCompareSessionA] = useState('');
  const [compareSessionB, setCompareSessionB] = useState('');
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    proxyFetch('/scope/engagements').then(r => r.ok ? r.json() : []).then(d => setEngagements(Array.isArray(d) ? d : [])).catch(() => {});
    proxyFetch('/sessions').then(r => r.ok ? r.json() : []).then(d => setSessions(Array.isArray(d) ? d : [])).catch(() => {});
  }, [proxyFetch]);

  useEffect(() => {
    if (!selectedEngagement) { setCredentials([]); return; }
    proxyFetch(`/scope/engagements/${selectedEngagement}/credentials`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setCredentials(Array.isArray(d) ? d : []))
      .catch(() => setCredentials([]));
  }, [proxyFetch, selectedEngagement]);

  const loadJobs = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const res = await proxyFetch(`/crawl/jobs?session_id=${activeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setJobs(Array.isArray(data) ? data : data.items || []);
      }
    } catch {}
  }, [proxyFetch, activeSessionId]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // WS listener for crawl events
  useEffect(() => {
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'crawl_progress') {
          setJobs(prev => prev.map(j => j.id === msg.data?.job_id ? { ...j, ...msg.data } : j));
        } else if (msg.type === 'crawl_complete') {
          setJobs(prev => prev.map(j => j.id === msg.data?.job_id ? { ...j, status: 'complete', ...msg.data } : j));
          loadJobs();
        }
      } catch {}
    };
    const ws = wsRef.current;
    if (ws) ws.addEventListener('message', handler);
    return () => { if (ws) ws.removeEventListener('message', handler); };
  }, [wsRef.current, loadJobs]);

  const startCrawl = async () => {
    if (!activeSessionId) return;
    setCrawling(true);
    try {
      const body = {
        url: crawlUrl || undefined,
        max_depth: maxDepth,
        max_pages: maxPages,
        engagement_id: selectedEngagement || undefined,
        credential_id: selectedCredential || undefined,
      };
      const res = await proxyFetch(`/crawl/start?session_id=${activeSessionId}`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setJobs(prev => [data, ...prev]);
      }
    } catch {}
    setCrawling(false);
  };

  const loadPages = async (jobId) => {
    try {
      const res = await proxyFetch(`/crawl/jobs/${jobId}/pages`);
      if (res.ok) {
        const data = await res.json();
        setPages(Array.isArray(data) ? data : data.items || []);
        setSelectedJob(jobId);
      }
    } catch {}
  };

  const compareSessions = async () => {
    if (!compareSessionA || !compareSessionB) return;
    setComparing(true);
    try {
      const res = await proxyFetch('/compare/sessions', {
        method: 'POST',
        body: JSON.stringify({ session_a: compareSessionA, session_b: compareSessionB }),
      });
      if (res.ok) {
        const data = await res.json();
        setCompareResult(data);
      }
    } catch {}
    setComparing(false);
  };

  return (
    <div>
      {/* Crawl Config */}
      <div style={S.card}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#e2e8f0' }}>Start New Crawl</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 4 }}>Start URL (optional)</div>
            <input style={{ ...S.input, width: 280 }} placeholder="https://target.com/" value={crawlUrl} onChange={e => setCrawlUrl(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 4 }}>Engagement</div>
            <select style={S.select} value={selectedEngagement} onChange={e => setSelectedEngagement(e.target.value)}>
              <option value="">None</option>
              {engagements.map(eng => <option key={eng.id} value={eng.id}>{eng.name || eng.title}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 4 }}>Credential</div>
            <select style={S.select} value={selectedCredential} onChange={e => setSelectedCredential(e.target.value)}>
              <option value="">None</option>
              {credentials.map(c => <option key={c.id} value={c.id}>{c.username || c.name || c.id}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 4 }}>Max Depth</div>
            <input style={{ ...S.input, width: 60 }} type="number" min={1} max={20} value={maxDepth} onChange={e => setMaxDepth(Number(e.target.value))} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 4 }}>Max Pages</div>
            <input style={{ ...S.input, width: 80 }} type="number" min={1} max={10000} value={maxPages} onChange={e => setMaxPages(Number(e.target.value))} />
          </div>
          <button style={S.btn('primary')} onClick={startCrawl} disabled={crawling || !activeSessionId}>
            {crawling ? 'Starting...' : 'Start Crawl'}
          </button>
        </div>
      </div>

      {/* Crawl Jobs */}
      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 460px)', minHeight: 300 }}>
        <div style={{ flex: '0 0 50%', overflow: 'auto', background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #2a2a4a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>Crawl Jobs</span>
            <button style={S.btn()} onClick={loadJobs}>Refresh</button>
          </div>
          {jobs.length === 0 ? (
            <div style={S.emptyState}>No crawl jobs yet</div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>URL</th>
                  <th style={S.th}>Pages</th>
                  <th style={S.th}>Progress</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.id} style={S.row(selectedJob === job.id)} onClick={() => loadPages(job.id)}>
                    <td style={S.td}>
                      <span style={S.tag(
                        job.status === 'complete' ? '#22c55e' :
                        job.status === 'running' ? '#3b82f6' :
                        job.status === 'failed' ? '#ef4444' : '#f59e0b'
                      )}>
                        {job.status || 'pending'}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.url || job.start_url || '-'}
                    </td>
                    <td style={S.td}>{job.pages_found || job.page_count || 0}</td>
                    <td style={S.td}>
                      {job.status === 'running' && (
                        <div style={{ width: 100, height: 6, background: '#2a2a4a', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(100, (job.progress || 0))}%`, height: '100%', background: '#3b82f6', borderRadius: 3, transition: 'width 0.3s' }} />
                        </div>
                      )}
                      {job.status === 'complete' && <span style={{ color: '#22c55e', fontSize: 11 }}>Done</span>}
                      {job.status === 'failed' && <span style={{ color: '#ef4444', fontSize: 11 }}>Failed</span>}
                    </td>
                    <td style={S.td}>
                      <button style={{ ...S.btn(), padding: '2px 8px', fontSize: 11 }} onClick={(e) => { e.stopPropagation(); loadPages(job.id); }}>
                        View Pages
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pages or Compare */}
        <div style={{ flex: 1, overflow: 'auto', background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a' }}>
          {selectedJob ? (
            <div>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #2a2a4a', fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
                Crawled Pages ({pages.length})
              </div>
              {pages.length === 0 ? (
                <div style={S.emptyState}>No pages found</div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>URL</th>
                      <th style={S.th}>Type</th>
                      <th style={S.th}>Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map((page, i) => (
                      <tr key={i} style={S.row(false)}
                        onMouseOver={e => e.currentTarget.style.background = '#16213e'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={S.td}><StatusBadge status={page.status || page.status_code} /></td>
                        <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={page.url}>
                          {page.url}
                        </td>
                        <td style={{ ...S.td, fontSize: 11, color: '#8888aa' }}>{(page.content_type || '').split(';')[0]}</td>
                        <td style={{ ...S.td, fontSize: 11, color: '#8888aa' }}>
                          {page.content_length > 1024 ? `${(page.content_length / 1024).toFixed(1)}K` : `${page.content_length || 0}B`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#e2e8f0' }}>Compare Sessions</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 4 }}>Session A</div>
                  <select style={S.select} value={compareSessionA} onChange={e => setCompareSessionA(e.target.value)}>
                    <option value="">Select...</option>
                    {sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 4 }}>Session B</div>
                  <select style={S.select} value={compareSessionB} onChange={e => setCompareSessionB(e.target.value)}>
                    <option value="">Select...</option>
                    {sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <button style={S.btn('primary')} onClick={compareSessions} disabled={comparing || !compareSessionA || !compareSessionB}>
                  {comparing ? 'Comparing...' : 'Compare'}
                </button>
              </div>
              {compareResult ? (
                <div>
                  {compareResult.only_in_a && compareResult.only_in_a.length > 0 && (
                    <div style={S.section}>
                      <div style={S.sectionTitle}>Only in Session A ({compareResult.only_in_a.length})</div>
                      {compareResult.only_in_a.map((url, i) => (
                        <div key={i} style={{ fontFamily: 'monospace', fontSize: 12, padding: '2px 0', color: '#ef4444' }}>- {url}</div>
                      ))}
                    </div>
                  )}
                  {compareResult.only_in_b && compareResult.only_in_b.length > 0 && (
                    <div style={S.section}>
                      <div style={S.sectionTitle}>Only in Session B ({compareResult.only_in_b.length})</div>
                      {compareResult.only_in_b.map((url, i) => (
                        <div key={i} style={{ fontFamily: 'monospace', fontSize: 12, padding: '2px 0', color: '#22c55e' }}>+ {url}</div>
                      ))}
                    </div>
                  )}
                  {compareResult.differences && compareResult.differences.length > 0 && (
                    <div style={S.section}>
                      <div style={S.sectionTitle}>Differences ({compareResult.differences.length})</div>
                      {compareResult.differences.map((diff, i) => (
                        <div key={i} style={{ ...S.card, padding: '8px 12px' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#f59e0b' }}>{diff.url || diff.path}</div>
                          <div style={{ fontSize: 11, color: '#8888aa', marginTop: 2 }}>{diff.description || diff.detail || 'Status or content differs'}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(!compareResult.only_in_a || compareResult.only_in_a.length === 0) &&
                   (!compareResult.only_in_b || compareResult.only_in_b.length === 0) &&
                   (!compareResult.differences || compareResult.differences.length === 0) && (
                    <div style={{ color: '#22c55e', fontSize: 13 }}>Sessions are identical</div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#8888aa', fontSize: 13 }}>Select two sessions to compare their crawl results</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 5: INPUTS
// ═══════════════════════════════════════════════════════════
function InputsTab({ proxyFetch, activeSessionId, sendToRepeater, sendFormToRepeater, onAskAI }) {
  const [inputs, setInputs] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [inputsLoading, setInputsLoading] = useState(false);
  const [paramsLoading, setParamsLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('inputs');
  const [contextMenu, setContextMenu] = useState(null);

  const detectInputs = async () => {
    if (!activeSessionId) return;
    setInputsLoading(true);
    try {
      await proxyFetch(`/inputs/detect?session_id=${activeSessionId}`, { method: 'POST' });
      const res = await proxyFetch(`/inputs?session_id=${activeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setInputs(Array.isArray(data) ? data : data.items || []);
      }
    } catch {}
    setInputsLoading(false);
  };

  const detectParameters = async () => {
    if (!activeSessionId) return;
    setParamsLoading(true);
    try {
      await proxyFetch(`/parameters/detect?session_id=${activeSessionId}`, { method: 'POST' });
      const res = await proxyFetch(`/parameters?session_id=${activeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setParameters(Array.isArray(data) ? data : data.items || []);
      }
    } catch {}
    setParamsLoading(false);
  };

  const [findings, setFindings] = useState([]);

  const loadExisting = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const [inputsRes, paramsRes, findingsRes] = await Promise.all([
        proxyFetch(`/inputs?session_id=${activeSessionId}`),
        proxyFetch(`/parameters?session_id=${activeSessionId}`),
        proxyFetch(`/findings?session_id=${activeSessionId}`),
      ]);
      if (inputsRes.ok) {
        const data = await inputsRes.json();
        setInputs(Array.isArray(data) ? data : data.items || []);
      }
      if (paramsRes.ok) {
        const data = await paramsRes.json();
        setParameters(Array.isArray(data) ? data : data.items || []);
      }
      if (findingsRes.ok) {
        const data = await findingsRes.json();
        setFindings(Array.isArray(data) ? data : data.items || []);
      }
    } catch {}
  }, [proxyFetch, activeSessionId]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  // Build a lookup of vulnerable param+host combinations from findings
  // Each entry: { paramName, host } extracted from the finding title + affected_url
  const vulnerableParamHosts = useMemo(() => {
    const vuln = [];
    for (const f of findings) {
      const title = (f.title || '').toLowerCase();
      const affectedUrl = f.affected_url || '';
      // Extract param name from finding titles like "Stored XSS in 'comment'"
      const match = title.match(/in\s+'(\w+)'/);
      if (!match) continue;
      const paramName = match[1].toLowerCase();
      // Extract host from affected_url
      let host = '';
      try {
        const u = new URL(affectedUrl);
        host = u.hostname.toLowerCase();
      } catch { /* if no valid URL, host stays empty */ }
      vuln.push({ paramName, host });
    }
    return vuln;
  }, [findings]);

  // Check if a specific param+context is vulnerable
  const isParamVulnerable = useCallback((name, contextUrl) => {
    if (!name || vulnerableParamHosts.length === 0) return false;
    const n = name.toLowerCase();
    // Extract host from the context URL (input.url, param.host, or param.endpoints[0])
    let ctxHost = '';
    if (contextUrl) {
      try {
        // Could be a full URL or just a host
        const u = contextUrl.startsWith('http') ? new URL(contextUrl) : null;
        ctxHost = u ? u.hostname.toLowerCase() : contextUrl.toLowerCase();
      } catch {
        ctxHost = contextUrl.toLowerCase();
      }
    }
    return vulnerableParamHosts.some(v =>
      v.paramName === n && (
        !v.host || !ctxHost || // If either is missing, match on param name only
        v.host === ctxHost ||
        ctxHost.includes(v.host) ||
        v.host.includes(ctxHost)
      )
    );
  }, [vulnerableParamHosts]);

  const triggerScan = async (item, scanType) => {
    try {
      const requestId = item.request_id || item.first_seen_request_id;
      if (!requestId) { console.warn('No request_id for scan'); return; }
      const res = await proxyFetch(`/scanner/request?session_id=${activeSessionId}`, {
        method: 'POST',
        body: JSON.stringify({ request_id: requestId, test_types: [scanType] }),
      });
      if (res.ok) {
        const data = await res.json();
        console.log(`Scan started: job ${data.job_id}, ${data.parameters_found} params found`);
      }
    } catch (err) { console.error('triggerScan error:', err); }
  };

  const handleContextMenu = (e, item) => {
    e.preventDefault();
    const paramName = item.field_name || item.param_name || '';
    const paramType = item.param_type || 'body';
    const endpoint = (item.endpoints || [])[0] || item.url || '';

    const menuItems = [];

    // AI-driven testing options with intensity levels
    if (onAskAI) {
      menuItems.push(
        { label: '\u{1F916} AI Test (All)', onClick: () => onAskAI({
          url: endpoint, path: endpoint, paramName, paramLocation: paramType,
          attack: null, suggestedPrompt: `Test the "${paramName}" ${paramType} parameter for all vulnerability types`,
        })},
        { label: '\u{26A1} XSS — Quick (10)', onClick: () => onAskAI({
          url: endpoint, path: endpoint, paramName, paramLocation: paramType,
          attack: 'xss', intensity: 'quick', suggestedPrompt: `Test the "${paramName}" ${paramType} parameter for XSS [intensity: quick]`,
        })},
        { label: '\u{26A1} XSS — Moderate (100)', onClick: () => onAskAI({
          url: endpoint, path: endpoint, paramName, paramLocation: paramType,
          attack: 'xss', intensity: 'moderate', suggestedPrompt: `Test the "${paramName}" ${paramType} parameter for XSS [intensity: moderate]`,
        })},
        { label: '\u{26A1} XSS — Full (all KB)', onClick: () => onAskAI({
          url: endpoint, path: endpoint, paramName, paramLocation: paramType,
          attack: 'xss', intensity: 'full', suggestedPrompt: `Test the "${paramName}" ${paramType} parameter for XSS [intensity: full]`,
        })},
        { label: '\u{1F489} SQLi — Quick (10)', onClick: () => onAskAI({
          url: endpoint, path: endpoint, paramName, paramLocation: paramType,
          attack: 'sqli', intensity: 'quick', suggestedPrompt: `Test the "${paramName}" ${paramType} parameter for SQL injection [intensity: quick]`,
        })},
        { label: '\u{1F489} SQLi — Moderate (100)', onClick: () => onAskAI({
          url: endpoint, path: endpoint, paramName, paramLocation: paramType,
          attack: 'sqli', intensity: 'moderate', suggestedPrompt: `Test the "${paramName}" ${paramType} parameter for SQL injection [intensity: moderate]`,
        })},
        { label: '\u{1F489} SQLi — Full (all KB)', onClick: () => onAskAI({
          url: endpoint, path: endpoint, paramName, paramLocation: paramType,
          attack: 'sqli', intensity: 'full', suggestedPrompt: `Test the "${paramName}" ${paramType} parameter for SQL injection [intensity: full]`,
        })},
        { separator: true },
      );
    }

    // If this is a form input with a request_id, offer to send the form to repeater
    if (item.request_id && item.form_action !== undefined && sendFormToRepeater) {
      menuItems.push({
        label: 'Send Form to Repeater',
        onClick: () => sendFormToRepeater({
          request_id: item.request_id,
          form_action: item.form_action || '',
          form_method: item.form_method || 'GET',
        }),
      });
    }

    // If this is a parameter with a first_seen_request_id, offer to send original request to repeater
    if ((item.first_seen_request_id || item.request_id) && sendToRepeater) {
      menuItems.push({
        label: 'Send Request to Repeater',
        onClick: () => sendToRepeater({ id: item.first_seen_request_id || item.request_id }),
      });
    }

    menuItems.push(
      { separator: true },
      { label: 'Copy Name', onClick: () => navigator.clipboard.writeText(paramName) },
      { label: 'Copy Value', onClick: () => navigator.clipboard.writeText((item.example_values || [])[0] || item.form_action || '') },
    );

    setContextMenu({ x: e.clientX, y: e.clientY, items: menuItems });
  };

  return (
    <div>
      <div style={S.toolbar}>
        <div style={S.tabBar}>
          <button style={S.tab(activeSubTab === 'inputs')} onClick={() => setActiveSubTab('inputs')}>Form Inputs</button>
          <button style={S.tab(activeSubTab === 'params')} onClick={() => setActiveSubTab('params')}>Parameters</button>
        </div>
        <div style={{ flex: 1 }} />
        {activeSubTab === 'inputs' ? (
          <>
            <button style={S.btn('primary')} onClick={detectInputs} disabled={inputsLoading || !activeSessionId}>
              {inputsLoading ? 'Detecting...' : 'Detect Inputs'}
            </button>
            {inputs.length > 0 && (
              <button style={S.btn('danger')} onClick={async () => {
                if (!window.confirm('Delete all detected inputs?')) return;
                await proxyFetch(`/inputs?session_id=${activeSessionId}`, { method: 'DELETE' });
                setInputs([]);
              }}>Clear All</button>
            )}
          </>
        ) : (
          <>
            <button style={S.btn('primary')} onClick={detectParameters} disabled={paramsLoading || !activeSessionId}>
              {paramsLoading ? 'Detecting...' : 'Detect Parameters'}
            </button>
            {parameters.length > 0 && (
              <button style={S.btn('danger')} onClick={async () => {
                if (!window.confirm('Delete all detected parameters?')) return;
                await proxyFetch(`/parameters?session_id=${activeSessionId}`, { method: 'DELETE' });
                setParameters([]);
              }}>Clear All</button>
            )}
          </>
        )}
      </div>

      <div style={{ background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a', height: 'calc(100vh - 340px)', overflow: 'auto' }}>
        {activeSubTab === 'inputs' ? (
          inputs.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize: 14, marginBottom: 8 }}>No inputs detected</div>
              <div style={{ fontSize: 12 }}>Click "Detect Inputs" to scan captured traffic for form fields.</div>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Field Name</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>URL</th>
                  <th style={S.th}>Method</th>
                  <th style={S.th}>Classification</th>
                  <th style={S.th}>Form Action</th>
                </tr>
              </thead>
              <tbody>
                {inputs.map((input, i) => {
                  const fieldName = input.field_name || input.name || '';
                  const isVuln = isParamVulnerable(fieldName, input.url);
                  return (
                  <tr key={i} style={{ ...S.row(false), background: isVuln ? '#ef444415' : undefined, borderLeft: isVuln ? '3px solid #ef4444' : undefined }}
                    onContextMenu={(e) => handleContextMenu(e, input)}
                    onMouseOver={e => e.currentTarget.style.background = isVuln ? '#ef444425' : '#16213e'}
                    onMouseOut={e => e.currentTarget.style.background = isVuln ? '#ef444415' : 'transparent'}
                  >
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: isVuln ? '#ef4444' : '#3b82f6' }}>
                      {isVuln && <span title="Vulnerability found!" style={{ marginRight: 4 }}>&#x26A0;&#xFE0F;</span>}
                      {fieldName || '-'}
                    </td>
                    <td style={S.td}>
                      <span style={S.tag(
                        (input.field_type || input.type) === 'password' ? '#ef4444' :
                        (input.field_type || input.type) === 'hidden' ? '#f59e0b' :
                        (input.field_type || input.type) === 'text' ? '#3b82f6' : '#8888aa'
                      )}>
                        {input.field_type || input.type || 'text'}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontSize: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{input.url || '-'}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{input.form_method || '-'}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, color: '#8888aa', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{input.form_classification || '-'}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, color: '#8888aa', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{input.form_action || '-'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )
        ) : (
          parameters.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize: 14, marginBottom: 8 }}>No parameters detected</div>
              <div style={{ fontSize: 12 }}>Click "Detect Parameters" to scan captured traffic for URL and body parameters.</div>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Parameter</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Host</th>
                  <th style={S.th}>Endpoint</th>
                  <th style={S.th}>Example Value</th>
                  <th style={S.th}>Count</th>
                </tr>
              </thead>
              <tbody>
                {parameters.map((param, i) => {
                  const pName = param.param_name || param.name || '';
                  const isVuln = isParamVulnerable(pName, param.host || (param.endpoints || [])[0]);
                  return (
                  <tr key={i} style={{ ...S.row(false), background: isVuln ? '#ef444415' : undefined, borderLeft: isVuln ? '3px solid #ef4444' : undefined }}
                    onContextMenu={(e) => handleContextMenu(e, param)}
                    onMouseOver={e => e.currentTarget.style.background = isVuln ? '#ef444425' : '#16213e'}
                    onMouseOut={e => e.currentTarget.style.background = isVuln ? '#ef444415' : 'transparent'}
                  >
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: isVuln ? '#ef4444' : '#3b82f6' }}>
                      {isVuln && <span title="Vulnerability found!" style={{ marginRight: 4 }}>&#x26A0;&#xFE0F;</span>}
                      {pName || '-'}
                    </td>
                    <td style={S.td}>
                      <span style={S.tag(
                        param.param_type === 'query' ? '#3b82f6' :
                        param.param_type === 'body' ? '#22c55e' :
                        param.param_type === 'cookie' ? '#f59e0b' :
                        param.param_type === 'header' ? '#8b5cf6' : '#8888aa'
                      )}>
                        {param.param_type || 'unknown'}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontSize: 11, color: '#8888aa', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {param.host ? `${param.scheme || 'https'}://${param.host}` : '-'}
                    </td>
                    <td style={{ ...S.td, fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={(param.endpoints || []).join(', ')}>
                      {(param.endpoints || []).length > 0 ? param.endpoints[0] : '-'}
                      {(param.endpoints || []).length > 1 && <span style={{ color: '#8888aa', fontSize: 10 }}> +{param.endpoints.length - 1}</span>}
                    </td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, color: '#8888aa', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={(param.example_values || []).join(', ')}>
                      {(param.example_values || []).length > 0 ? param.example_values[0] : '-'}
                    </td>
                    <td style={{ ...S.td, fontSize: 12, color: '#8888aa' }}>{param.count || 1}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </div>

      {contextMenu && (
        <ContextMenu x={contextMenu.x} y={contextMenu.y} items={contextMenu.items} onClose={() => setContextMenu(null)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 6: SCANNER
// ═══════════════════════════════════════════════════════════
function ScannerTab({ proxyFetch, activeSessionId, wsRef, engagementId }) {
  const [jobs, setJobs] = useState([]);
  const [findings, setFindings] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('jobs');
  const [analyzingFindingId, setAnalyzingFindingId] = useState(null);

  const loadJobs = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const res = await proxyFetch(`/scanner/jobs?session_id=${activeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setJobs(Array.isArray(data) ? data : data.items || []);
      }
    } catch {}
  }, [proxyFetch, activeSessionId]);

  const loadFindings = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const res = await proxyFetch(`/scanner/findings?session_id=${activeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setFindings(Array.isArray(data) ? data : data.items || []);
      }
    } catch {}
  }, [proxyFetch, activeSessionId]);

  useEffect(() => { loadJobs(); loadFindings(); }, [loadJobs, loadFindings]);

  // WS listener for scan progress
  useEffect(() => {
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'scan_progress') {
          setJobs(prev => prev.map(j => j.id === msg.data?.job_id ? { ...j, ...msg.data } : j));
          if (msg.data?.status === 'complete') {
            loadFindings();
          }
        }
      } catch {}
    };
    const ws = wsRef.current;
    if (ws) ws.addEventListener('message', handler);
    return () => { if (ws) ws.removeEventListener('message', handler); };
  }, [wsRef.current, loadFindings]);

  const scanAll = async () => {
    if (!activeSessionId) return;
    setScanning(true);
    try {
      const res = await proxyFetch(`/scanner/all?session_id=${activeSessionId}`, { method: 'POST' });
      if (res.ok) {
        loadJobs();
      }
    } catch {}
    setScanning(false);
  };

  return (
    <div>
      <div style={S.toolbar}>
        <div style={S.tabBar}>
          <button style={S.tab(activeSubTab === 'jobs')} onClick={() => setActiveSubTab('jobs')}>Scan Jobs</button>
          <button style={S.tab(activeSubTab === 'findings')} onClick={() => setActiveSubTab('findings')}>Scan Findings ({findings.length})</button>
        </div>
        <div style={{ flex: 1 }} />
        <button style={S.btn('primary')} onClick={scanAll} disabled={scanning || !activeSessionId}>
          {scanning ? 'Starting...' : 'Scan All Parameters'}
        </button>
        {activeSubTab === 'jobs' && jobs.some(j => j.status === 'running') && (
          <button style={S.btn('danger')} onClick={async () => {
            for (const j of jobs.filter(j => j.status === 'running')) {
              await proxyFetch(`/scanner/stop/${j.id}`, { method: 'POST' });
            }
            loadJobs();
          }}>Stop All</button>
        )}
        <button style={S.btn()} onClick={() => { loadJobs(); loadFindings(); }}>Refresh</button>
        {activeSubTab === 'jobs' && jobs.length > 0 && (
          <button style={S.btn('danger')} onClick={async () => {
            if (!confirm('Delete all scan jobs?')) return;
            await proxyFetch(`/scanner/jobs?session_id=${activeSessionId}`, { method: 'DELETE' });
            loadJobs();
          }}>Delete All Jobs</button>
        )}
        {activeSubTab === 'findings' && findings.length > 0 && (
          <button style={S.btn('danger')} onClick={async () => {
            if (!confirm('Delete all scan findings?')) return;
            await proxyFetch(`/scanner/findings?session_id=${activeSessionId}`, { method: 'DELETE' });
            loadFindings();
          }}>Delete All Findings</button>
        )}
      </div>

      <div style={{ background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a', height: 'calc(100vh - 340px)', overflow: 'auto' }}>
        {activeSubTab === 'jobs' ? (
          jobs.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize: 14, marginBottom: 8 }}>No scan jobs</div>
              <div style={{ fontSize: 12 }}>Click "Scan All Parameters" or right-click a parameter to scan it.</div>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Target</th>
                  <th style={S.th}>Parameter</th>
                  <th style={S.th}>Progress</th>
                  <th style={S.th}>Findings</th>
                  <th style={{ ...S.th, width: 40 }}></th>
                  <th style={S.th}>Started</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => {
                  const cfg = job.config || {};
                  const summary = job.results_summary || {};
                  const paramName = cfg.param_name || (cfg.param_count ? `${cfg.param_count} params` : '-');
                  const target = cfg.request_id ? cfg.request_id.substring(0, 8) + '...' : job.target_type || '-';
                  const findingsCount = summary.findings || summary.total_findings || 0;
                  const progress = summary.tested && summary.total ? Math.round((summary.tested / summary.total) * 100) : null;
                  return (
                    <tr key={job.id} style={S.row(selectedJob === job.id)}
                      onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
                      onMouseOver={e => e.currentTarget.style.background = '#16213e'}
                      onMouseOut={e => e.currentTarget.style.background = selectedJob === job.id ? 'rgba(59,130,246,0.1)' : 'transparent'}
                    >
                      <td style={S.td}>
                        <span style={S.tag(
                          job.status === 'complete' ? '#22c55e' :
                          job.status === 'running' ? '#3b82f6' :
                          job.status === 'failed' ? '#ef4444' :
                          job.status === 'cancelled' ? '#8888aa' : '#f59e0b'
                        )}>
                          {job.status || 'pending'}
                        </span>
                      </td>
                      <td style={S.td}>
                        {(job.scan_type || '').split(',').map((t, i) => (
                          <span key={i} style={{ ...S.tag(t.trim() === 'sqli' ? '#ef4444' : t.trim() === 'xss' ? '#f59e0b' : '#3b82f6'), marginRight: 4 }}>
                            {t.trim().toUpperCase()}
                          </span>
                        ))}
                      </td>
                      <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {job.target_type === 'all' ? 'All session params' : job.target_type === 'request' ? `Request ${target}` : target}
                      </td>
                      <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, color: '#3b82f6' }}>{paramName}</td>
                      <td style={S.td}>
                        {job.status === 'running' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 60, height: 6, background: '#2a2a4a', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${progress || 30}%`, height: '100%', background: '#3b82f6', borderRadius: 3, transition: 'width 0.3s' }} />
                            </div>
                            {job.current_param && <span style={{ fontSize: 10, color: '#8888aa' }}>{job.current_param}</span>}
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: '#8888aa' }}>{job.status === 'complete' ? '100%' : '-'}</span>
                        )}
                      </td>
                      <td style={{ ...S.td, fontSize: 12, color: findingsCount > 0 ? '#ef4444' : '#8888aa', fontWeight: findingsCount > 0 ? 600 : 400 }}>
                        {findingsCount}
                      </td>
                      <td style={{ ...S.td, fontSize: 11, color: '#8888aa' }}>
                        {job.started_at ? new Date(job.started_at).toLocaleTimeString() : '-'}
                      </td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                        {job.status === 'running' && (
                          <button style={{ ...S.btn('danger'), padding: '2px 8px', fontSize: 11, marginRight: 4 }}
                            onClick={async (e) => {
                              e.stopPropagation();
                              await proxyFetch(`/scanner/stop/${job.id}`, { method: 'POST' });
                              loadJobs();
                            }} title="Stop scan">Stop</button>
                        )}
                        <button style={{ ...S.btn('danger'), padding: '2px 6px', fontSize: 11 }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await proxyFetch(`/scanner/jobs/${job.id}`, { method: 'DELETE' });
                            loadJobs();
                          }} title="Delete job">&times;</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        ) : (
          findings.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize: 14, marginBottom: 8 }}>No findings</div>
              <div style={{ fontSize: 12 }}>Run scans to discover vulnerabilities.</div>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Severity</th>
                  <th style={S.th}>Title</th>
                  <th style={S.th}>Affected URL</th>
                  <th style={S.th}>Evidence</th>
                  <th style={S.th}>Found</th>
                  <th style={{ ...S.th, width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {findings.map((finding, i) => {
                  // Extract type from title (e.g. "SQL Injection in 'x'" -> sqli, "Reflected XSS" -> xss)
                  const titleLower = (finding.title || '').toLowerCase();
                  const ftype = titleLower.includes('sql') ? 'sqli' : titleLower.includes('xss') ? 'xss' : 'other';
                  // Extract short URL for display
                  let shortUrl = finding.affected_url || '-';
                  try { const u = new URL(shortUrl); shortUrl = u.host + u.pathname; } catch {}
                  return (
                    <React.Fragment key={finding.id || i}>
                    <tr style={S.row(false)}
                      onMouseOver={e => e.currentTarget.style.background = '#16213e'}
                      onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={S.td}>
                        <SeverityBadge severity={finding.severity} />
                      </td>
                      <td style={{ ...S.td, fontSize: 13, maxWidth: 300 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={S.tag(ftype === 'sqli' ? '#ef4444' : ftype === 'xss' ? '#f59e0b' : '#3b82f6')}>
                            {ftype.toUpperCase()}
                          </span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {finding.title || 'Untitled'}
                          </span>
                        </div>
                      </td>
                      <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {shortUrl}
                      </td>
                      <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, color: '#8888aa', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {finding.evidence || finding.description || '-'}
                      </td>
                      <td style={{ ...S.td, fontSize: 11, color: '#8888aa' }}>
                        {finding.created_at ? new Date(finding.created_at).toLocaleTimeString() : '-'}
                      </td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                        <button style={{ ...S.btn('primary'), padding: '2px 8px', fontSize: 10, marginRight: 4 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnalyzingFindingId(analyzingFindingId === finding.id ? null : finding.id);
                          }} title="Analyze with AI">&#x1f916;</button>
                        <button style={{ ...S.btn('danger'), padding: '2px 6px', fontSize: 11 }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await proxyFetch(`/scanner/findings/${finding.id}`, { method: 'DELETE' });
                            loadFindings();
                          }} title="Delete finding">&times;</button>
                      </td>
                    </tr>
                    {analyzingFindingId === finding.id && (
                      <tr><td colSpan={7} style={{ padding: '0 8px 8px' }}>
                        <AIAnalysisPanel
                          findingData={finding}
                          engagementId={engagementId}
                          onClose={() => setAnalyzingFindingId(null)}
                        />
                      </td></tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 7: FINDINGS
// ═══════════════════════════════════════════════════════════
// ═══════════════════════ Collaborator Tab ═══════════════════════

function CollaboratorTab({ activeSessionId }) {
  const [interactions, setInteractions] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subTab, setSubTab] = useState('interactions');
  const [expandedId, setExpandedId] = useState(null);
  const [generateType, setGenerateType] = useState('ssrf');
  const generateMode = 'localhost';
  const [generating, setGenerating] = useState(false);
  const [generatedPayloads, setGeneratedPayloads] = useState([]);

  const COLLAB_API = (window.__ENV__?.REACT_APP_COLLABORATOR_URL || process.env.REACT_APP_COLLABORATOR_URL || 'http://localhost:8080') + '/api';
  const token = sessionStorage.getItem('token');

  const collabFetch = useCallback(async (path, opts = {}) => {
    const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${COLLAB_API}${path}`, { ...opts, headers });
  }, [token, COLLAB_API]);

  const loadInteractions = useCallback(async () => {
    if (!activeSessionId) return;
    setLoading(true);
    try {
      const res = await collabFetch(`/interactions?session_id=${activeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setInteractions(data.interactions || []);
      }
    } catch (e) { console.error('Collab interactions error:', e); }
    setLoading(false);
  }, [activeSessionId, collabFetch]);

  const loadTokens = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const res = await collabFetch(`/tokens?session_id=${activeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setTokens(data.tokens || []);
      }
    } catch (e) { console.error('Collab tokens error:', e); }
  }, [activeSessionId, collabFetch]);

  useEffect(() => { loadInteractions(); loadTokens(); }, [loadInteractions, loadTokens]);

  // Auto-poll every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => { loadInteractions(); }, 5000);
    return () => clearInterval(interval);
  }, [loadInteractions]);

  const generatePayloads = async () => {
    setGenerating(true);
    try {
      const res = await collabFetch('/generate-payload', {
        method: 'POST',
        body: JSON.stringify({ payload_type: generateType, session_id: activeSessionId || null, mode: generateMode }),
      });
      if (res.ok) {
        const data = await res.json();
        setGeneratedPayloads(data.payloads || []);
        loadTokens();
      } else {
        const err = await res.text();
        console.error('Generate payload failed:', res.status, err);
        alert(`Failed to generate payloads: ${res.status} ${err.slice(0, 200)}`);
      }
    } catch (e) {
      console.error('Generate error:', e);
      alert(`Connection error: ${e.message}. Is the Collaborator service running on port 8080?`);
    }
    setGenerating(false);
  };

  const deleteAll = async () => {
    if (!window.confirm('Delete all collaborator tokens and interactions?')) return;
    await collabFetch(`/interactions?session_id=${activeSessionId}`, { method: 'DELETE' });
    loadInteractions();
    loadTokens();
    setGeneratedPayloads([]);
  };

  const sevColor = (type) => ({
    sqli: '#ef4444', cmdi: '#ef4444', xxe: '#f97316', ssrf: '#f59e0b', xss: '#3b82f6', generic: '#8888aa',
  }[type] || '#8888aa');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{ padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid #2a2a4a', flexShrink: 0 }}>
        <button style={S.tab(subTab === 'interactions')} onClick={() => setSubTab('interactions')}>
          Interactions ({interactions.length})
        </button>
        <button style={S.tab(subTab === 'tokens')} onClick={() => setSubTab('tokens')}>
          Tokens ({tokens.length})
        </button>
        <button style={S.tab(subTab === 'generate')} onClick={() => setSubTab('generate')}>
          Generate Payloads
        </button>
        <span style={{ flex: 1 }} />
        <button style={S.btn()} onClick={() => { loadInteractions(); loadTokens(); }}>Refresh</button>
        {(interactions.length > 0 || tokens.length > 0) && (
          <button style={S.btn('danger')} onClick={deleteAll}>Clear All</button>
        )}
      </div>

      {/* Interaction count banner */}
      {interactions.length > 0 && subTab !== 'interactions' && (
        <div style={{
          padding: '6px 12px', background: '#22c55e15', borderBottom: '1px solid #22c55e30',
          fontSize: 11, color: '#22c55e', cursor: 'pointer',
        }} onClick={() => setSubTab('interactions')}>
          {interactions.length} OOB interaction{interactions.length !== 1 ? 's' : ''} received — click to view
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {/* Interactions */}
        {subTab === 'interactions' && (
          interactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#8888aa', fontSize: 12 }}>
              {loading ? 'Polling for interactions...' : 'No OOB interactions received yet. Generate payloads and inject them into targets.'}
              <div style={{ marginTop: 8, fontSize: 10 }}>Auto-polling every 5 seconds</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2a2a4a', position: 'sticky', top: 0, background: '#0d1117', zIndex: 1 }}>
                  <th style={S.th}>Time</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Token</th>
                  <th style={S.th}>Source IP</th>
                  <th style={S.th}>Method</th>
                  <th style={S.th}>Path</th>
                  <th style={S.th}>Param</th>
                </tr>
              </thead>
              <tbody>
                {interactions.map(int => (
                  <tr key={int.id} style={{ borderBottom: '1px solid #1a1a2a', cursor: 'pointer',
                    background: expandedId === int.id ? '#16213e' : `${sevColor(int.payload_type)}08`,
                  }} onClick={() => setExpandedId(expandedId === int.id ? null : int.id)}>
                    <td style={{ ...S.td, fontSize: 10, fontFamily: 'monospace', color: '#8888aa' }}>
                      {new Date(int.created_at).toLocaleTimeString('en-US', { hour12: false })}
                    </td>
                    <td style={S.td}>
                      <span style={{
                        padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                        background: `${sevColor(int.payload_type)}20`, color: sevColor(int.payload_type),
                      }}>{(int.payload_type || 'http').toUpperCase()}</span>
                    </td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 10, color: '#3b82f6' }}>{int.token}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, color: '#e2e8f0' }}>{int.source_ip || '-'}</td>
                    <td style={{ ...S.td, fontSize: 11 }}>{int.method || '-'}</td>
                    <td style={{ ...S.td, fontSize: 10, fontFamily: 'monospace', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{int.path || '-'}</td>
                    <td style={{ ...S.td, fontSize: 11, color: '#f59e0b' }}>{int.param_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* Tokens */}
        {subTab === 'tokens' && (
          tokens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#8888aa', fontSize: 12 }}>
              No active tokens. Generate payloads to create tokens.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2a2a4a', position: 'sticky', top: 0, background: '#0d1117' }}>
                  <th style={S.th}>Token</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Param</th>
                  <th style={S.th}>Callback URL</th>
                  <th style={S.th}>Interactions</th>
                  <th style={S.th}>Expires</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map(tok => (
                  <tr key={tok.id} style={{ borderBottom: '1px solid #1a1a2a',
                    background: tok.interaction_count > 0 ? '#22c55e10' : 'transparent',
                  }}>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 10, color: '#3b82f6' }}>{tok.token}</td>
                    <td style={S.td}>
                      <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                        background: `${sevColor(tok.payload_type)}20`, color: sevColor(tok.payload_type),
                      }}>{tok.payload_type}</span>
                    </td>
                    <td style={{ ...S.td, color: '#f59e0b', fontSize: 11 }}>{tok.param_name || '-'}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 10, color: '#8888aa', cursor: 'pointer' }}
                      onClick={() => navigator.clipboard.writeText(tok.callback_url)} title="Click to copy">
                      {tok.callback_url}
                    </td>
                    <td style={{ ...S.td, color: tok.interaction_count > 0 ? '#22c55e' : '#8888aa', fontWeight: tok.interaction_count > 0 ? 700 : 400 }}>
                      {tok.interaction_count}
                    </td>
                    <td style={{ ...S.td, fontSize: 10, color: '#8888aa' }}>
                      {new Date(tok.expires_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* Generate Payloads */}
        {subTab === 'generate' && (
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 8 }}>Generate OOB Payloads</div>
              <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 12 }}>
                Creates unique collaborator tokens and generates payloads you can inject into targets.
                When the target calls back, the interaction is logged here.
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select value={generateType} onChange={e => setGenerateType(e.target.value)}
                  style={{ padding: '6px 10px', background: '#16213e', color: '#e2e8f0', border: '1px solid #2a2a4a', borderRadius: 6, fontSize: 12 }}>
                  <option value="ssrf">SSRF</option>
                  <option value="sqli">Blind SQLi</option>
                  <option value="cmdi">Command Injection</option>
                  <option value="xxe">XXE</option>
                  <option value="xss">Blind XSS</option>
                </select>
                <button style={S.btn('primary')} onClick={generatePayloads} disabled={generating}>
                  {generating ? 'Generating...' : 'Generate Payloads'}
                </button>
              </div>
            </div>

            {generatedPayloads.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#22c55e', marginBottom: 8 }}>
                  Generated {generatedPayloads.length} payload{generatedPayloads.length !== 1 ? 's' : ''}
                </div>
                {generatedPayloads.map((p, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', marginBottom: 8, background: '#16213e',
                    borderRadius: 8, border: '1px solid #2a2a4a',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 10, color: '#3b82f6', fontFamily: 'monospace' }}>Token: {p.token}</span>
                      <button style={{ ...S.btn(), padding: '2px 8px', fontSize: 10 }}
                        onClick={() => navigator.clipboard.writeText(p.payload)}>Copy</button>
                    </div>
                    <pre style={{
                      background: '#0d1117', padding: '6px 10px', borderRadius: 6, margin: 0,
                      fontSize: 11, color: '#e2e8f0', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    }}>{p.payload}</pre>
                    <div style={{ fontSize: 9, color: '#8888aa', marginTop: 4 }}>
                      Callback: <span style={{ color: '#f59e0b' }}>{p.callback_url}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════ Libraries Tab ═══════════════════════

function LibrariesTab({ proxyFetch, activeSessionId }) {
  const [libraries, setLibraries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedLib, setExpandedLib] = useState(null);

  const loadLibraries = useCallback(async () => {
    if (!activeSessionId) return;
    setLoading(true);
    try {
      const res = await proxyFetch(`/analysis/libraries?session_id=${activeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setLibraries(data.libraries || []);
        setStats({ total: data.total_libraries, vulnerable: data.vulnerable_libraries, vulns: data.total_vulnerabilities, cves: data.cves || [] });
      }
    } catch (e) { console.error('Failed to load libraries:', e); }
    setLoading(false);
  }, [activeSessionId, proxyFetch]);

  useEffect(() => { loadLibraries(); }, [loadLibraries]);

  const rescan = async () => {
    setLoading(true);
    try {
      await proxyFetch(`/analysis/libraries/rescan?session_id=${activeSessionId}`, { method: 'POST' });
      await loadLibraries();
    } catch (e) { console.error('Rescan failed:', e); }
    setLoading(false);
  };

  const deleteAll = async () => {
    if (!window.confirm('Delete all library detection results?')) return;
    await proxyFetch(`/analysis/libraries?session_id=${activeSessionId}`, { method: 'DELETE' });
    loadLibraries();
  };

  const sevColor = (s) => ({ Critical: '#ef4444', High: '#f97316', Medium: '#f59e0b', Low: '#3b82f6', Info: '#8888aa' }[s] || '#8888aa');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Stats bar */}
      <div style={{ padding: '8px 12px', display: 'flex', gap: 16, alignItems: 'center', borderBottom: '1px solid #2a2a4a', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
          <span style={{ color: '#e2e8f0' }}><strong>{stats.total || 0}</strong> libraries</span>
          {stats.vulnerable > 0 && <span style={{ color: '#ef4444' }}><strong>{stats.vulnerable}</strong> vulnerable</span>}
          {stats.vulns > 0 && <span style={{ color: '#f59e0b' }}><strong>{stats.vulns}</strong> CVEs</span>}
        </div>
        <span style={{ flex: 1 }} />
        <button style={S.btn()} onClick={rescan} disabled={loading}>{loading ? 'Scanning...' : 'Rescan All'}</button>
        <button style={S.btn()} onClick={loadLibraries} disabled={loading}>Refresh</button>
        {libraries.length > 0 && <button style={S.btn('danger')} onClick={deleteAll}>Clear</button>}
      </div>

      {/* CVE summary */}
      {stats.cves && stats.cves.length > 0 && (
        <div style={{ padding: '6px 12px', background: '#ef444410', borderBottom: '1px solid #ef444430', fontSize: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {stats.cves.map(cve => (
            <a key={cve} href={`https://nvd.nist.gov/vuln/detail/${cve}`} target="_blank" rel="noopener noreferrer"
              style={{ color: '#ef4444', textDecoration: 'none', fontFamily: 'monospace' }}>{cve}</a>
          ))}
        </div>
      )}

      {/* Library list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {libraries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#8888aa', fontSize: 12 }}>
            {loading ? 'Scanning for libraries...' : 'No libraries detected yet. Browse a site through the proxy to auto-detect.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a4a', position: 'sticky', top: 0, background: '#0d1117', zIndex: 1 }}>
                <th style={S.th}>Severity</th>
                <th style={S.th}>Library</th>
                <th style={S.th}>Version</th>
                <th style={S.th}>Detection</th>
                <th style={S.th}>CVEs</th>
                <th style={S.th}>Source</th>
              </tr>
            </thead>
            <tbody>
              {libraries.map((lib, i) => {
                const isExpanded = expandedLib === lib.id;
                return (
                  <React.Fragment key={lib.id || i}>
                    <tr
                      style={{ borderBottom: '1px solid #1a1a2a', cursor: lib.vulnerabilities.length > 0 ? 'pointer' : 'default',
                        background: isExpanded ? '#16213e' : lib.vulnerabilities.length > 0 ? `${sevColor(lib.severity)}08` : 'transparent',
                      }}
                      onClick={() => lib.vulnerabilities.length > 0 && setExpandedLib(isExpanded ? null : lib.id)}
                    >
                      <td style={S.td}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          background: `${sevColor(lib.severity)}20`, color: sevColor(lib.severity),
                        }}>{lib.severity}</span>
                      </td>
                      <td style={{ ...S.td, fontWeight: 600, color: '#e2e8f0' }}>{lib.library}</td>
                      <td style={{ ...S.td, fontFamily: 'monospace', color: lib.vulnerabilities.length > 0 ? '#ef4444' : '#22c55e' }}>
                        {lib.version}
                      </td>
                      <td style={{ ...S.td, fontSize: 10, color: '#8888aa' }}>{lib.detection_method}</td>
                      <td style={{ ...S.td, color: lib.vulnerabilities.length > 0 ? '#ef4444' : '#8888aa' }}>
                        {lib.vulnerabilities.length > 0 ? `${lib.vulnerabilities.length} CVEs` : 'None known'}
                      </td>
                      <td style={{ ...S.td, fontSize: 10, color: '#8888aa', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(lib.sources || []).join(', ') || '-'}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0, background: '#0a0a14' }}>
                          <div style={{ padding: '8px 16px' }}>
                            {lib.vulnerabilities.map((v, j) => (
                              <div key={j} style={{ padding: '6px 0', borderBottom: j < lib.vulnerabilities.length - 1 ? '1px solid #1a1a2a' : 'none', display: 'flex', gap: 12, alignItems: 'center' }}>
                                <span style={{
                                  padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                                  background: `${sevColor(v.severity)}20`, color: sevColor(v.severity), flexShrink: 0,
                                }}>{v.severity}</span>
                                {v.cve && (
                                  <a href={`https://nvd.nist.gov/vuln/detail/${v.cve}`} target="_blank" rel="noopener noreferrer"
                                    style={{ color: '#3b82f6', fontFamily: 'monospace', fontSize: 11, flexShrink: 0 }}>{v.cve}</a>
                                )}
                                <span style={{ fontSize: 11, color: '#ccc' }}>{v.info}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function FindingsTab({ proxyFetch, activeSessionId, engagementId }) {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [pushStatus, setPushStatus] = useState({});
  const [pushError, setPushError] = useState('');
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);

  const loadFindings = useCallback(async () => {
    if (!activeSessionId) return;
    setLoading(true);
    try {
      const res = await proxyFetch(`/findings?session_id=${activeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setFindings(Array.isArray(data) ? data : data.items || []);
      }
    } catch {}
    setLoading(false);
  }, [proxyFetch, activeSessionId]);

  useEffect(() => { loadFindings(); }, [loadFindings]);

  const pushFinding = async (findingId) => {
    if (!engagementId) {
      setPushError('You must select an engagement before pushing findings to AttackPath.');
      return;
    }
    setPushError('');
    setPushStatus(prev => ({ ...prev, [findingId]: 'pushing' }));
    try {
      // Find the finding data
      const finding = findings.find(f => f.id === findingId);
      if (!finding) { setPushStatus(prev => ({ ...prev, [findingId]: 'error' })); return; }

      // Call backend Express API directly to create finding
      const token = sessionStorage.getItem('token');
      const backendRes = await fetch('/api/findings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          engagementId: engagementId,
          title: finding.title || 'Untitled Finding',
          severity: (finding.severity || 'Medium'),
          details: finding.description || '',
          observation: finding.description || '',
          stepsToReproduce: finding.evidence || '',
          affectedEndpoints: finding.affected_url || '',
          recommendation: finding.recommendation || '',
          status: 'Open',
        }),
      });

      if (backendRes.ok) {
        const result = await backendRes.json();
        // Mark as pushed in proxy DB
        await proxyFetch(`/findings/${findingId}/mark-pushed`, {
          method: 'PUT',
          body: JSON.stringify({
            engagement_id: engagementId,
            attackpath_finding_id: result.id || null,
          }),
        });
        setPushStatus(prev => ({ ...prev, [findingId]: 'pushed' }));
        loadFindings();
      } else {
        const err = await backendRes.json().catch(() => ({}));
        setPushError(err.error || `Push failed (${backendRes.status})`);
        setPushStatus(prev => ({ ...prev, [findingId]: 'error' }));
      }
    } catch (err) {
      setPushError(err.message || 'Push failed');
      setPushStatus(prev => ({ ...prev, [findingId]: 'error' }));
    }
  };

  // Helper to extract type from title
  const getTypeFromTitle = (finding) => {
    const title = (finding.title || '').toLowerCase();
    if (title.includes('sql') || title.includes('sqli')) return 'SQLi';
    if (title.includes('stored xss')) return 'Stored XSS';
    if (title.includes('xss')) return 'XSS';
    return 'VULN';
  };

  const getTypeColor = (type) => {
    if (type === 'SQLi') return '#ef4444';
    if (type === 'Stored XSS') return '#dc2626';
    if (type === 'XSS') return '#f59e0b';
    return '#8888aa';
  };

  // Extract URL host from affected_url
  const getHost = (finding) => {
    try {
      if (finding.affected_url) return new URL(finding.affected_url).host;
    } catch {}
    return '-';
  };

  const codeBlock = { background: '#0f3460', padding: 12, borderRadius: 6, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#e2e8f0' };

  return (
    <div>
      <div style={S.toolbar}>
        <button style={S.btn()} onClick={loadFindings} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh Findings'}
        </button>
        <span style={S.stat}>{findings.length} findings</span>
        {!engagementId && (
          <span style={{ fontSize: 12, color: '#f59e0b', marginLeft: 12 }}>
            &#9888; Select an engagement above to enable push to AttackPath
          </span>
        )}
      </div>

      {pushError && (
        <div style={{ padding: '8px 16px', background: '#7f1d1d', borderRadius: 8, marginBottom: 8, color: '#fecaca', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{pushError}</span>
          <button onClick={() => setPushError('')} style={{ background: 'none', border: 'none', color: '#fecaca', cursor: 'pointer', fontSize: 16 }}>&#x2715;</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 340px)', minHeight: 400 }}>
        <div style={{ flex: selectedFinding ? '0 0 50%' : 1, overflow: 'auto', background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a' }}>
          {findings.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize: 14, marginBottom: 8 }}>No findings</div>
              <div style={{ fontSize: 12 }}>Run scans from the Scanner or Inputs tab to discover vulnerabilities.</div>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Severity</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Title</th>
                  <th style={S.th}>URL</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {findings.map(finding => {
                  const vulnType = getTypeFromTitle(finding);
                  return (
                    <tr key={finding.id} style={S.row(selectedFinding?.id === finding.id)}
                      onClick={() => setSelectedFinding(finding)}
                      onMouseOver={e => { if (selectedFinding?.id !== finding.id) e.currentTarget.style.background = '#16213e'; }}
                      onMouseOut={e => { if (selectedFinding?.id !== finding.id) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={S.td}><SeverityBadge severity={finding.severity} /></td>
                      <td style={S.td}>
                        <span style={S.tag(getTypeColor(vulnType))}>
                          {vulnType}
                        </span>
                      </td>
                      <td style={{ ...S.td, fontSize: 13, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {finding.title || 'Untitled'}
                      </td>
                      <td style={{ ...S.td, fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                        {finding.affected_url || getHost(finding) || '-'}
                      </td>
                      <td style={S.td}>
                        <span style={S.tag(
                          finding.pushed_to_attackpath ? '#22c55e' : '#8888aa'
                        )}>
                          {finding.pushed_to_attackpath ? 'Pushed' : 'Local'}
                        </span>
                      </td>
                      <td style={S.td}>
                        {!finding.pushed_to_attackpath && (
                          <button
                            style={{ ...S.btn(engagementId ? 'primary' : undefined), padding: '2px 10px', fontSize: 11, opacity: engagementId ? 1 : 0.5 }}
                            onClick={(e) => { e.stopPropagation(); pushFinding(finding.id); }}
                            disabled={pushStatus[finding.id] === 'pushing'}
                            title={!engagementId ? 'Select an engagement first' : 'Push to AttackPath'}
                          >
                            {pushStatus[finding.id] === 'pushing' ? 'Pushing...' :
                             pushStatus[finding.id] === 'pushed' ? 'Pushed' :
                             pushStatus[finding.id] === 'error' ? 'Retry' :
                             'Push'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {selectedFinding && (
          <div style={{ flex: '0 0 49%', minWidth: 0, background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a', overflow: 'auto', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>{selectedFinding.title || 'Finding Details'}</div>
              <button onClick={() => setSelectedFinding(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#8888aa' }}>&#x2715;</button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <SeverityBadge severity={selectedFinding.severity} />
              <span style={S.tag(getTypeColor(getTypeFromTitle(selectedFinding)))}>
                {getTypeFromTitle(selectedFinding)}
              </span>
              {selectedFinding.pushed_to_attackpath && (
                <span style={S.tag('#22c55e')}>Pushed</span>
              )}
            </div>

            {/* Affected URL */}
            {selectedFinding.affected_url && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Affected URL</div>
                <div style={{ ...codeBlock, fontSize: 12 }}>
                  {selectedFinding.affected_url}
                </div>
              </div>
            )}

            {/* Vulnerable Parameter - extract from title */}
            {(() => {
              const match = (selectedFinding.title || '').match(/in '([^']+)'/);
              return match ? (
                <div style={S.section}>
                  <div style={S.sectionTitle}>Vulnerable Parameter</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#f59e0b', padding: '4px 8px', background: '#1e293b', borderRadius: 4, display: 'inline-block' }}>
                    {match[1]}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Description */}
            {selectedFinding.description && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Description</div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: '#e2e8f0' }}>{selectedFinding.description}</div>
              </div>
            )}

            {/* Evidence / Payload */}
            {selectedFinding.evidence && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Evidence / Payload</div>
                <div style={codeBlock}>
                  {selectedFinding.evidence}
                </div>
              </div>
            )}

            {/* Request ID */}
            {selectedFinding.request_id && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Request ID</div>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#8888aa' }}>
                  {selectedFinding.request_id}
                </div>
              </div>
            )}

            {/* Recommendation */}
            {selectedFinding.recommendation && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Recommendation</div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: '#e2e8f0' }}>{selectedFinding.recommendation}</div>
              </div>
            )}

            {/* Pushed info */}
            {selectedFinding.pushed_to_attackpath && selectedFinding.pushed_at && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Push Details</div>
                <div style={{ fontSize: 12, color: '#8888aa' }}>
                  Pushed at: {new Date(selectedFinding.pushed_at).toLocaleString()}
                  {selectedFinding.attackpath_finding_id && (
                    <span style={{ marginLeft: 8 }}>| Finding ID: {selectedFinding.attackpath_finding_id}</span>
                  )}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div style={S.section}>
              <div style={S.sectionTitle}>Discovered</div>
              <div style={{ fontSize: 12, color: '#8888aa' }}>
                {selectedFinding.created_at ? new Date(selectedFinding.created_at).toLocaleString() : '-'}
              </div>
            </div>

            {/* Push button */}
            {!selectedFinding.pushed_to_attackpath && (
              <div style={{ marginTop: 12 }}>
                {!engagementId ? (
                  <div style={{ padding: '10px 16px', background: '#1e293b', borderRadius: 8, border: '1px solid #f59e0b33', color: '#f59e0b', fontSize: 13, textAlign: 'center' }}>
                    &#9888; Select an engagement above to push findings to AttackPath
                  </div>
                ) : (
                  <button
                    style={{ ...S.btn('primary'), width: '100%' }}
                    onClick={() => pushFinding(selectedFinding.id)}
                    disabled={pushStatus[selectedFinding.id] === 'pushing'}
                  >
                    {pushStatus[selectedFinding.id] === 'pushing' ? 'Pushing...' : 'Push to AttackPath'}
                  </button>
                )}
              </div>
            )}

            {/* AI Analysis */}
            <div style={{ marginTop: 12 }}>
              <button
                style={{ ...S.btn(showAiAnalysis ? undefined : 'primary'), width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onClick={() => setShowAiAnalysis(!showAiAnalysis)}
              >
                &#x1f916; {showAiAnalysis ? 'Hide AI Analysis' : 'Analyze with AI'}
              </button>
            </div>
            {showAiAnalysis && (
              <AIAnalysisPanel
                findingData={selectedFinding}
                engagementId={engagementId}
                onClose={() => setShowAiAnalysis(false)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// INTRUDER TAB (Burp Intruder clone)
// ═══════════════════════════════════════════════════════════

const PAYLOAD_PRESETS = {
  'XSS': [
    '<script>alert(1)</script>',
    '<img src=x onerror=alert(1)>',
    '"><script>alert(1)</script>',
    "'><script>alert(1)</script>",
    '<svg onload=alert(1)>',
    '<svg/onload=alert(1)>',
    'javascript:alert(1)',
    '"><img src=x onerror=alert(1)>',
    '<details open ontoggle=alert(1)>',
    '<body onload=alert(1)>',
    '<iframe src="javascript:alert(1)">',
    '<input onfocus=alert(1) autofocus>',
    '<marquee onstart=alert(1)>',
    '<select onfocus=alert(1) autofocus>',
    '<textarea onfocus=alert(1) autofocus>',
    '<video><source onerror=alert(1)>',
    '<audio src=x onerror=alert(1)>',
    "'-alert(1)-'",
    '"-alert(1)-"',
    '${alert(1)}',
    '{{constructor.constructor("alert(1)")()}}',
    '<a href="javascript:alert(1)">click</a>',
    '<math><mtext><table><mglyph><svg><mtext><textarea><path id="</textarea><img onerror=alert(1) src=1>">',
    '<img src=x onerror="&#97;lert(1)">',
    '%3Cscript%3Ealert(1)%3C/script%3E',
  ],
  'SQLi': [
    "'", '"', "' OR '1'='1", "' OR '1'='1' --", '" OR "1"="1',
    "' OR 1=1 --", "admin' --", "1' ORDER BY 100 --",
    "' UNION SELECT NULL --", "' UNION SELECT NULL,NULL --",
    "1; WAITFOR DELAY '0:0:3' --", "1' AND SLEEP(3) --",
    "'; SELECT pg_sleep(3) --", "' AND '1'='1", "') OR ('1'='1",
    "1 OR 1=1", "1' OR '1'='1'/*", "' OR ''='",
  ],
  'Auth Bypass': [
    'admin', 'administrator', 'root', 'test', 'guest',
    "admin' --", "admin'/*", "' OR 1=1 --", "' OR 1=1#",
    "admin' OR '1'='1", "admin') OR ('1'='1'--",
    "') OR ('x'='x", "' OR username IS NOT NULL OR username = '",
  ],
  'Path Traversal': [
    '../../../etc/passwd', '..\\..\\..\\windows\\system32\\drivers\\etc\\hosts',
    '....//....//....//etc/passwd', '..%2f..%2f..%2fetc%2fpasswd',
    '..%252f..%252f..%252fetc%252fpasswd', '/etc/passwd',
    '%2e%2e/%2e%2e/%2e%2e/etc/passwd', '....//....//etc/shadow',
    '..\\..\\..\\..\\windows\\win.ini', '/proc/self/environ',
  ],
  'SSTI': [
    '{{7*7}}', '${7*7}', '<%= 7*7 %>', '#{7*7}', '{7*7}',
    '{{config}}', '{{self.__class__}}', '${T(java.lang.Runtime).getRuntime()}',
    '{{request.application.__globals__}}', '{{"".__class__.__mro__}}',
  ],
  'Command Injection': [
    '; ls', '| ls', '`ls`', '$(ls)', '; cat /etc/passwd',
    '| cat /etc/passwd', '`cat /etc/passwd`', '$(cat /etc/passwd)',
    '; sleep 5', '| sleep 5', '& ping -c 3 127.0.0.1 &',
    '|| whoami', '&& whoami', '; whoami',
  ],
};

const ATTACK_TYPES = [
  { value: 'sniper', label: 'Sniper', desc: 'Tests one position at a time with its payload list. Other positions use original values.' },
  { value: 'battering_ram', label: 'Battering Ram', desc: 'Uses the same payload in all positions simultaneously.' },
  { value: 'pitchfork', label: 'Pitchfork', desc: 'Uses payloads from each set in parallel (zipped). Stops at shortest list.' },
  { value: 'cluster_bomb', label: 'Cluster Bomb', desc: 'Tests all combinations of all payload sets (cartesian product). Can generate many requests!' },
];

const PROCESSING_TYPES = [
  { value: 'url_encode', label: 'URL Encode' },
  { value: 'url_decode', label: 'URL Decode' },
  { value: 'base64_encode', label: 'Base64 Encode' },
  { value: 'base64_decode', label: 'Base64 Decode' },
  { value: 'html_encode', label: 'HTML Encode' },
  { value: 'md5', label: 'MD5 Hash' },
  { value: 'sha1', label: 'SHA1 Hash' },
  { value: 'sha256', label: 'SHA256 Hash' },
  { value: 'add_prefix', label: 'Add Prefix' },
  { value: 'add_suffix', label: 'Add Suffix' },
];

const MARKER = '\u00a7';

function IntruderTab({ proxyFetch, activeSessionId, intruderTabs, setIntruderTabs, wsRef }) {
  const [activeIntruderId, setActiveIntruderId] = useState(intruderTabs[0]?.id || null);
  const [activeSubTab, setActiveSubTab] = useState('positions');
  const [attacks, setAttacks] = useState({});
  const [wordlists, setWordlists] = useState([]);
  const [totalPreview, setTotalPreview] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [resultDetail, setResultDetail] = useState(null);
  const [resultSort, setResultSort] = useState({ by: 'request_index', dir: 'asc' });
  const [resultFilter, setResultFilter] = useState('');
  const [resultPage, setResultPage] = useState(0);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (intruderTabs.length > 0 && !intruderTabs.find(t => t.id === activeIntruderId)) {
      setActiveIntruderId(intruderTabs[intruderTabs.length - 1].id);
    }
  }, [intruderTabs, activeIntruderId]);

  // Load wordlists once
  useEffect(() => {
    proxyFetch('/intruder/wordlists').then(r => r.ok ? r.json() : []).then(setWordlists).catch(() => {});
  }, [proxyFetch]);

  // WebSocket listener for intruder events
  useEffect(() => {
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'intruder_result') {
          const d = msg.data;
          setAttacks(prev => {
            const attack = prev[d.attack_id] || { status: 'running', completed: 0, total: 0, errors: 0, results: [] };
            const newResults = [...attack.results, {
              id: d.result_id,
              request_index: d.request_index,
              payload_values: d.payload_values,
              response_status: d.response_status,
              response_length: d.response_length,
              duration_ms: d.duration_ms,
              error: d.error,
            }];
            // Keep last 500 in memory
            if (newResults.length > 500) newResults.splice(0, newResults.length - 500);
            return { ...prev, [d.attack_id]: { ...attack, results: newResults, completed: d.completed || attack.completed + 1, total: d.total || attack.total } };
          });
        } else if (msg.type === 'intruder_progress') {
          const d = msg.data;
          setAttacks(prev => ({
            ...prev,
            [d.attack_id]: { ...(prev[d.attack_id] || {}), completed: d.completed, total: d.total, errors: d.errors },
          }));
        } else if (msg.type === 'intruder_complete') {
          const d = msg.data;
          setAttacks(prev => ({
            ...prev,
            [d.attack_id]: { ...(prev[d.attack_id] || {}), status: d.status, completed: d.completed || 0, total: d.total || 0, errors: d.errors || 0 },
          }));
        }
      } catch {}
    };
    const ws = wsRef.current;
    if (ws) ws.addEventListener('message', handler);
    return () => { if (ws) ws.removeEventListener('message', handler); };
  }, [wsRef.current]);

  const activeInstance = intruderTabs.find(t => t.id === activeIntruderId);

  const updateTab = (tabId, updates) => {
    setIntruderTabs(prev => prev.map(t => t.id === tabId ? { ...t, ...updates } : t));
  };

  const addNewTab = () => {
    const id = crypto.randomUUID();
    const newTab = {
      id, name: `Tab ${intruderTabs.length + 1}`,
      target: { scheme: 'https', host: 'example.com', port: 443 },
      templateRequest: 'GET / HTTP/1.1\r\nHost: example.com\r\n',
      attackType: 'sniper',
      payloadSets: [{ type: 'simple_list', items: [] }],
      processingRules: [],
      concurrency: 10, delayMs: 0, timeoutMs: 30000,
      followRedirects: false, storeRequests: false,
    };
    setIntruderTabs(prev => [...prev, newTab]);
    setActiveIntruderId(id);
  };

  const closeTab = (tabId) => {
    setIntruderTabs(prev => {
      const next = prev.filter(t => t.id !== tabId);
      if (activeIntruderId === tabId && next.length > 0) setActiveIntruderId(next[next.length - 1].id);
      return next;
    });
  };

  // Count marker positions in template
  const countPositions = (tmpl) => {
    if (!tmpl) return 0;
    return (tmpl.split(MARKER).length - 1) / 2 | 0;
  };

  // Store selection before textarea loses focus (button mousedown fires before blur)
  const selectionRef = useRef({ start: 0, end: 0 });
  const captureSelection = () => {
    if (textareaRef.current) {
      selectionRef.current = { start: textareaRef.current.selectionStart, end: textareaRef.current.selectionEnd };
    }
  };

  const addMarker = () => {
    if (!activeInstance) return;
    const { start, end } = selectionRef.current;
    const text = activeInstance.templateRequest;
    const newText = text.substring(0, start) + MARKER + text.substring(start, end) + MARKER + text.substring(end);
    updateTab(activeInstance.id, { templateRequest: newText });
  };

  const clearMarkers = () => {
    if (!activeInstance) return;
    updateTab(activeInstance.id, { templateRequest: activeInstance.templateRequest.replace(new RegExp(MARKER, 'g'), '') });
  };

  const autoDetectPositions = () => {
    if (!activeInstance) return;
    let tmpl = activeInstance.templateRequest.replace(new RegExp(MARKER, 'g'), '');
    const parsed = parseRawRequest(tmpl);
    // Mark query params
    if (parsed.queryString) {
      const params = parsed.queryString.split('&');
      for (const param of params) {
        const eq = param.indexOf('=');
        if (eq > 0) {
          const val = param.substring(eq + 1);
          if (val) tmpl = tmpl.replace(val, MARKER + val + MARKER);
        }
      }
    }
    // Mark body params (form-encoded)
    if (parsed.body && parsed.body.includes('=')) {
      const params = parsed.body.split('&');
      for (const param of params) {
        const eq = param.indexOf('=');
        if (eq > 0) {
          const val = param.substring(eq + 1);
          if (val) {
            const idx = tmpl.lastIndexOf(val);
            if (idx > -1) tmpl = tmpl.substring(0, idx) + MARKER + val + MARKER + tmpl.substring(idx + val.length);
          }
        }
      }
    }
    updateTab(activeInstance.id, { templateRequest: tmpl });
  };

  // Ensure enough payload sets for positions
  const ensurePayloadSets = (inst, numPositions) => {
    if (inst.payloadSets.length < numPositions) {
      const newSets = [...inst.payloadSets];
      while (newSets.length < numPositions) newSets.push({ type: 'simple_list', items: [] });
      updateTab(inst.id, { payloadSets: newSets });
    }
  };

  // Start attack
  const startAttack = async () => {
    if (!activeInstance || !activeSessionId) return;
    const numPos = countPositions(activeInstance.templateRequest);
    if (numPos === 0) return;

    try {
      const res = await proxyFetch(`/intruder/attacks?session_id=${activeSessionId}`, {
        method: 'POST',
        body: JSON.stringify({
          name: activeInstance.name,
          template_request: activeInstance.templateRequest,
          target_scheme: activeInstance.target.scheme,
          target_host: activeInstance.target.host,
          target_port: activeInstance.target.port,
          attack_type: activeInstance.attackType,
          positions: [],
          payload_sets: activeInstance.payloadSets.slice(0, numPos),
          processing_rules: activeInstance.processingRules,
          concurrency: activeInstance.concurrency,
          delay_ms: activeInstance.delayMs,
          timeout_ms: activeInstance.timeoutMs,
          follow_redirects: activeInstance.followRedirects,
          store_requests: activeInstance.storeRequests,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setAttacks(prev => ({
          ...prev,
          [data.attack_id]: { status: 'running', completed: 0, total: data.total_requests, errors: 0, results: [] },
        }));
        updateTab(activeInstance.id, { attackId: data.attack_id });
        setActiveSubTab('results');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to start attack');
      }
    } catch (err) {
      alert('Failed to start attack: ' + err.message);
    }
  };

  const controlAttack = async (action) => {
    if (!activeInstance?.attackId) return;
    await proxyFetch(`/intruder/attacks/${activeInstance.attackId}/${action}`, { method: 'POST' });
    if (action === 'pause') setAttacks(prev => ({ ...prev, [activeInstance.attackId]: { ...prev[activeInstance.attackId], status: 'paused' } }));
    if (action === 'resume') setAttacks(prev => ({ ...prev, [activeInstance.attackId]: { ...prev[activeInstance.attackId], status: 'running' } }));
    if (action === 'cancel') setAttacks(prev => ({ ...prev, [activeInstance.attackId]: { ...prev[activeInstance.attackId], status: 'cancelled' } }));
  };

  const loadResults = useCallback(async (attackId, page = 0) => {
    const res = await proxyFetch(`/intruder/attacks/${attackId}/results?sort_by=${resultSort.by}&sort_dir=${resultSort.dir}&limit=100&offset=${page * 100}${resultFilter ? `&search=${encodeURIComponent(resultFilter)}` : ''}`);
    if (res.ok) {
      const data = await res.json();
      return data;
    }
    return { total: 0, items: [] };
  }, [proxyFetch, resultSort, resultFilter]);

  const loadResultDetail = async (attackId, resultId) => {
    const res = await proxyFetch(`/intruder/attacks/${attackId}/results/${resultId}`);
    if (res.ok) setResultDetail(await res.json());
  };

  const calculateTotal = async () => {
    if (!activeInstance) return;
    const numPos = countPositions(activeInstance.templateRequest);
    if (numPos === 0) { setTotalPreview({ total: 0 }); return; }
    // Build positions array for the API
    const positions = [];
    const parts = activeInstance.templateRequest.split(MARKER);
    for (let i = 1; i < parts.length; i += 2) {
      positions.push({ index: (i - 1) / 2, original: parts[i] });
    }
    const res = await proxyFetch('/intruder/calculate-total', {
      method: 'POST',
      body: JSON.stringify({
        attack_type: activeInstance.attackType,
        positions,
        payload_sets: activeInstance.payloadSets.slice(0, numPos),
      }),
    });
    if (res.ok) setTotalPreview(await res.json());
  };

  // Render highlighted template
  const renderTemplate = (tmpl) => {
    if (!tmpl) return null;
    const parts = tmpl.split(MARKER);
    return parts.map((part, i) => {
      if (i % 2 === 1) {
        return <span key={i} style={{ background: '#f59e0b30', color: '#f59e0b', borderRadius: 2, padding: '0 2px' }}>{MARKER}{part}{MARKER}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  // ── Empty state ──
  if (intruderTabs.length === 0) {
    return (
      <div style={S.emptyState}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎯</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#e2e8f0', marginBottom: 8 }}>Intruder</div>
        <div style={{ color: '#8888aa', marginBottom: 16, maxWidth: 400, margin: '0 auto 16px' }}>
          Automated parameterized attacks. Send requests from History (right-click &rarr; "Send to Intruder") or create a new tab.
        </div>
        <button style={S.btn('primary')} onClick={addNewTab}>New Intruder Tab</button>
      </div>
    );
  }

  const currentAttack = activeInstance?.attackId ? attacks[activeInstance.attackId] : null;
  const numPositions = activeInstance ? countPositions(activeInstance.templateRequest) : 0;

  return (
    <div>
      {/* Instance tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 12, borderBottom: '1px solid #2a2a4a', overflowX: 'auto' }}>
        {intruderTabs.map(tab => (
          <div key={tab.id} style={{ display: 'flex', alignItems: 'center', borderBottom: tab.id === activeIntruderId ? '2px solid #f59e0b' : '2px solid transparent', padding: '8px 0' }}>
            <button onClick={() => setActiveIntruderId(tab.id)}
              style={{ background: 'none', border: 'none', color: tab.id === activeIntruderId ? '#f59e0b' : '#8888aa', padding: '4px 12px', cursor: 'pointer', fontSize: 13, fontWeight: tab.id === activeIntruderId ? 600 : 400 }}>
              {tab.name}
            </button>
            <button onClick={() => closeTab(tab.id)}
              style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer', fontSize: 11, padding: '2px 4px', lineHeight: 1 }}>
              &times;
            </button>
          </div>
        ))}
        <button onClick={addNewTab} style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer', fontSize: 18, padding: '4px 12px' }}>+</button>
      </div>

      {activeInstance && (
        <>
          {/* Sub-tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 16 }}>
            {['positions', 'payloads', 'options', 'results'].map(tab => (
              <button key={tab} onClick={() => setActiveSubTab(tab)}
                style={{
                  ...S.btn(activeSubTab === tab ? 'primary' : undefined),
                  borderRadius: 0, textTransform: 'capitalize',
                  borderBottom: activeSubTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                }}>
                {tab}{tab === 'positions' ? ` (${numPositions})` : ''}
              </button>
            ))}
          </div>

          {/* ── POSITIONS SUB-TAB ── */}
          {activeSubTab === 'positions' && (
            <div>
              <div style={S.toolbar}>
                <select style={S.select} value={activeInstance.attackType}
                  onChange={e => updateTab(activeInstance.id, { attackType: e.target.value })}>
                  {ATTACK_TYPES.map(at => <option key={at.value} value={at.value}>{at.label}</option>)}
                </select>
                <button style={S.btn('primary')} onMouseDown={captureSelection} onClick={addMarker}>Add {MARKER}</button>
                <button style={S.btn()} onClick={autoDetectPositions}>Auto</button>
                <button style={S.btn('danger')} onClick={clearMarkers}>Clear</button>
                <span style={S.stat}>{numPositions} position{numPositions !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ fontSize: 12, color: '#8888aa', marginBottom: 12 }}>
                {ATTACK_TYPES.find(a => a.value === activeInstance.attackType)?.desc}
              </div>
              {/* Target */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <select style={{ ...S.select, width: 80 }} value={activeInstance.target.scheme}
                  onChange={e => updateTab(activeInstance.id, { target: { ...activeInstance.target, scheme: e.target.value } })}>
                  <option value="https">https</option>
                  <option value="http">http</option>
                </select>
                <input style={{ ...S.input, flex: 1 }} value={activeInstance.target.host} placeholder="Host"
                  onChange={e => updateTab(activeInstance.id, { target: { ...activeInstance.target, host: e.target.value } })} />
                <input style={{ ...S.input, width: 80 }} type="number" value={activeInstance.target.port}
                  onChange={e => updateTab(activeInstance.id, { target: { ...activeInstance.target, port: parseInt(e.target.value) || 443 } })} />
              </div>
              {/* Template editor with highlighting */}
              <div style={{ position: 'relative' }}>
                <div style={{
                  ...S.textarea, minHeight: 300, position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  pointerEvents: 'none', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  color: 'transparent', background: 'transparent', border: '1px solid transparent',
                }}>
                  {renderTemplate(activeInstance.templateRequest)}
                </div>
                <textarea ref={textareaRef} style={{ ...S.textarea, minHeight: 300, background: '#000000', position: 'relative', caretColor: '#e2e8f0' }}
                  value={activeInstance.templateRequest}
                  onChange={e => updateTab(activeInstance.id, { templateRequest: e.target.value })}
                  spellCheck={false} />
              </div>
            </div>
          )}

          {/* ── PAYLOADS SUB-TAB ── */}
          {activeSubTab === 'payloads' && (
            <div>
              {numPositions === 0 ? (
                <div style={S.emptyState}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>🎯</div>
                  <div>Add payload positions in the Positions tab first</div>
                </div>
              ) : (
                <div>
                  {/* Position selector */}
                  <div style={S.toolbar}>
                    <span style={{ color: '#8888aa', fontSize: 12 }}>Payload set for:</span>
                    {Array.from({ length: numPositions }, (_, i) => i).map(i => {
                      ensurePayloadSets(activeInstance, numPositions);
                      return null;
                    })}
                  </div>
                  {Array.from({ length: numPositions }, (_, posIdx) => {
                    const ps = activeInstance.payloadSets[posIdx] || { type: 'simple_list', items: [] };
                    return (
                      <div key={posIdx} style={{ ...S.card, marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <span style={{ fontWeight: 600, color: '#f59e0b', fontSize: 13 }}>Position {posIdx + 1}</span>
                          <select style={S.select} value={ps.type} onChange={e => {
                            const newSets = [...activeInstance.payloadSets];
                            newSets[posIdx] = { ...newSets[posIdx], type: e.target.value };
                            updateTab(activeInstance.id, { payloadSets: newSets });
                          }}>
                            <option value="simple_list">Simple List</option>
                            <option value="numbered">Numbered</option>
                            <option value="runtime_file">Wordlist</option>
                          </select>
                        </div>

                        {ps.type === 'simple_list' && (
                          <div>
                            <textarea style={{ ...S.textarea, minHeight: 120 }}
                              placeholder="One payload per line..."
                              value={(ps.items || []).join('\n')}
                              onChange={e => {
                                const newSets = [...activeInstance.payloadSets];
                                newSets[posIdx] = { ...ps, items: e.target.value.split('\n').filter(l => l !== '') };
                                updateTab(activeInstance.id, { payloadSets: newSets });
                              }} />
                            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={S.stat}>{(ps.items || []).length} payload{(ps.items || []).length !== 1 ? 's' : ''}</span>
                              <select style={S.select} value="" onChange={e => {
                                if (!e.target.value) return;
                                const presets = PAYLOAD_PRESETS[e.target.value];
                                if (presets) {
                                  const newSets = [...activeInstance.payloadSets];
                                  newSets[posIdx] = { ...ps, items: [...(ps.items || []), ...presets] };
                                  updateTab(activeInstance.id, { payloadSets: newSets });
                                }
                                e.target.value = '';
                              }}>
                                <option value="">+ Add preset list...</option>
                                {Object.keys(PAYLOAD_PRESETS).map(k => (
                                  <option key={k} value={k}>{k} ({PAYLOAD_PRESETS[k].length})</option>
                                ))}
                              </select>
                              <button style={S.btn()} onClick={async () => {
                                try {
                                  const text = await navigator.clipboard.readText();
                                  const newSets = [...activeInstance.payloadSets];
                                  const existing = ps.items || [];
                                  newSets[posIdx] = { ...ps, items: [...existing, ...text.split('\n').filter(l => l.trim())] };
                                  updateTab(activeInstance.id, { payloadSets: newSets });
                                } catch {}
                              }}>Paste from Clipboard</button>
                            </div>
                          </div>
                        )}

                        {ps.type === 'numbered' && (
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <label style={{ color: '#8888aa', fontSize: 12 }}>From
                              <input style={{ ...S.input, width: 80, marginLeft: 4 }} type="number" value={ps.from || 0}
                                onChange={e => {
                                  const newSets = [...activeInstance.payloadSets];
                                  newSets[posIdx] = { ...ps, from: parseInt(e.target.value) || 0 };
                                  updateTab(activeInstance.id, { payloadSets: newSets });
                                }} />
                            </label>
                            <label style={{ color: '#8888aa', fontSize: 12 }}>To
                              <input style={{ ...S.input, width: 80, marginLeft: 4 }} type="number" value={ps.to || 100}
                                onChange={e => {
                                  const newSets = [...activeInstance.payloadSets];
                                  newSets[posIdx] = { ...ps, to: parseInt(e.target.value) || 100 };
                                  updateTab(activeInstance.id, { payloadSets: newSets });
                                }} />
                            </label>
                            <label style={{ color: '#8888aa', fontSize: 12 }}>Step
                              <input style={{ ...S.input, width: 60, marginLeft: 4 }} type="number" value={ps.step || 1}
                                onChange={e => {
                                  const newSets = [...activeInstance.payloadSets];
                                  newSets[posIdx] = { ...ps, step: parseInt(e.target.value) || 1 };
                                  updateTab(activeInstance.id, { payloadSets: newSets });
                                }} />
                            </label>
                            <span style={S.stat}>
                              {Math.max(0, Math.floor(((ps.to || 100) - (ps.from || 0)) / Math.max(1, ps.step || 1)) + 1)} payloads
                            </span>
                          </div>
                        )}

                        {ps.type === 'runtime_file' && (
                          <div>
                            <select style={{ ...S.select, width: '100%' }} value={ps.file || ''}
                              onChange={e => {
                                const newSets = [...activeInstance.payloadSets];
                                newSets[posIdx] = { ...ps, file: e.target.value };
                                updateTab(activeInstance.id, { payloadSets: newSets });
                              }}>
                              <option value="">-- Select wordlist --</option>
                              {wordlists.map(w => (
                                <option key={w.name} value={w.name}>{w.name} ({w.count} items)</option>
                              ))}
                            </select>
                            {ps.file && wordlists.find(w => w.name === ps.file) && (
                              <div style={{ marginTop: 8, fontSize: 12, color: '#8888aa' }}>
                                Preview: {wordlists.find(w => w.name === ps.file).preview.join(', ')}...
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── OPTIONS SUB-TAB ── */}
          {activeSubTab === 'options' && (
            <div>
              <div style={S.card}>
                <div style={S.sectionTitle}>Request Engine</div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                  <label style={{ color: '#8888aa', fontSize: 12 }}>Concurrency
                    <input style={{ ...S.input, width: 80, marginLeft: 4 }} type="number" min="1" max="100"
                      value={activeInstance.concurrency}
                      onChange={e => updateTab(activeInstance.id, { concurrency: Math.min(100, Math.max(1, parseInt(e.target.value) || 10)) })} />
                  </label>
                  <label style={{ color: '#8888aa', fontSize: 12 }}>Delay (ms)
                    <input style={{ ...S.input, width: 80, marginLeft: 4 }} type="number" min="0" max="5000"
                      value={activeInstance.delayMs}
                      onChange={e => updateTab(activeInstance.id, { delayMs: Math.max(0, parseInt(e.target.value) || 0) })} />
                  </label>
                  <label style={{ color: '#8888aa', fontSize: 12 }}>Timeout (ms)
                    <input style={{ ...S.input, width: 100, marginLeft: 4 }} type="number" min="1000" max="120000"
                      value={activeInstance.timeoutMs}
                      onChange={e => updateTab(activeInstance.id, { timeoutMs: Math.max(1000, parseInt(e.target.value) || 30000) })} />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <label style={{ color: '#e2e8f0', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={activeInstance.followRedirects}
                      onChange={e => updateTab(activeInstance.id, { followRedirects: e.target.checked })} />
                    Follow Redirects
                  </label>
                  <label style={{ color: '#e2e8f0', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={activeInstance.storeRequests}
                      onChange={e => updateTab(activeInstance.id, { storeRequests: e.target.checked })} />
                    Store Full Requests
                  </label>
                </div>
              </div>

              <div style={S.card}>
                <div style={S.sectionTitle}>Payload Processing</div>
                {(activeInstance.processingRules || []).map((rule, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: '#8888aa', fontSize: 11, width: 20 }}>{i + 1}.</span>
                    <span style={{ color: '#e2e8f0', fontSize: 13, flex: 1 }}>
                      {PROCESSING_TYPES.find(p => p.value === rule.type)?.label || rule.type}
                      {(rule.type === 'add_prefix' || rule.type === 'add_suffix') && (
                        <input style={{ ...S.input, width: 120, marginLeft: 8 }} value={rule.value || ''} placeholder="Value..."
                          onChange={e => {
                            const newRules = [...activeInstance.processingRules];
                            newRules[i] = { ...rule, value: e.target.value };
                            updateTab(activeInstance.id, { processingRules: newRules });
                          }} />
                      )}
                    </span>
                    <button style={{ ...S.btn('danger'), padding: '2px 8px', fontSize: 11 }}
                      onClick={() => {
                        const newRules = activeInstance.processingRules.filter((_, j) => j !== i);
                        updateTab(activeInstance.id, { processingRules: newRules });
                      }}>&times;</button>
                  </div>
                ))}
                <select style={S.select} value="" onChange={e => {
                  if (e.target.value) {
                    updateTab(activeInstance.id, {
                      processingRules: [...(activeInstance.processingRules || []), { type: e.target.value, value: '' }],
                    });
                    e.target.value = '';
                  }
                }}>
                  <option value="">+ Add Processing Rule</option>
                  {PROCESSING_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* ── RESULTS SUB-TAB ── */}
          {activeSubTab === 'results' && (
            <div>
              {/* Controls */}
              <div style={S.toolbar}>
                {!currentAttack || currentAttack.status === 'complete' || currentAttack.status === 'cancelled' || currentAttack.status === 'failed' ? (
                  <>
                    <button style={S.btn('primary')} onClick={startAttack} disabled={numPositions === 0}>
                      Start Attack
                    </button>
                    <button style={S.btn()} onClick={calculateTotal}>Preview Total</button>
                    {totalPreview && (
                      <span style={S.stat}>
                        {totalPreview.total?.toLocaleString()} requests
                        {totalPreview.exceeds_limit && <span style={{ color: '#ef4444', marginLeft: 4 }}>Exceeds limit!</span>}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {currentAttack.status === 'running' && (
                      <button style={S.btn()} onClick={() => controlAttack('pause')}>Pause</button>
                    )}
                    {currentAttack.status === 'paused' && (
                      <button style={S.btn('primary')} onClick={() => controlAttack('resume')}>Resume</button>
                    )}
                    <button style={S.btn('danger')} onClick={() => controlAttack('cancel')}>Cancel</button>
                  </>
                )}
                {currentAttack && (
                  <span style={S.stat}>
                    {currentAttack.status === 'running' ? '🟢' : currentAttack.status === 'paused' ? '⏸️' : currentAttack.status === 'complete' ? '✅' : currentAttack.status === 'cancelled' ? '🚫' : '❌'}
                    {' '}{currentAttack.status}
                    {currentAttack.errors > 0 && <span style={{ color: '#ef4444', marginLeft: 4 }}>{currentAttack.errors} errors</span>}
                  </span>
                )}
              </div>

              {/* Progress bar */}
              {currentAttack && currentAttack.total > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#8888aa', marginBottom: 4 }}>
                    <span>{currentAttack.completed?.toLocaleString()} / {currentAttack.total?.toLocaleString()}</span>
                    <span>{((currentAttack.completed / currentAttack.total) * 100).toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: '#2a2a4a', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 3, transition: 'width 0.3s',
                      width: `${Math.min(100, (currentAttack.completed / currentAttack.total) * 100)}%`,
                      background: currentAttack.status === 'complete' ? '#22c55e' : currentAttack.status === 'cancelled' ? '#ef4444' : '#3b82f6',
                    }} />
                  </div>
                </div>
              )}

              {/* Filter */}
              {currentAttack && currentAttack.results?.length > 0 && (
                <div style={{ ...S.toolbar, marginBottom: 8 }}>
                  <input style={S.input} placeholder="Filter payloads..." value={resultFilter} onChange={e => setResultFilter(e.target.value)} />
                  <select style={S.select} value={resultSort.by} onChange={e => setResultSort(prev => ({ ...prev, by: e.target.value }))}>
                    <option value="request_index">#</option>
                    <option value="response_status">Status</option>
                    <option value="response_length">Length</option>
                    <option value="duration_ms">Duration</option>
                  </select>
                  <button style={S.btn()} onClick={() => setResultSort(prev => ({ ...prev, dir: prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                    {resultSort.dir === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              )}

              {/* Results table */}
              {currentAttack && currentAttack.results?.length > 0 ? (
                <div style={{ maxHeight: 500, overflow: 'auto', border: '1px solid #2a2a4a', borderRadius: 8 }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>#</th>
                        <th style={S.th}>Payload(s)</th>
                        <th style={S.th}>Status</th>
                        <th style={S.th}>Length</th>
                        <th style={S.th}>Duration</th>
                        <th style={S.th}>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentAttack.results
                        .filter(r => !resultFilter || JSON.stringify(r.payload_values).toLowerCase().includes(resultFilter.toLowerCase()))
                        .sort((a, b) => {
                          const key = resultSort.by;
                          const aVal = a[key] ?? 0;
                          const bVal = b[key] ?? 0;
                          return resultSort.dir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
                        })
                        .map(r => (
                          <tr key={r.id || r.request_index} style={S.row(selectedResult === r.id)}
                            onClick={() => {
                              setSelectedResult(r.id);
                              if (r.id && activeInstance?.attackId) loadResultDetail(activeInstance.attackId, r.id);
                            }}>
                            <td style={S.td}>{r.request_index}</td>
                            <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {Object.values(r.payload_values || {}).join(' | ')}
                            </td>
                            <td style={S.td}><StatusBadge status={r.response_status} /></td>
                            <td style={S.td}>{r.response_length != null ? r.response_length.toLocaleString() : '-'}</td>
                            <td style={S.td}>{r.duration_ms != null ? `${r.duration_ms}ms` : '-'}</td>
                            <td style={{ ...S.td, color: r.error ? '#ef4444' : '#8888aa', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.error || '-'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : currentAttack ? (
                <div style={S.emptyState}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                  <div>Waiting for results...</div>
                </div>
              ) : (
                <div style={S.emptyState}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>🎯</div>
                  <div>Configure positions, payloads, and options, then click "Start Attack"</div>
                </div>
              )}

              {/* Result detail panel */}
              {resultDetail && (
                <div style={{ ...S.card, marginTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 14 }}>
                      Request #{resultDetail.request_index} — {resultDetail.method} {resultDetail.path}
                    </span>
                    <button style={S.btn()} onClick={() => { setResultDetail(null); setSelectedResult(null); }}>&times;</button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <StatusBadge status={resultDetail.response_status} />
                    <span style={S.stat}>{resultDetail.duration_ms}ms</span>
                    <span style={S.stat}>{resultDetail.response_length?.toLocaleString()} bytes</span>
                    {resultDetail.error && <span style={{ ...S.stat, color: '#ef4444' }}>{resultDetail.error}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#8888aa', marginBottom: 12 }}>
                    Payloads: {Object.entries(resultDetail.payload_values || {}).map(([k, v]) => (
                      <span key={k} style={S.tag('#f59e0b')}>#{parseInt(k) + 1}: {v}</span>
                    ))}
                  </div>
                  {resultDetail.request_body && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={S.sectionTitle}>Request Body</div>
                      <pre style={{ ...S.textarea, minHeight: 60, maxHeight: 150, overflow: 'auto' }}>{resultDetail.request_body}</pre>
                    </div>
                  )}
                  {resultDetail.response_body && (
                    <div>
                      <div style={S.sectionTitle}>Response Body</div>
                      <pre style={{ ...S.textarea, minHeight: 80, maxHeight: 300, overflow: 'auto' }}>{resultDetail.response_body}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// AI ANALYSIS PANEL — inline finding/request analysis
// ═══════════════════════════════════════════════════════════

function AIAnalysisPanel({ findingData, engagementId, onClose }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toolActivity, setToolActivity] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!findingData) return;
    const controller = new AbortController();
    abortRef.current = controller;
    const token = sessionStorage.getItem('token');

    // Send finding data as a "request" for analysis
    const requestData = {
      method: 'FINDING',
      host: findingData.affected_url || findingData.url || 'unknown',
      path: '/',
      headers: {},
      body: `Title: ${findingData.title || ''}\nSeverity: ${findingData.severity || ''}\nDescription: ${findingData.description || ''}\nEvidence: ${findingData.evidence || ''}\nRecommendation: ${findingData.recommendation || ''}`,
    };

    consumeSSEStream(
      `${ORCH_API}/ai/analyze-request`,
      { request_data: requestData, response_data: null, engagement_id: engagementId || null },
      token,
      {
        onToken: (t) => { setText(prev => prev + t); setToolActivity(null); },
        onToolCall: (d) => setToolActivity(`Querying ${d.tool}...`),
        onToolResult: () => setToolActivity(null),
        onDone: () => setLoading(false),
        onError: (msg) => { setError(msg); setLoading(false); },
      },
      controller.signal,
    );

    return () => controller.abort();
  }, [findingData, engagementId]);

  return (
    <div style={{
      background: '#0d1117', border: '1px solid #3b82f680', borderRadius: 8,
      padding: 16, marginTop: 8, position: 'relative',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#3b82f6' }}>AI Analysis</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer', fontSize: 16 }}>&times;</button>
      </div>
      {toolActivity && (
        <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>&#x1f50d;</span> {toolActivity}
        </div>
      )}
      {error ? (
        <div style={{ color: '#ef4444', fontSize: 12 }}>{error}</div>
      ) : (
        <div style={{
          fontSize: 12, color: '#e2e8f0', lineHeight: 1.7, whiteSpace: 'pre-wrap',
          fontFamily: 'system-ui, sans-serif', maxHeight: 400, overflowY: 'auto',
        }}>
          {text || (loading ? 'Analyzing...' : 'No response')}
          {loading && text && <span style={{ animation: 'blink 1s infinite', color: '#3b82f6' }}>|</span>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// AI REQUEST ANALYSIS MODAL — for History tab
// ═══════════════════════════════════════════════════════════

function AIRequestAnalysisModal({ requestDetail, engagementId, onClose }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toolActivity, setToolActivity] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!requestDetail) return;
    const controller = new AbortController();
    abortRef.current = controller;
    const token = sessionStorage.getItem('token');

    const d = requestDetail;
    const requestData = {
      method: d.method,
      host: d.host,
      path: d.path || '/',
      query_string: d.query_string || '',
      is_tls: d.is_tls,
      headers: {},
      body: d.request_body || '',
    };
    // Parse headers from raw
    if (d.request_headers) {
      try {
        const parsed = typeof d.request_headers === 'string' ? JSON.parse(d.request_headers) : d.request_headers;
        if (Array.isArray(parsed)) parsed.forEach(h => { requestData.headers[h.name || h[0]] = h.value || h[1]; });
        else if (typeof parsed === 'object') Object.assign(requestData.headers, parsed);
      } catch {}
    }

    const responseData = d.response_status ? {
      status_code: d.response_status,
      headers: {},
      body: (d.response_body || '').slice(0, 50000),
    } : null;
    if (responseData && d.response_headers) {
      try {
        const parsed = typeof d.response_headers === 'string' ? JSON.parse(d.response_headers) : d.response_headers;
        if (Array.isArray(parsed)) parsed.forEach(h => { responseData.headers[h.name || h[0]] = h.value || h[1]; });
        else if (typeof parsed === 'object') Object.assign(responseData.headers, parsed);
      } catch {}
    }

    consumeSSEStream(
      `${ORCH_API}/ai/analyze-request`,
      { request_data: requestData, response_data: responseData, engagement_id: engagementId || null },
      token,
      {
        onToken: (t) => setText(prev => prev + t),
        onDone: () => setLoading(false),
        onError: (msg) => { setError(msg); setLoading(false); },
      },
      controller.signal,
    );

    return () => controller.abort();
  }, [requestDetail, engagementId]);

  const d = requestDetail || {};
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#1a1a2e', border: '1px solid #3b82f680', borderRadius: 12,
        width: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid #2a2a4a',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>&#x1f916;</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>AI Request Analysis</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}>&times;</button>
        </div>

        {/* Request summary */}
        <div style={{ padding: '8px 16px', background: '#0d1117', borderBottom: '1px solid #2a2a4a', fontSize: 12 }}>
          <span style={{ color: METHOD_COLORS[d.method] || '#6b7280', fontWeight: 700, marginRight: 8 }}>{d.method}</span>
          <span style={{ color: '#8888aa' }}>{d.scheme || 'https'}://{d.host}{d.path}</span>
          {d.response_status && (
            <span style={{ marginLeft: 12, color: STATUS_COLORS[Math.floor(d.response_status / 100)] || '#6b7280', fontWeight: 600 }}>{d.response_status}</span>
          )}
        </div>

        {/* Analysis content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {error ? (
            <div style={{ color: '#ef4444', fontSize: 13 }}>{error}</div>
          ) : (
            <div style={{
              fontSize: 13, color: '#e2e8f0', lineHeight: 1.7, whiteSpace: 'pre-wrap',
              fontFamily: 'system-ui, sans-serif',
            }}>
              {text || (loading ? 'Analyzing request for security issues...' : 'No response')}
              {loading && text && <span style={{ animation: 'blink 1s infinite', color: '#3b82f6' }}>|</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// AI COPILOT SIDEBAR — persistent chat assistant
// ═══════════════════════════════════════════════════════════

const AI_STORAGE_KEY = 'ai_copilot_state';

function _loadAIState() {
  try {
    const raw = sessionStorage.getItem(AI_STORAGE_KEY);
    if (!raw) return {};
    const s = JSON.parse(raw);
    // Restore Date objects in activity log
    if (Array.isArray(s.activityLog)) {
      s.activityLog = s.activityLog.map(e => ({ ...e, ts: new Date(e.ts) }));
    }
    return s;
  } catch { return {}; }
}

function _saveAIState(messages, activityLog, contextSent, showLog) {
  try {
    sessionStorage.setItem(AI_STORAGE_KEY, JSON.stringify({
      messages, activityLog, contextSent, showLog,
    }));
  } catch {}
}

// ═══════════════════════ AI Logs Tab ═══════════════════════

function AILogsTab({ activeSessionId, engagementId }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // all, tool_call, tool_result, chat_request, chat_response, error

  const ORCH_API = (window.__ENV__?.REACT_APP_ORCHESTRATOR_URL || process.env.REACT_APP_ORCHESTRATOR_URL || 'http://localhost:8100') + '/api';
  const token = sessionStorage.getItem('token');

  const loadLogs = useCallback(async () => {
    if (!activeSessionId && !engagementId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeSessionId) params.append('session_id', activeSessionId);
      else if (engagementId) params.append('engagement_id', engagementId);
      params.append('limit', '500');
      const resp = await fetch(`${ORCH_API}/ai/activity?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setEntries(data.entries || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error('Failed to load AI logs:', e); }
    setLoading(false);
  }, [activeSessionId, engagementId, token, ORCH_API]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const deleteAll = async () => {
    if (!window.confirm('Delete all AI activity logs for this session?')) return;
    const params = new URLSearchParams();
    if (activeSessionId) params.append('session_id', activeSessionId);
    else if (engagementId) params.append('engagement_id', engagementId);
    await fetch(`${ORCH_API}/ai/activity?${params}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    loadLogs();
  };

  const deleteEntry = async (id) => {
    await fetch(`${ORCH_API}/ai/activity/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    loadLogs();
  };

  const filtered = filter === 'all' ? entries : entries.filter(e => e.event_type === filter);

  const typeColors = {
    chat_request: '#3b82f6',
    chat_response: '#22c55e',
    tool_call: '#f59e0b',
    tool_result: '#a855f7',
    error: '#ef4444',
  };
  const typeIcons = {
    chat_request: '💬',
    chat_response: '🤖',
    tool_call: '⚡',
    tool_result: '✓',
    error: '✗',
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{ padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid #2a2a4a', flexShrink: 0 }}>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ padding: '4px 8px', background: '#16213e', color: '#e2e8f0', border: '1px solid #2a2a4a', borderRadius: 6, fontSize: 11 }}
        >
          <option value="all">All Events ({entries.length})</option>
          <option value="chat_request">Chat Requests ({entries.filter(e => e.event_type === 'chat_request').length})</option>
          <option value="chat_response">Chat Responses ({entries.filter(e => e.event_type === 'chat_response').length})</option>
          <option value="tool_call">Tool Calls ({entries.filter(e => e.event_type === 'tool_call').length})</option>
          <option value="tool_result">Tool Results ({entries.filter(e => e.event_type === 'tool_result').length})</option>
          <option value="error">Errors ({entries.filter(e => e.event_type === 'error').length})</option>
        </select>
        <button style={S.btn()} onClick={loadLogs} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: '#8888aa' }}>{total} total entries</span>
        {entries.length > 0 && (
          <button style={S.btn('danger')} onClick={deleteAll}>Delete All</button>
        )}
      </div>

      {/* Log entries */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#8888aa', fontSize: 12 }}>
            {loading ? 'Loading AI activity logs...' : 'No AI activity logged yet. Use the AI Copilot to generate activity.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a4a', position: 'sticky', top: 0, background: '#0d1117', zIndex: 1 }}>
                <th style={{ ...S.th, width: 130 }}>Time</th>
                <th style={{ ...S.th, width: 100 }}>Type</th>
                <th style={{ ...S.th, width: 120 }}>Tool</th>
                <th style={S.th}>Content</th>
                <th style={{ ...S.th, width: 70 }}>Duration</th>
                <th style={{ ...S.th, width: 60 }}>Tokens</th>
                <th style={{ ...S.th, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(entry => {
                const isExpanded = expandedId === entry.id;
                const time = new Date(entry.created_at).toLocaleTimeString('en-US', { hour12: false });
                const color = typeColors[entry.event_type] || '#8888aa';
                const icon = typeIcons[entry.event_type] || '·';

                // Build preview text
                let preview = '';
                if (entry.message_content) preview = entry.message_content;
                else if (entry.tool_name && entry.event_type === 'tool_call') {
                  preview = `${entry.tool_name}(${JSON.stringify(entry.tool_input || {}).slice(0, 100)})`;
                } else if (entry.tool_output) preview = entry.tool_output;

                return (
                  <React.Fragment key={entry.id}>
                    <tr
                      style={{
                        borderBottom: '1px solid #1a1a2a', cursor: 'pointer',
                        background: isExpanded ? '#16213e' : 'transparent',
                      }}
                      onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    >
                      <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 10, color: '#8888aa' }}>{time}</td>
                      <td style={{ ...S.td }}>
                        <span style={{
                          padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                          background: `${color}20`, color,
                        }}>
                          {icon} {entry.event_type.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ ...S.td, color: '#f59e0b', fontFamily: 'monospace', fontSize: 10 }}>
                        {entry.tool_name || '-'}
                      </td>
                      <td style={{ ...S.td, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#ccc' }}>
                        {preview.slice(0, 150)}{preview.length > 150 ? '...' : ''}
                      </td>
                      <td style={{ ...S.td, fontSize: 10, color: '#8888aa', textAlign: 'right' }}>
                        {entry.duration_ms ? `${entry.duration_ms}ms` : '-'}
                      </td>
                      <td style={{ ...S.td, fontSize: 10, color: '#8888aa', textAlign: 'right' }}>
                        {entry.tokens_used || '-'}
                      </td>
                      <td style={S.td}>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteEntry(entry.id); }}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}
                          title="Delete entry"
                        >&times;</button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, background: '#0a0a14' }}>
                          <div style={{ padding: '8px 16px', fontSize: 11 }}>
                            {entry.message_content && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ color: '#8888aa', fontSize: 9, marginBottom: 4, textTransform: 'uppercase' }}>
                                  {entry.message_role || 'Message'} Content
                                </div>
                                <pre style={{
                                  background: '#16213e', padding: '8px 12px', borderRadius: 6,
                                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#e2e8f0',
                                  maxHeight: 300, overflowY: 'auto', margin: 0, fontSize: 11,
                                }}>{entry.message_content}</pre>
                              </div>
                            )}
                            {entry.tool_input && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ color: '#8888aa', fontSize: 9, marginBottom: 4, textTransform: 'uppercase' }}>
                                  Tool Input
                                </div>
                                <pre style={{
                                  background: '#16213e', padding: '8px 12px', borderRadius: 6,
                                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#f59e0b',
                                  maxHeight: 200, overflowY: 'auto', margin: 0, fontSize: 11,
                                }}>{JSON.stringify(entry.tool_input, null, 2)}</pre>
                              </div>
                            )}
                            {entry.tool_output && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ color: '#8888aa', fontSize: 9, marginBottom: 4, textTransform: 'uppercase' }}>
                                  Tool Output
                                </div>
                                <pre style={{
                                  background: '#16213e', padding: '8px 12px', borderRadius: 6,
                                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#22c55e',
                                  maxHeight: 300, overflowY: 'auto', margin: 0, fontSize: 11,
                                }}>{entry.tool_output}</pre>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 16, fontSize: 10, color: '#8888aa' }}>
                              {entry.tokens_used > 0 && <span>Tokens: {entry.tokens_used}</span>}
                              {entry.duration_ms && <span>Duration: {entry.duration_ms}ms</span>}
                              {entry.session_id && <span>Session: {entry.session_id.slice(0, 8)}...</span>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ─── Auto Pentest Tab ───
const AUTOPENTEST_STATUS_COLORS = {
  pending: '#f59e0b', approved: '#3b82f6', testing: '#f97316', violated: '#ef4444',
  held: '#22c55e', error: '#dc2626', not_violated: '#6b7280', completed: '#22c55e',
  running: '#3b82f6', paused: '#f59e0b', failed: '#ef4444', cancelled: '#6b7280',
};
const AUTOPENTEST_CATEGORY_COLORS = {
  authentication: '#ef4444', authorization: '#f97316', input_validation: '#3b82f6',
  csrf: '#a855f7', business_logic: '#6b7280', session_management: '#8b5cf6',
  information_disclosure: '#f59e0b', injection: '#dc2626',
};
const AUTOPENTEST_PHASE_COLORS = {
  discovery: '#3b82f6', model: '#a855f7', hypothesize: '#f97316',
  test: '#ef4444', report: '#22c55e',
};
const PRIORITY_COLORS = [
  '#dc2626','#ef4444','#f97316','#f59e0b','#eab308',
  '#84cc16','#22c55e','#14b8a6','#6b7280','#9ca3af',
];

function AutoPentestTab({ activeSessionId, engagementId }) {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [invariants, setInvariants] = useState([]);
  const [testRuns, setTestRuns] = useState([]);
  const [forcedBrowsing, setForcedBrowsing] = useState([]);
  const [appModel, setAppModel] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [subTab, setSubTab] = useState('invariants');
  const [mode, setMode] = useState('suggestion');
  const [forcedBrowsingEnabled, setForcedBrowsingEnabled] = useState(true);
  const [expandedInvariant, setExpandedInvariant] = useState(null);
  const [expandedViolation, setExpandedViolation] = useState(null);
  const logEndRef = useRef(null);

  const ORCH_API_BASE = (window.__ENV__?.REACT_APP_ORCHESTRATOR_URL || process.env.REACT_APP_ORCHESTRATOR_URL || 'http://localhost:8100') + '/api';
  const token = sessionStorage.getItem('token');
  const authHeaders = useMemo(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  // Load sessions
  const loadSessions = useCallback(async () => {
    if (!engagementId) return;
    try {
      const resp = await fetch(`${ORCH_API_BASE}/autopentest/sessions?engagement_id=${engagementId}`, { headers: authHeaders });
      if (resp.ok) {
        const data = await resp.json();
        setSessions(data.sessions || data || []);
        // Auto-select latest running or most recent session
        const list = data.sessions || data || [];
        const running = list.find(s => s.status === 'running' || s.status === 'paused');
        if (running) setActiveSession(running);
        else if (list.length > 0 && !activeSession) setActiveSession(list[0]);
      }
    } catch (e) { console.error('Failed to load autopentest sessions:', e); }
  }, [engagementId, authHeaders, ORCH_API_BASE]);

  // Load session detail data
  const loadSessionData = useCallback(async () => {
    if (!activeSession?.id) return;
    setLoading(true);
    try {
      const [invResp, trResp, fbResp, modelResp, logResp] = await Promise.all([
        fetch(`${ORCH_API_BASE}/autopentest/sessions/${activeSession.id}/invariants`, { headers: authHeaders }),
        fetch(`${ORCH_API_BASE}/autopentest/sessions/${activeSession.id}/test-runs`, { headers: authHeaders }),
        fetch(`${ORCH_API_BASE}/autopentest/sessions/${activeSession.id}/forced-browsing`, { headers: authHeaders }),
        fetch(`${ORCH_API_BASE}/autopentest/sessions/${activeSession.id}/model`, { headers: authHeaders }),
        fetch(`${ORCH_API_BASE}/autopentest/sessions/${activeSession.id}/log`, { headers: authHeaders }),
      ]);
      if (invResp.ok) { const d = await invResp.json(); setInvariants(d.invariants || d || []); }
      if (trResp.ok) { const d = await trResp.json(); setTestRuns(d.test_runs || d || []); }
      if (fbResp.ok) { const d = await fbResp.json(); setForcedBrowsing(d.results || d || []); }
      if (modelResp.ok) { const d = await modelResp.json(); setAppModel(d.model || d || []); }
      if (logResp.ok) { const d = await logResp.json(); setLog(d.entries || d || []); }
    } catch (e) { console.error('Failed to load session data:', e); }
    setLoading(false);
  }, [activeSession?.id, authHeaders, ORCH_API_BASE]);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { if (activeSession?.id) loadSessionData(); }, [activeSession?.id, loadSessionData]);

  // SSE stream for real-time updates
  useEffect(() => {
    if (!activeSession || activeSession.status === 'completed' || activeSession.status === 'failed' || activeSession.status === 'cancelled') return;
    const evtSource = new EventSource(`${ORCH_API_BASE}/autopentest/sessions/${activeSession.id}/stream?token=${token}`);
    evtSource.addEventListener('status', (e) => {
      try {
        const data = JSON.parse(e.data);
        setActiveSession(prev => prev ? { ...prev, ...data } : prev);
      } catch {}
    });
    evtSource.addEventListener('log', (e) => {
      try {
        const entry = JSON.parse(e.data);
        setLog(prev => [...prev, entry]);
      } catch {}
    });
    evtSource.addEventListener('invariant', (e) => {
      try {
        const inv = JSON.parse(e.data);
        setInvariants(prev => {
          const idx = prev.findIndex(i => i.id === inv.id);
          if (idx >= 0) { const next = [...prev]; next[idx] = inv; return next; }
          return [...prev, inv];
        });
      } catch {}
    });
    evtSource.addEventListener('test_run', (e) => {
      try {
        const tr = JSON.parse(e.data);
        setTestRuns(prev => [...prev, tr]);
      } catch {}
    });
    evtSource.addEventListener('forced_browsing', (e) => {
      try {
        const fb = JSON.parse(e.data);
        setForcedBrowsing(prev => [...prev, fb]);
      } catch {}
    });
    evtSource.addEventListener('done', () => {
      evtSource.close();
      loadSessions();
      loadSessionData();
    });
    evtSource.onerror = () => { evtSource.close(); };
    return () => evtSource.close();
  }, [activeSession?.id, activeSession?.status, token, ORCH_API_BASE, loadSessions, loadSessionData]);

  // Actions
  const startSession = async () => {
    setStarting(true);
    try {
      const resp = await fetch(`${ORCH_API_BASE}/autopentest/sessions`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ engagement_id: engagementId, mode, forced_browsing: forcedBrowsingEnabled }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setActiveSession(data.session || data);
        loadSessions();
      }
    } catch (e) { console.error('Failed to start autopentest:', e); }
    setStarting(false);
  };

  const sessionAction = async (action) => {
    if (!activeSession?.id) return;
    try {
      await fetch(`${ORCH_API_BASE}/autopentest/sessions/${activeSession.id}/${action}`, {
        method: 'POST', headers: authHeaders,
      });
      loadSessions();
    } catch (e) { console.error(`Failed to ${action} session:`, e); }
  };

  const approveInvariant = async (id) => {
    try {
      await fetch(`${ORCH_API_BASE}/autopentest/invariants/${id}/approve`, { method: 'POST', headers: authHeaders });
      setInvariants(prev => prev.map(i => i.id === id ? { ...i, status: 'approved' } : i));
    } catch (e) { console.error('Failed to approve invariant:', e); }
  };

  const rejectInvariant = async (id) => {
    try {
      await fetch(`${ORCH_API_BASE}/autopentest/invariants/${id}/reject`, { method: 'POST', headers: authHeaders });
      setInvariants(prev => prev.filter(i => i.id !== id));
    } catch (e) { console.error('Failed to reject invariant:', e); }
  };

  const approveAll = async () => {
    const pending = invariants.filter(i => i.status === 'pending');
    await Promise.all(pending.map(i => approveInvariant(i.id)));
  };

  const rejectAll = async () => {
    const pending = invariants.filter(i => i.status === 'pending');
    await Promise.all(pending.map(i => rejectInvariant(i.id)));
  };

  // Computed
  const violations = testRuns.filter(tr => tr.verdict === 'violated');
  const stats = {
    total: invariants.length,
    violated: invariants.filter(i => i.status === 'violated').length,
    pending: invariants.filter(i => i.status === 'pending').length,
    findings: violations.length,
  };
  const isRunning = activeSession && (activeSession.status === 'running' || activeSession.status === 'paused');

  const SUB_TABS = [
    { key: 'invariants', label: 'Invariants', count: invariants.length },
    { key: 'violations', label: 'Violations', count: violations.length },
    { key: 'forced_browsing', label: 'Forced Browsing', count: forcedBrowsing.length },
    { key: 'model', label: 'App Model', count: appModel.length },
    { key: 'log', label: 'Activity Log', count: log.length },
  ];

  // Sort forced browsing: accessible first
  const sortedFB = useMemo(() => {
    const order = { accessible: 0, partial: 1, redirect: 2, blocked: 3 };
    return [...forcedBrowsing].sort((a, b) => (order[a.verdict] ?? 9) - (order[b.verdict] ?? 9));
  }, [forcedBrowsing]);

  // Group app model by type
  const modelGroups = useMemo(() => {
    const groups = {};
    (appModel || []).forEach(item => {
      const type = item.model_type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(item);
    });
    return groups;
  }, [appModel]);

  const fbVerdictColors = { accessible: '#ef4444', blocked: '#22c55e', partial: '#f59e0b', redirect: '#3b82f6' };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Top control bar */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #2a2a4a', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
        {!activeSession || activeSession.status === 'completed' || activeSession.status === 'failed' || activeSession.status === 'cancelled' ? (
          <>
            <button style={S.btn('primary')} onClick={startSession} disabled={starting || !engagementId}>
              {starting ? 'Starting...' : 'Start Auto Pentest'}
            </button>
            <select style={S.select} value={mode} onChange={e => setMode(e.target.value)}>
              <option value="suggestion">Suggestion Mode</option>
              <option value="autonomous">Autonomous Mode</option>
            </select>
            <label style={{ fontSize: 12, color: '#8888aa', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={forcedBrowsingEnabled} onChange={e => setForcedBrowsingEnabled(e.target.checked)} />
              Forced Browsing
            </label>
            {sessions.length > 0 && (
              <select style={S.select} value={activeSession?.id || ''} onChange={e => {
                const s = sessions.find(s => s.id === e.target.value);
                if (s) setActiveSession(s);
              }}>
                <option value="">-- Previous Sessions --</option>
                {sessions.map(s => (
                  <option key={s.id} value={s.id}>{new Date(s.created_at).toLocaleString()} - {s.status}</option>
                ))}
              </select>
            )}
          </>
        ) : (
          <>
            <span style={{
              ...S.tag(AUTOPENTEST_STATUS_COLORS[activeSession.status] || '#6b7280'),
              fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
            }}>
              {activeSession.status === 'running' && '● '}{activeSession.status}
            </span>
            <span style={S.stat}>Invariants: {stats.total}</span>
            <span style={S.stat}>Violated: <span style={{ color: '#ef4444', fontWeight: 600 }}>{stats.violated}</span></span>
            <span style={S.stat}>Findings: <span style={{ color: '#f59e0b', fontWeight: 600 }}>{stats.findings}</span></span>
            {stats.pending > 0 && <span style={S.stat}>Pending: <span style={{ color: '#f59e0b', fontWeight: 600 }}>{stats.pending}</span></span>}
            <span style={{ flex: 1 }} />
            {activeSession.status === 'running' && (
              <button style={S.btn()} onClick={() => sessionAction('pause')}>Pause</button>
            )}
            {activeSession.status === 'paused' && (
              <button style={S.btn('primary')} onClick={() => sessionAction('resume')}>Resume</button>
            )}
            {isRunning && (
              <button style={S.btn('danger')} onClick={() => sessionAction('stop')}>Stop</button>
            )}
          </>
        )}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #2a2a4a', background: '#16213e', flexShrink: 0 }}>
        {SUB_TABS.map(t => (
          <button key={t.key} style={S.tab(subTab === t.key)} onClick={() => setSubTab(t.key)}>
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 0 }}>
        {loading && !activeSession ? (
          <div style={S.emptyState}>Loading...</div>
        ) : !activeSession ? (
          <div style={S.emptyState}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 14, marginBottom: 4 }}>No Auto Pentest session</div>
            <div style={{ fontSize: 12 }}>Start a session to automatically discover and test security invariants</div>
          </div>
        ) : (
          <>
            {/* Invariants sub-tab */}
            {subTab === 'invariants' && (
              <div>
                {mode === 'suggestion' && stats.pending > 0 && (
                  <div style={{ padding: '8px 12px', display: 'flex', gap: 8, borderBottom: '1px solid #2a2a4a' }}>
                    <button style={S.btn('primary')} onClick={approveAll}>Approve All ({stats.pending})</button>
                    <button style={S.btn('danger')} onClick={rejectAll}>Reject All</button>
                  </div>
                )}
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Pri</th>
                      <th style={S.th}>Category</th>
                      <th style={S.th}>Hypothesis</th>
                      <th style={S.th}>Target</th>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invariants.length === 0 && (
                      <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: '#8888aa', padding: 30 }}>
                        {loading ? 'Loading invariants...' : 'No invariants discovered yet'}
                      </td></tr>
                    )}
                    {invariants.map(inv => (
                      <React.Fragment key={inv.id}>
                        <tr style={S.row(expandedInvariant === inv.id)} onClick={() => setExpandedInvariant(expandedInvariant === inv.id ? null : inv.id)}>
                          <td style={S.td}>
                            <span style={{
                              display: 'inline-block', width: 24, height: 24, borderRadius: '50%', textAlign: 'center',
                              lineHeight: '24px', fontSize: 11, fontWeight: 700, color: '#fff',
                              background: PRIORITY_COLORS[(inv.priority || 5) - 1] || '#6b7280',
                            }}>{inv.priority || '-'}</span>
                          </td>
                          <td style={S.td}>
                            <span style={S.tag(AUTOPENTEST_CATEGORY_COLORS[inv.category] || '#6b7280')}>
                              {(inv.category || 'other').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={{ ...S.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {inv.hypothesis || inv.description || '-'}
                          </td>
                          <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11 }}>{inv.target || '-'}</td>
                          <td style={S.td}>
                            <span style={{
                              ...S.tag(AUTOPENTEST_STATUS_COLORS[inv.status] || '#6b7280'),
                            }}>
                              {inv.status === 'testing' && '⟳ '}{inv.status || 'unknown'}
                            </span>
                          </td>
                          <td style={S.td} onClick={e => e.stopPropagation()}>
                            {inv.status === 'pending' && (
                              <>
                                <button style={{ ...S.btn('primary'), padding: '3px 8px', fontSize: 11, marginRight: 4 }} onClick={() => approveInvariant(inv.id)}>Approve</button>
                                <button style={{ ...S.btn('danger'), padding: '3px 8px', fontSize: 11 }} onClick={() => rejectInvariant(inv.id)}>Reject</button>
                              </>
                            )}
                            <span style={{ cursor: 'pointer', marginLeft: 4, fontSize: 10 }}>{expandedInvariant === inv.id ? '▼' : '▶'}</span>
                          </td>
                        </tr>
                        {expandedInvariant === inv.id && (
                          <tr>
                            <td colSpan={6} style={{ ...S.td, background: '#16213e', padding: 12 }}>
                              <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 6 }}>Test Runs for this invariant:</div>
                              {testRuns.filter(tr => tr.invariant_id === inv.id).length === 0 ? (
                                <div style={{ fontSize: 12, color: '#6b7280' }}>No test runs yet</div>
                              ) : (
                                <table style={{ ...S.table, fontSize: 11 }}>
                                  <thead><tr>
                                    <th style={S.th}>Time</th><th style={S.th}>Verdict</th><th style={S.th}>Payload</th><th style={S.th}>Duration</th>
                                  </tr></thead>
                                  <tbody>
                                    {testRuns.filter(tr => tr.invariant_id === inv.id).map(tr => (
                                      <tr key={tr.id}>
                                        <td style={S.td}>{new Date(tr.created_at || tr.timestamp).toLocaleTimeString()}</td>
                                        <td style={S.td}><span style={S.tag(AUTOPENTEST_STATUS_COLORS[tr.verdict] || '#6b7280')}>{tr.verdict}</span></td>
                                        <td style={{ ...S.td, fontFamily: 'monospace', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{tr.payload || '-'}</td>
                                        <td style={S.td}>{tr.duration_ms ? `${tr.duration_ms}ms` : '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Violations sub-tab */}
            {subTab === 'violations' && (
              <div>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Time</th>
                      <th style={S.th}>Invariant</th>
                      <th style={S.th}>Target</th>
                      <th style={S.th}>Payload</th>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {violations.length === 0 && (
                      <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: '#8888aa', padding: 30 }}>
                        No violations found yet
                      </td></tr>
                    )}
                    {violations.map(tr => {
                      const inv = invariants.find(i => i.id === tr.invariant_id);
                      return (
                        <React.Fragment key={tr.id}>
                          <tr style={S.row(expandedViolation === tr.id)} onClick={() => setExpandedViolation(expandedViolation === tr.id ? null : tr.id)}>
                            <td style={S.td}>{new Date(tr.created_at || tr.timestamp).toLocaleTimeString()}</td>
                            <td style={{ ...S.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {inv?.hypothesis || inv?.description || tr.invariant_id?.slice(0, 8) || '-'}
                            </td>
                            <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11 }}>{tr.target || inv?.target || '-'}</td>
                            <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {tr.payload || '-'}
                            </td>
                            <td style={S.td}><span style={S.tag('#ef4444')}>violated</span></td>
                            <td style={S.td}>{tr.duration_ms ? `${tr.duration_ms}ms` : '-'}</td>
                          </tr>
                          {expandedViolation === tr.id && (
                            <tr>
                              <td colSpan={6} style={{ ...S.td, background: '#16213e', padding: 0 }}>
                                <div style={{ display: 'flex', gap: 0, maxHeight: 300, overflow: 'auto' }}>
                                  <div style={{ flex: 1, borderRight: '1px solid #2a2a4a', padding: 12 }}>
                                    <div style={{ fontSize: 10, color: '#8888aa', marginBottom: 4, fontWeight: 700 }}>REQUEST</div>
                                    <pre style={{ margin: 0, fontSize: 11, color: '#e2e8f0', whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                      {tr.request || 'No request data'}
                                    </pre>
                                  </div>
                                  <div style={{ flex: 1, padding: 12 }}>
                                    <div style={{ fontSize: 10, color: '#8888aa', marginBottom: 4, fontWeight: 700 }}>RESPONSE</div>
                                    <pre style={{ margin: 0, fontSize: 11, color: '#e2e8f0', whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                      {tr.response || 'No response data'}
                                    </pre>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Forced Browsing sub-tab */}
            {subTab === 'forced_browsing' && (
              <div>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>URL</th>
                      <th style={S.th}>Method</th>
                      <th style={S.th}>Auth Status</th>
                      <th style={S.th}>NoAuth Status</th>
                      <th style={S.th}>Similarity</th>
                      <th style={S.th}>Verdict</th>
                      <th style={S.th}>Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFB.length === 0 && (
                      <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: '#8888aa', padding: 30 }}>
                        No forced browsing results yet
                      </td></tr>
                    )}
                    {sortedFB.map((fb, idx) => (
                      <tr key={fb.id || idx}>
                        <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {fb.url || fb.path || '-'}
                        </td>
                        <td style={S.td}><MethodBadge method={fb.method || 'GET'} /></td>
                        <td style={S.td}><StatusBadge status={fb.auth_status} /></td>
                        <td style={S.td}><StatusBadge status={fb.noauth_status} /></td>
                        <td style={S.td}>{fb.similarity != null ? `${Math.round(fb.similarity * 100)}%` : '-'}</td>
                        <td style={S.td}>
                          <span style={S.tag(fbVerdictColors[fb.verdict] || '#6b7280')}>
                            {fb.verdict || 'unknown'}
                          </span>
                        </td>
                        <td style={S.td}>
                          {fb.severity && <span style={S.tag(SEVERITY_COLORS[fb.severity] || '#6b7280')}>{fb.severity}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* App Model sub-tab */}
            {subTab === 'model' && (
              <div style={{ padding: 12 }}>
                {Object.keys(modelGroups).length === 0 ? (
                  <div style={S.emptyState}>No application model data yet</div>
                ) : (
                  Object.entries(modelGroups).map(([type, items]) => (
                    <ModelGroup key={type} type={type} items={items} />
                  ))
                )}
              </div>
            )}

            {/* Activity Log sub-tab */}
            {subTab === 'log' && (
              <div style={{ padding: 0 }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Time</th>
                      <th style={S.th}>Phase</th>
                      <th style={S.th}>Level</th>
                      <th style={S.th}>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.length === 0 && (
                      <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center', color: '#8888aa', padding: 30 }}>
                        No activity log entries yet
                      </td></tr>
                    )}
                    {log.map((entry, idx) => (
                      <tr key={entry.id || idx}>
                        <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 11 }}>
                          {new Date(entry.timestamp || entry.created_at).toLocaleTimeString()}
                        </td>
                        <td style={S.td}>
                          {entry.phase && (
                            <span style={S.tag(AUTOPENTEST_PHASE_COLORS[entry.phase] || '#6b7280')}>
                              {entry.phase}
                            </span>
                          )}
                        </td>
                        <td style={S.td}>
                          <span style={{ color: entry.level === 'error' ? '#ef4444' : entry.level === 'warning' ? '#f59e0b' : '#8888aa', fontSize: 11 }}>
                            {entry.level || 'info'}
                          </span>
                        </td>
                        <td style={{ ...S.td, fontSize: 12 }}>{entry.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div ref={logEndRef} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Collapsible model group helper for AutoPentestTab
function ModelGroup({ type, items }) {
  const [open, setOpen] = useState(true);
  const typeLabels = { endpoint: 'Endpoints', auth_mechanism: 'Auth Mechanisms', trust_boundary: 'Trust Boundaries', data_flow: 'Data Flows' };
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #2a2a4a' }}
        onClick={() => setOpen(!open)}
      >
        <span style={{ fontSize: 10, color: '#8888aa' }}>{open ? '▼' : '▶'}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {typeLabels[type] || type.replace(/_/g, ' ')} ({items.length})
        </span>
      </div>
      {open && (
        <div style={{ padding: '8px 0' }}>
          {items.map((item, idx) => (
            <div key={item.id || idx} style={{ ...S.card, padding: 10, marginBottom: 8 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0', marginBottom: 4 }}>
                {item.identifier || item.name || item.path || '-'}
              </div>
              {item.properties && typeof item.properties === 'object' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {Object.entries(item.properties).map(([k, v]) => (
                    <span key={k} style={{ fontSize: 10, color: '#8888aa', background: '#1a1a2e', padding: '2px 6px', borderRadius: 4, border: '1px solid #2a2a4a' }}>
                      {k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AICopilotSidebar({ isOpen, onToggle, engagementId, activeSessionId, engagementName, targetContext, clearTargetContext }) {
  const saved = useRef(_loadAIState()).current;
  const [messages, setMessages] = useState(saved.messages || []);
  const [inputText, setInputText] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState('');
  const [toolActivity, setToolActivity] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [aiTestMode, setAiTestMode] = useState('smart'); // 'smart' or 'aggressive'

  // Pre-fill input when targetContext has a suggestedPrompt (from parameter tamper)
  useEffect(() => {
    if (targetContext?.suggestedPrompt) {
      setInputText(targetContext.suggestedPrompt);
    }
  }, [targetContext]);
  const [activityLog, setActivityLog] = useState(saved.activityLog || []);
  const [showLog, setShowLog] = useState(saved.showLog !== undefined ? saved.showLog : true);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [knowledgeUploading, setKnowledgeUploading] = useState(false);
  const [knowledgeScope, setKnowledgeScope] = useState('global');
  const messagesEndRef = useRef(null);
  const logEndRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);
  const contextSentRef = useRef(saved.contextSent || false);

  // Persist chat state to sessionStorage on changes
  useEffect(() => {
    _saveAIState(messages, activityLog, contextSentRef.current, showLog);
  }, [messages, activityLog, showLog]);

  const addLogEntry = useCallback((entry) => {
    setActivityLog(prev => [...prev, { ...entry, ts: new Date() }]);
  }, []);

  // Check AI status on mount
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    fetch(`${ORCH_API}/ai/status`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => setAiStatus(d))
      .catch(() => {});
  }, []);

  // Load knowledge files
  const loadKnowledge = useCallback(async () => {
    const token = sessionStorage.getItem('token');
    const url = engagementId
      ? `${ORCH_API}/ai/knowledge?engagement_id=${engagementId}`
      : `${ORCH_API}/ai/knowledge`;
    try {
      const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setKnowledgeFiles(d.files || []); }
    } catch {}
  }, [engagementId]);

  useEffect(() => { if (showKnowledge) loadKnowledge(); }, [showKnowledge, loadKnowledge]);

  const uploadKnowledgeFile = useCallback(async (file) => {
    if (!file) return;
    setKnowledgeUploading(true);
    const token = sessionStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('engagement_id', knowledgeScope === 'engagement' && engagementId ? engagementId : '');
    try {
      const r = await fetch(`${ORCH_API}/ai/knowledge/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (r.ok) {
        addLogEntry({ type: 'start', text: `Uploaded: ${file.name} (processing...)` });
        // Poll for status
        const data = await r.json();
        const pollId = data.id;
        const poll = setInterval(async () => {
          const pr = await fetch(`${ORCH_API}/ai/knowledge/${pollId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (pr.ok) {
            const pd = await pr.json();
            if (pd.status === 'ready' || pd.status === 'error') {
              clearInterval(poll);
              addLogEntry({ type: pd.status === 'ready' ? 'done' : 'error', text: `${file.name}: ${pd.status} (${pd.chunk_count || 0} chunks)` });
              loadKnowledge();
            }
          }
        }, 2000);
        setTimeout(() => clearInterval(poll), 120000); // Max 2 min polling
        loadKnowledge();
      }
    } catch {}
    setKnowledgeUploading(false);
  }, [engagementId, knowledgeScope, addLogEntry, loadKnowledge]);

  // Reset context flag when engagement changes
  useEffect(() => { contextSentRef.current = false; }, [engagementId]);

  // Auto-scroll on new messages and log entries
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamBuffer]);
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activityLog]);

  const sendMessage = useCallback(async () => {
    if (!inputText.trim() || streaming || !engagementId) return;

    // Build context prefix for the first message in this engagement
    let userContent = inputText.trim();
    if (!contextSentRef.current) {
      const ctx = [
        `[Context: engagement_id=${engagementId}`,
        activeSessionId ? `session_id=${activeSessionId}` : null,
        engagementName ? `engagement="${engagementName}"` : null,
      ].filter(Boolean).join(', ') + '. Use these IDs when calling tools.]';
      userContent = ctx + '\n\n' + userContent;
      contextSentRef.current = true;
    }

    // Inject target context from sitemap "AI Copilot" action
    if (targetContext) {
      const targetInfo = `[TARGET: ${targetContext.url || targetContext.path}. Focus your actions on this specific URL/path only.]`;
      userContent = targetInfo + '\n\n' + userContent;
      if (clearTargetContext) clearTargetContext();
    }

    // Inject test mode
    if (aiTestMode === 'aggressive') {
      userContent = '[MODE: aggressive - spray all payloads]\n' + userContent;
    }

    const userMsg = { role: 'user', content: userContent };
    // Display the clean message (without context prefix)
    const displayMsg = { role: 'user', content: inputText.trim() + (targetContext ? ` [target: ${targetContext.path || targetContext.url}]` : '') };
    const newMessages = [...messages, userMsg];
    setMessages(prev => [...prev, displayMsg]);
    setInputText('');
    setStreaming(true);
    setStreamBuffer('');
    setToolActivity(null);

    const controller = new AbortController();
    abortRef.current = controller;
    const token = sessionStorage.getItem('token');
    let accumulated = '';
    let tokenCount = 0;

    setShowLog(true); // Auto-expand activity log on new message
    addLogEntry({ type: 'start', text: `Sending: "${inputText.trim().slice(0, 80)}${inputText.trim().length > 80 ? '...' : ''}"` });

    await consumeSSEStream(
      `${ORCH_API}/ai/chat`,
      { engagement_id: engagementId, messages: newMessages, session_id: activeSessionId || null },
      token,
      {
        onToken: (t) => {
          accumulated += t;
          tokenCount++;
          setStreamBuffer(accumulated);
          setToolActivity(null);
        },
        onToolCall: (d) => {
          setToolActivity(d.tool);
          addLogEntry({ type: 'tool_call', tool: d.tool, text: `Calling tool: ${d.tool}` });
        },
        onToolResult: (d) => {
          setToolActivity(null);
          const preview = d?.result_preview || '';
          addLogEntry({ type: 'tool_result', tool: d?.tool, text: `${d?.tool} → ${preview.slice(0, 150)}${preview.length > 150 ? '...' : ''}` });
        },
        onDone: (d) => {
          setMessages(prev => [...prev, { role: 'assistant', content: accumulated }]);
          setStreamBuffer('');
          setStreaming(false);
          setToolActivity(null);
          addLogEntry({
            type: 'done',
            text: `Complete — ${tokenCount} tokens`,
          });
        },
        onError: (msg) => {
          setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${msg}` }]);
          setStreamBuffer('');
          setStreaming(false);
          setToolActivity(null);
          addLogEntry({ type: 'error', text: `Error: ${msg}` });
        },
      },
      controller.signal,
    );
  }, [inputText, streaming, messages, engagementId, activeSessionId, engagementName, targetContext, clearTargetContext]);

  const stopStreaming = () => {
    if (abortRef.current) abortRef.current.abort();
    if (streamBuffer) {
      setMessages(prev => [...prev, { role: 'assistant', content: streamBuffer + '\n\n*(stopped)*' }]);
    }
    setStreamBuffer('');
    setStreaming(false);
    setToolActivity(null);
  };

  if (!isOpen) return null;

  const isReady = aiStatus?.enabled && engagementId;

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 400,
      background: '#0d1117', borderLeft: '2px solid #3b82f680',
      display: 'flex', flexDirection: 'column', zIndex: 9998,
      boxShadow: '-4px 0 20px rgba(0,0,0,0.4)',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid #2a2a4a',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#1a1a2e',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>&#x1f916;</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>AI Copilot</span>
          {aiStatus?.enabled && (
            <span style={{
              fontSize: 9, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
              background: aiStatus.provider === 'ollama' ? '#f59e0b20' : '#22c55e20',
              color: aiStatus.provider === 'ollama' ? '#f59e0b' : '#22c55e',
            }}>
              {aiStatus.provider === 'ollama'
                ? `Ollama ${aiStatus.model_chat || ''}`
                : aiStatus.model_chat?.split('-').slice(0, 2).join(' ')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => setShowKnowledge(!showKnowledge)}
            style={{
              background: showKnowledge ? '#3b82f620' : 'none', border: showKnowledge ? '1px solid #3b82f650' : '1px solid transparent',
              color: showKnowledge ? '#3b82f6' : '#8888aa', cursor: 'pointer', fontSize: 11,
              padding: '2px 8px', borderRadius: 4, fontWeight: 600,
            }}
            title="Knowledge Base"
          >&#x1f4da; KB{knowledgeFiles.length > 0 ? ` (${knowledgeFiles.length})` : ''}</button>
          <button onClick={onToggle} style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer', fontSize: 20 }}>&times;</button>
        </div>
      </div>

      {/* Knowledge Base Panel */}
      {showKnowledge && (
        <div style={{ borderBottom: '1px solid #2a2a4a', background: '#0a0a14', maxHeight: 250, overflowY: 'auto' }}>
          <div style={{ padding: '8px 12px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={knowledgeScope}
              onChange={e => setKnowledgeScope(e.target.value)}
              style={{ fontSize: 10, padding: '3px 6px', background: '#16213e', color: '#e2e8f0', border: '1px solid #2a2a4a', borderRadius: 4 }}
            >
              <option value="global">Global</option>
              {engagementId && <option value="engagement">This Engagement</option>}
            </select>
            <label style={{
              fontSize: 10, padding: '3px 8px', background: '#22c55e20', color: '#22c55e',
              border: '1px solid #22c55e30', borderRadius: 4, cursor: 'pointer', fontWeight: 600,
              opacity: knowledgeUploading ? 0.5 : 1,
            }}>
              {knowledgeUploading ? 'Uploading...' : '+ Upload File'}
              <input
                type="file"
                accept=".txt,.md,.json,.csv"
                style={{ display: 'none' }}
                disabled={knowledgeUploading}
                onChange={e => { if (e.target.files[0]) uploadKnowledgeFile(e.target.files[0]); e.target.value = ''; }}
              />
            </label>
            <span style={{ fontSize: 9, color: '#8888aa' }}>.txt .md .json .csv</span>
          </div>
          {knowledgeFiles.length === 0 ? (
            <div style={{ padding: '12px', textAlign: 'center', fontSize: 11, color: '#8888aa' }}>
              No knowledge files uploaded. Upload pentest cheatsheets, payload lists, or methodology docs.
            </div>
          ) : (
            <div style={{ padding: '0 8px 8px' }}>
              {knowledgeFiles.map(f => (
                <div key={f.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
                  background: '#16213e', borderRadius: 4, marginBottom: 3, fontSize: 11,
                }}>
                  <span style={{ color: '#e2e8f0', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.filename}
                  </span>
                  <span style={{
                    fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 600,
                    background: f.status === 'ready' ? '#22c55e20' : f.status === 'error' ? '#ef444420' : '#f59e0b20',
                    color: f.status === 'ready' ? '#22c55e' : f.status === 'error' ? '#ef4444' : '#f59e0b',
                  }}>
                    {f.status === 'ready' ? `${f.chunk_count} chunks` : f.status}
                  </span>
                  <span style={{ fontSize: 9, color: f.is_global ? '#3b82f6' : '#f59e0b' }}>
                    {f.is_global ? 'global' : 'eng'}
                  </span>
                  <button
                    onClick={async () => {
                      const token = sessionStorage.getItem('token');
                      await fetch(`${ORCH_API}/ai/knowledge/${f.id}`, {
                        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
                      });
                      loadKnowledge();
                    }}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12, padding: 0 }}
                    title="Delete"
                  >&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Status bar */}
      {!isReady ? (
        <div style={{ padding: '8px 16px', background: '#ef444420', borderBottom: '1px solid #2a2a4a', fontSize: 11, color: '#ef4444' }}>
          {!aiStatus?.enabled ? 'AI is not enabled. Configure in .env.orchestrator.' : !engagementId ? 'Select an engagement to use AI Copilot.' : ''}
        </div>
      ) : (
        <div style={{ padding: '6px 16px', background: '#1a1a2e', borderBottom: '1px solid #2a2a4a', fontSize: 10, color: '#8888aa', display: 'flex', gap: 12 }}>
          {engagementName && <span>Engagement: <span style={{ color: '#e2e8f0' }}>{engagementName}</span></span>}
          {activeSessionId && <span>Session: <span style={{ color: '#3b82f6', fontFamily: 'monospace' }}>{activeSessionId.slice(0, 8)}...</span></span>}
          <span style={{ marginLeft: 'auto' }} />
          <span
            onClick={() => setAiTestMode(aiTestMode === 'smart' ? 'aggressive' : 'smart')}
            style={{
              cursor: 'pointer', padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600,
              background: aiTestMode === 'aggressive' ? '#ef444430' : '#22c55e30',
              color: aiTestMode === 'aggressive' ? '#ef4444' : '#22c55e',
              border: `1px solid ${aiTestMode === 'aggressive' ? '#ef444450' : '#22c55e50'}`,
            }}
            title={aiTestMode === 'smart' ? 'Smart: AI picks best payloads from KB' : 'Aggressive: Spray all KB payloads'}
          >
            {aiTestMode === 'smart' ? 'Smart' : 'Aggressive'}
          </span>
        </div>
      )}

      {/* Target context banner */}
      {targetContext && (
        <div style={{
          padding: '6px 12px', background: '#a855f710', borderBottom: '1px solid #a855f730',
          fontSize: 11,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#a855f7', fontWeight: 600 }}>
              {targetContext.paramName ? 'Tamper:' : 'Target:'}
            </span>
            <span style={{ color: '#e2e8f0', fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {targetContext.paramName
                ? `${targetContext.paramName} (${targetContext.paramLocation || 'param'}) @ ${targetContext.url || targetContext.path}`
                : (targetContext.url || targetContext.path)}
            </span>
            <button
              onClick={() => { clearTargetContext && clearTargetContext(); setInputText(''); }}
              style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer', fontSize: 12 }}
              title="Clear target"
            >&times;</button>
          </div>
          {targetContext.paramName && targetContext.attack && (
            <div style={{ marginTop: 2, color: '#f59e0b', fontSize: 10, display: 'flex', gap: 12 }}>
              <span>Attack: {targetContext.attack === 'xss' ? 'XSS' : targetContext.attack === 'sqli' ? 'SQL Injection' : targetContext.attack === 'fuzz' ? 'Fuzzing' : 'AI Analysis'}</span>
              {targetContext.intensity && (
                <span style={{ color: targetContext.intensity === 'quick' ? '#22c55e' : targetContext.intensity === 'full' ? '#ef4444' : '#f59e0b' }}>
                  Intensity: {targetContext.intensity === 'quick' ? 'Quick (10)' : targetContext.intensity === 'moderate' ? 'Moderate (100)' : 'Full (all KB)'}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {messages.length === 0 && !streaming && (
          <div style={{ textAlign: 'center', color: '#8888aa', fontSize: 12, padding: '40px 20px', lineHeight: 1.8 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>&#x1f916;</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>AI Pentest Copilot</div>
            <div>Ask about findings, suggest next tests, analyze the engagement scope, or get help writing reports.</div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
          }}>
            <div style={{
              maxWidth: '85%', padding: '8px 12px', borderRadius: 10,
              fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap',
              ...(m.role === 'user'
                ? { background: '#3b82f630', color: '#93c5fd', borderBottomRightRadius: 2 }
                : { background: '#1a1a2e', color: '#e2e8f0', border: '1px solid #2a2a4a', borderBottomLeftRadius: 2 }),
            }}>
              {m.content}
            </div>
          </div>
        ))}

        {/* Streaming message */}
        {streaming && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              maxWidth: '85%', padding: '8px 12px', borderRadius: 10, borderBottomLeftRadius: 2,
              background: '#1a1a2e', color: '#e2e8f0', border: '1px solid #3b82f640',
              fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap',
            }}>
              {toolActivity && (
                <div style={{ fontSize: 10, color: '#f59e0b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  &#x1f50d; Querying {toolActivity}...
                </div>
              )}
              {streamBuffer || (!toolActivity ? '...' : '')}
              <span style={{ color: '#3b82f6' }}>|</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Activity Log */}
      {activityLog.length > 0 && (
        <div style={{ borderTop: '1px solid #2a2a4a', background: '#0a0a14' }}>
          <div
            onClick={() => setShowLog(!showLog)}
            style={{
              padding: '6px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', userSelect: 'none', fontSize: 11, color: '#8888aa',
              borderBottom: showLog ? '1px solid #1a1a2a' : 'none',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#f59e0b' }}>&#x26A1;</span>
              <span style={{ fontWeight: 600 }}>Activity Log</span>
              <span style={{
                fontSize: 9, padding: '1px 5px', borderRadius: 8,
                background: streaming ? '#f59e0b30' : '#3b82f620',
                color: streaming ? '#f59e0b' : '#3b82f6',
              }}>
                {activityLog.length}
              </span>
            </span>
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span
                onClick={(e) => { e.stopPropagation(); setActivityLog([]); }}
                style={{ fontSize: 9, color: '#ef4444', cursor: 'pointer' }}
              >Clear</span>
              <span style={{ fontSize: 10, transform: showLog ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>&#x25B2;</span>
            </span>
          </div>
          {showLog && (
            <div style={{
              maxHeight: 180, overflowY: 'auto', padding: '4px 8px',
              fontFamily: '"JetBrains Mono", "Cascadia Code", "Fira Code", monospace',
              fontSize: 10, lineHeight: 1.7,
            }}>
              {activityLog.map((entry, i) => {
                const timeStr = entry.ts.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
                const iconMap = {
                  start: { icon: '▶', color: '#3b82f6' },
                  tool_call: { icon: '⚡', color: '#f59e0b' },
                  tool_result: { icon: '✓', color: '#22c55e' },
                  done: { icon: '●', color: '#22c55e' },
                  error: { icon: '✗', color: '#ef4444' },
                };
                const { icon, color } = iconMap[entry.type] || { icon: '·', color: '#8888aa' };
                return (
                  <div key={i} style={{
                    display: 'flex', gap: 6, padding: '1px 0',
                    color: entry.type === 'error' ? '#ef4444' : '#aaa',
                  }}>
                    <span style={{ color: '#555', flexShrink: 0 }}>{timeStr}</span>
                    <span style={{ color, flexShrink: 0 }}>{icon}</span>
                    <span style={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      color: entry.type === 'tool_call' ? '#f59e0b' : entry.type === 'tool_result' ? '#22c55e' : undefined,
                    }}>{entry.text}</span>
                  </div>
                );
              })}
              <div ref={logEndRef} />
            </div>
          )}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: '8px 12px', borderTop: '1px solid #2a2a4a', background: '#1a1a2e' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={isReady ? 'Ask about the engagement...' : 'AI not available'}
            disabled={!isReady || streaming}
            style={{
              flex: 1, resize: 'none', height: 36, padding: '8px 10px',
              background: '#16213e', color: '#e2e8f0', border: '1px solid #2a2a4a',
              borderRadius: 8, fontSize: 12, fontFamily: 'system-ui, sans-serif',
              outline: 'none',
            }}
            rows={1}
          />
          {streaming ? (
            <button onClick={stopStreaming} style={{
              padding: '0 12px', background: '#ef444430', color: '#ef4444',
              border: '1px solid #ef444450', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>Stop</button>
          ) : (
            <button onClick={sendMessage} disabled={!isReady || !inputText.trim()} style={{
              padding: '0 12px', background: '#3b82f630', color: '#3b82f6',
              border: '1px solid #3b82f650', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              opacity: (!isReady || !inputText.trim()) ? 0.4 : 1,
            }}>Send</button>
          )}
        </div>
        {aiStatus?.budget && (
          <div style={{ fontSize: 9, color: '#8888aa', marginTop: 4, textAlign: 'right' }}>
            ${aiStatus.budget.spent_usd.toFixed(2)} / ${aiStatus.budget.monthly_limit_usd} budget
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ═══════════════════════ Local Proxy Setup Panel ═══════════════════════

function LocalProxySetupPanel({ openLocalClient }) {
  const downloadFile = async (endpoint, filename) => {
    const token = sessionStorage.getItem('token');
    if (!token) { alert('Download failed — no auth token found. Please log in.'); return; }
    try {
      const BACKEND_URL = window.__ENV__?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || '';
      const r = await fetch(`${BACKEND_URL}/api/proxy-client/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) {
        const errText = await r.text().catch(() => r.statusText);
        alert(`Download failed (${r.status}): ${errText}`);
        return;
      }
      const b = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = filename;
      a.click();
    } catch (e) {
      alert(`Download failed: ${e.message}`);
    }
  };

  return (
    <div style={{ padding: 24, borderRadius: 12, background: '#0d1117', border: '2px solid #a855f740', marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 24 }}>&#x1F4E5;</span>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#a855f7' }}>Local Proxy Client</div>
          <div style={{ fontSize: 11, color: '#8888aa', marginTop: 2 }}>Proxy + AI running locally, findings sync to this server</div>
        </div>
      </div>

      {/* Benefits */}
      <div style={{ fontSize: 12, color: '#ccc', lineHeight: 1.8, marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px' }}>
        <div>&#x26A1; <strong>Low latency</strong> — proxy runs locally</div>
        <div>&#x1F512; <strong>Privacy</strong> — traffic stays on your machine</div>
        <div>&#x1F3AE; <strong>GPU AI</strong> — 5s with NVIDIA (vs 60s CPU)</div>
        <div>&#x1F4F6; <strong>Offline</strong> — syncs when reconnected</div>
      </div>

      {/* Requirements */}
      <div style={{ padding: '10px 14px', borderRadius: 8, background: '#16213e', border: '1px solid #2a2a4a', marginBottom: 20, fontSize: 11, color: '#8888aa' }}>
        <span style={{ fontWeight: 600, color: '#e2e8f0' }}>Requirements: </span>
        Docker Desktop &bull; 16GB RAM &bull; 15GB disk &bull; NVIDIA GPU 8GB+ VRAM (optional)
      </div>

      {/* Steps */}
      <div style={{ fontSize: 13, color: '#8888aa', lineHeight: 2, marginBottom: 20 }}>
        <div style={{ marginBottom: 12 }}>
          <span style={{ color: '#a855f7', fontWeight: 700, marginRight: 8 }}>1.</span>
          Download both files below and place them in a folder (e.g. <code style={{ color: '#3b82f6' }}>~/.attackpath</code>)
        </div>
        <div style={{ marginBottom: 12 }}>
          <span style={{ color: '#a855f7', fontWeight: 700, marginRight: 8 }}>2.</span>
          Rename <code style={{ color: '#3b82f6' }}>attackpath.env</code> to <code style={{ color: '#3b82f6' }}>.env</code>, then run:
          <div style={{ marginTop: 4, padding: '6px 12px', borderRadius: 6, background: '#16213e', fontFamily: 'monospace', fontSize: 11, color: '#3b82f6' }}>
            docker compose -f docker-compose.local.yml up -d
          </div>
        </div>
        <div>
          <span style={{ color: '#a855f7', fontWeight: 700, marginRight: 8 }}>3.</span>
          Click <strong style={{ color: '#e2e8f0', cursor: 'pointer' }} onClick={openLocalClient}>Launch Local Client</strong> above — you'll be logged in automatically
        </div>
      </div>

      {/* Download buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button style={{ ...S.btn('primary'), background: '#a855f7', flex: 1, padding: '10px 16px' }}
          onClick={() => downloadFile('docker-compose.local.yml', 'docker-compose.local.yml')}>
          &#x1F4E6; Download Compose File
        </button>
        <button style={{ ...S.btn('primary'), background: '#3b82f6', flex: 1, padding: '10px 16px' }}
          onClick={() => downloadFile('env-template', 'attackpath.env')}>
          &#x1F511; Download attackpath.env (with your token)
        </button>
      </div>

      <div style={{ padding: '10px 16px', borderRadius: 8, background: '#f59e0b10', border: '1px solid #f59e0b30', fontSize: 11, color: '#f59e0b' }}>
        <strong>Note:</strong> First startup downloads the AI model (~9GB). Subsequent starts are instant.
        Findings sync to this server every 30 seconds.
      </div>
    </div>
  );
}


// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const TAB_NAMES = ['History', 'Intercept', 'Repeater', 'Intruder', 'Site Map', 'Crawl', 'Inputs', 'Scanner', 'Findings', 'Libraries', 'Collaborator', 'AI Logs', 'Auto Pentest'];

export default function AttackPathProxy() {
  const proxyFetch = useProxyFetch();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('History');
  const visitedTabsRef = useRef(new Set(['History']));

  // Track visited tabs so we can lazy-mount but keep them alive
  const handleSetActiveTab = useCallback((tab) => {
    visitedTabsRef.current.add(tab);
    setActiveTab(tab);
  }, []);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState('');

  // Scope enforcement & engagement — default ON so scope applies immediately
  const [scopeEnforced, setScopeEnforced] = useState(true);
  const [engagementId, setEngagementId] = useState('');
  const [engagementName, setEngagementName] = useState('');

  // Tab activity tracking — {tabName: count} for running jobs
  const [tabActivity, setTabActivity] = useState({});

  // AI Copilot Sidebar
  const [aiSidebarOpen, setAiSidebarOpen] = useState(false);
  const [aiTargetContext, setAiTargetContext] = useState(null); // { url, path } from sitemap context menu

  // AI Jobs tracking
  const [aiJobs, setAiJobs] = useState([]); // recent AI activity entries
  const [aiJobsExpanded, setAiJobsExpanded] = useState(false);

  // Repeater
  const [repeaterTabs, setRepeaterTabs] = useState([]);

  const sendToRepeater = useCallback((requestData) => {
    proxyFetch(`/requests/${requestData.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(detail => {
        if (!detail) return;
        const rawReq = buildRawRequest(detail);
        const newTab = {
          id: crypto.randomUUID(),
          name: `${detail.method} ${detail.host}${(detail.path || '/').substring(0, 30)}`,
          target: {
            scheme: detail.scheme || (detail.is_tls ? 'https' : 'http'),
            host: detail.host,
            port: detail.port || (detail.is_tls ? 443 : 80),
            path: detail.path || '/',
            query_string: detail.query_string || '',
            is_tls: detail.is_tls,
          },
          rawRequest: rawReq,
          response: null,
          history: [],
          historyIndex: -1,
          followRedirects: false,
          sending: false,
        };
        setRepeaterTabs(prev => [...prev, newTab]);
        handleSetActiveTab('Repeater');
      })
      .catch(() => {});
  }, [proxyFetch, handleSetActiveTab]);

  // Send a detected form to repeater — builds a request from form fields
  const sendFormToRepeater = useCallback(async (formData) => {
    try {
      const res = await proxyFetch(`/forms/build-request?session_id=${activeSessionId}`, {
        method: 'POST',
        body: JSON.stringify({
          request_id: formData.request_id,
          form_action: formData.form_action,
          form_method: formData.form_method,
          field_values: formData.field_values || {},
        }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const newTab = {
        id: crypto.randomUUID(),
        name: `${data.method} ${data.host}${(data.path || '/').substring(0, 30)}`,
        target: {
          scheme: data.scheme || 'https',
          host: data.host,
          port: data.port || 443,
          path: data.path || '/',
          query_string: '',
          is_tls: data.is_tls,
        },
        rawRequest: data.raw_request,
        response: null,
        history: [],
        historyIndex: -1,
        followRedirects: false,
        sending: false,
      };
      setRepeaterTabs(prev => [...prev, newTab]);
      handleSetActiveTab('Repeater');
    } catch (err) { console.error('sendFormToRepeater error:', err); }
  }, [proxyFetch, activeSessionId, handleSetActiveTab]);

  // Intruder
  const [intruderTabs, setIntruderTabs] = useState([]);

  const sendToIntruder = useCallback((requestData) => {
    proxyFetch(`/requests/${requestData.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(detail => {
        if (!detail) return;
        const rawReq = buildRawRequest(detail);
        const newTab = {
          id: crypto.randomUUID(),
          name: `${detail.method} ${detail.host}${(detail.path || '/').substring(0, 30)}`,
          target: {
            scheme: detail.scheme || (detail.is_tls ? 'https' : 'http'),
            host: detail.host,
            port: detail.port || (detail.is_tls ? 443 : 80),
          },
          templateRequest: rawReq,
          attackType: 'sniper',
          payloadSets: [{ type: 'simple_list', items: [] }],
          processingRules: [],
          concurrency: 10,
          delayMs: 0,
          timeoutMs: 30000,
          followRedirects: false,
          storeRequests: false,
        };
        setIntruderTabs(prev => [...prev, newTab]);
        handleSetActiveTab('Intruder');
      })
      .catch(() => {});
  }, [proxyFetch, handleSetActiveTab]);

  // WebSocket
  const wsRef = useRef(null);

  // Check proxy status — only connect when running locally (port 3080/3081)
  const checkStatus = useCallback(async () => {
    const port = window.location.port;
    if (port !== '3080' && port !== '3081') {
      setStatus({ online: false });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/proxy-api/health');
      if (res.ok) {
        const data = await res.json();
        setStatus({ online: true, ...data });
      } else {
        setStatus({ online: false });
      }
    } catch {
      setStatus({ online: false });
    }
    setLoading(false);
  }, []);

  // Load sessions
  const loadSessions = useCallback(async () => {
    try {
      const res = await proxyFetch('/sessions');
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
        const active = data.find(s => s.is_active);
        if (active && !activeSessionId) {
          setActiveSessionId(active.id);
        }
      }
    } catch {}
  }, [proxyFetch, activeSessionId]);

  // Create session
  const createSession = async (name) => {
    try {
      const res = await proxyFetch('/sessions', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        const data = await res.json();
        await proxyFetch(`/sessions/${data.id}/activate`, { method: 'POST' });
        setActiveSessionId(data.id);
        loadSessions();
      }
    } catch {}
  };

  // WebSocket for live updates
  useEffect(() => {
    if (!status?.online) return;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/proxy-api/ws/proxy`;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onclose = () => { setTimeout(connect, 3000); };
      ws.onerror = () => {};
    };

    connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [status?.online]);

  // Poll active jobs for tab activity badges
  const pollActivity = useCallback(async () => {
    if (!activeSessionId) return;
    const activity = {};
    try {
      // Scanner jobs
      const scanRes = await proxyFetch(`/scanner/jobs?session_id=${activeSessionId}`);
      if (scanRes.ok) {
        const jobs = await scanRes.json();
        const running = jobs.filter(j => j.status === 'running').length;
        if (running > 0) activity['Scanner'] = running;
      }
      // Intruder attacks
      const intRes = await proxyFetch(`/intruder/attacks?session_id=${activeSessionId}`);
      if (intRes.ok) {
        const attacks = await intRes.json();
        const running = attacks.filter(a => a.status === 'running' || a.status === 'paused').length;
        if (running > 0) activity['Intruder'] = running;
      }
      // Crawl jobs
      const crawlRes = await proxyFetch(`/crawl/jobs?session_id=${activeSessionId}`);
      if (crawlRes.ok) {
        const jobs = await crawlRes.json();
        const running = jobs.filter(j => j.status === 'running' || j.status === 'pending').length;
        if (running > 0) activity['Crawl'] = running;
      }
    } catch {}
    setTabActivity(activity);
  }, [proxyFetch, activeSessionId]);

  // Track activity from WebSocket events
  useEffect(() => {
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'scan_progress') {
          setTabActivity(prev => ({ ...prev, Scanner: Math.max(1, prev.Scanner || 0) }));
        } else if (msg.type === 'intruder_result' || msg.type === 'intruder_progress') {
          setTabActivity(prev => ({ ...prev, Intruder: Math.max(1, prev.Intruder || 0) }));
        } else if (msg.type === 'intruder_complete') {
          // Re-poll to get accurate count
          pollActivity();
        } else if (msg.type === 'crawl_progress') {
          setTabActivity(prev => ({ ...prev, Crawl: Math.max(1, prev.Crawl || 0) }));
        } else if (msg.type === 'crawl_complete') {
          pollActivity();
        }
      } catch {}
    };
    const ws = wsRef.current;
    if (ws) ws.addEventListener('message', handler);
    return () => { if (ws) ws.removeEventListener('message', handler); };
  }, [wsRef.current, pollActivity]);

  // Poll activity on session change and periodically
  useEffect(() => {
    if (!status?.online || !activeSessionId) return;
    pollActivity();
    const interval = setInterval(pollActivity, 15000);
    return () => clearInterval(interval);
  }, [status?.online, activeSessionId, pollActivity]);

  // Poll AI activity for running jobs
  const ORCH_API_MAIN = (window.__ENV__?.REACT_APP_ORCHESTRATOR_URL || process.env.REACT_APP_ORCHESTRATOR_URL || 'http://localhost:8100') + '/api';
  useEffect(() => {
    if (!activeSessionId) return;
    const token = sessionStorage.getItem('token');
    const poll = async () => {
      try {
        const res = await fetch(`${ORCH_API_MAIN}/ai/activity?session_id=${activeSessionId}&limit=20`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const entries = data.entries || [];
          // Find tool_call events without matching tool_result (running jobs)
          const calls = entries.filter(e => e.event_type === 'tool_call');
          const results = new Set(entries.filter(e => e.event_type === 'tool_result').map(e => e.tool_name + (e.created_at || '').slice(0, 16)));
          // Recent entries for display
          setAiJobs(entries.slice(0, 10));
        }
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [activeSessionId, ORCH_API_MAIN]);

  // Initial load
  useEffect(() => { checkStatus(); }, [checkStatus]);
  useEffect(() => { if (status?.online) loadSessions(); }, [status?.online, loadSessions]);

  const openLocalClient = useCallback(() => {
    const t = sessionStorage.getItem('token');
    const url = t ? `http://localhost:3080/?sso_token=${encodeURIComponent(t)}` : 'http://localhost:3080/';
    window.open(url, '_blank');
  }, []);

  // ─── Loading State ───
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80, color: '#e2e8f0' }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>Connecting to AttackPath Proxy...</div>
      </div>
    );
  }

  // ─── Offline State — Show setup instructions ───
  if (!status?.online) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#e2e8f0' }}>AttackPath Proxy</div>
            <div style={{ color: '#8888aa', fontSize: 14, marginTop: 4 }}>
              Run the proxy locally — download the client, then launch it below
            </div>
          </div>
          <button style={S.btn('primary')} onClick={openLocalClient}>Launch Local Client</button>
        </div>

        <ApiKeyManager />

        <LocalProxySetupPanel openLocalClient={openLocalClient} />
      </div>
    );
  }

  // ─── Online State — Full Proxy UI ───
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: '#e2e8f0' }}>
            AttackPath Proxy
            <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#22c55e20', color: '#22c55e' }}>Online</span>
          </div>
          <div style={{ color: '#8888aa', fontSize: 13, marginTop: 2 }}>
            Proxy: <code style={{ color: '#3b82f6' }}>localhost:8083</code>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={S.stat}>WS: {wsRef.current?.readyState === 1 ? 'Connected' : 'Reconnecting...'}</span>
          <button
            onClick={() => setAiSidebarOpen(!aiSidebarOpen)}
            style={{
              ...S.btn(aiSidebarOpen ? 'primary' : undefined),
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            &#x1f916; AI Copilot
          </button>
          <a href="/proxy-api/api/certs/ca.pem" download="attackpath-proxy-ca.pem" style={{ textDecoration: 'none' }}>
            <button style={S.btn()}>Download CA Cert</button>
          </a>
        </div>
      </div>

      {/* Session + Engagement row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
        <SessionSelector
          sessions={sessions}
          activeSession={activeSessionId}
          onSelect={(id) => { setActiveSessionId(id); }}
          onCreate={createSession}
          onRefresh={loadSessions}
        />
        <EngagementSelector proxyFetch={proxyFetch} activeSessionId={activeSessionId} onScopeChange={({ enforced, engagementId: eid, engagementName: ename }) => { setScopeEnforced(enforced); if (eid !== undefined) { setEngagementId(eid || ''); setEngagementName(ename || ''); } }} />
      </div>

      {/* AI Jobs Status */}
      {aiJobs.length > 0 && (() => {
        // Derive jobs from activity entries: group by chat_request → response cycles
        const jobs = [];
        let currentJob = null;
        // Process in chronological order (entries are DESC, reverse them)
        const sorted = [...aiJobs].reverse();
        for (const entry of sorted) {
          if (entry.event_type === 'chat_request') {
            if (currentJob) jobs.push(currentJob);
            currentJob = {
              id: entry.id,
              startedAt: entry.created_at,
              query: (entry.message_content || '').slice(0, 100),
              status: 'running',
              tools: [],
              duration: null,
              endedAt: null,
            };
          } else if (currentJob) {
            if (entry.event_type === 'tool_call') {
              currentJob.tools.push({ name: entry.tool_name, status: 'running' });
            } else if (entry.event_type === 'tool_result') {
              const tc = currentJob.tools.find(t => t.name === entry.tool_name && t.status === 'running');
              if (tc) { tc.status = 'complete'; tc.duration = entry.duration_ms; }
            } else if (entry.event_type === 'chat_response') {
              currentJob.status = 'complete';
              currentJob.duration = entry.duration_ms;
              currentJob.endedAt = entry.created_at;
            } else if (entry.event_type === 'error') {
              currentJob.status = 'error';
              currentJob.endedAt = entry.created_at;
            }
          }
        }
        if (currentJob) jobs.push(currentJob);
        jobs.reverse(); // Most recent first

        const runningCount = jobs.filter(j => j.status === 'running').length;
        if (jobs.length === 0) return null;

        const statusColors = { running: '#f59e0b', complete: '#22c55e', error: '#ef4444', cancelled: '#8888aa' };
        const statusIcons = { running: '⏳', complete: '✓', error: '✗', cancelled: '⊘' };

        return (
          <div style={{ marginBottom: 8, position: 'relative' }}>
            <button
              onClick={() => setAiJobsExpanded(!aiJobsExpanded)}
              style={{
                background: runningCount > 0 ? '#f59e0b15' : '#a855f715',
                border: `1px solid ${runningCount > 0 ? '#f59e0b30' : '#a855f730'}`,
                borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                color: runningCount > 0 ? '#f59e0b' : '#a855f7',
                fontSize: 12, fontWeight: 600, width: '100%', textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 14 }}>{runningCount > 0 ? '⏳' : '🤖'}</span>
              {runningCount > 0
                ? `${runningCount} AI Job${runningCount !== 1 ? 's' : ''} Running`
                : `${jobs.length} AI Job${jobs.length !== 1 ? 's' : ''}`}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 10, color: '#8888aa' }}>{aiJobsExpanded ? '▲' : '▼'}</span>
            </button>
            {aiJobsExpanded && (
              <div style={{
                background: '#0d1117', border: '1px solid #2a2a4a', borderRadius: 8,
                marginTop: 4, maxHeight: 300, overflowY: 'auto', fontSize: 11,
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2a2a4a' }}>
                      <th style={{ ...S.th, padding: '4px 8px', fontSize: 10 }}>Time</th>
                      <th style={{ ...S.th, padding: '4px 8px', fontSize: 10 }}>Status</th>
                      <th style={{ ...S.th, padding: '4px 8px', fontSize: 10 }}>Query</th>
                      <th style={{ ...S.th, padding: '4px 8px', fontSize: 10 }}>Tools</th>
                      <th style={{ ...S.th, padding: '4px 8px', fontSize: 10 }}>Duration</th>
                      <th style={{ ...S.th, padding: '4px 8px', fontSize: 10 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #1a1a2a' }}>
                        <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 10, color: '#8888aa' }}>
                          {new Date(job.startedAt).toLocaleTimeString('en-US', { hour12: false })}
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                            background: `${statusColors[job.status]}20`, color: statusColors[job.status],
                          }}>
                            {statusIcons[job.status]} {job.status}
                          </span>
                        </td>
                        <td style={{ padding: '4px 8px', fontSize: 10, color: '#ccc', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {job.query.replace(/\[Context:.*?\]/s, '').replace(/\[TARGET:.*?\]/s, '').trim().slice(0, 60)}
                        </td>
                        <td style={{ padding: '4px 8px', fontSize: 10 }}>
                          {job.tools.length > 0 ? job.tools.map((t, ti) => (
                            <span key={ti} style={{
                              padding: '1px 4px', borderRadius: 3, fontSize: 8, marginRight: 3,
                              background: t.status === 'complete' ? '#22c55e20' : '#f59e0b20',
                              color: t.status === 'complete' ? '#22c55e' : '#f59e0b',
                            }}>{t.name}{t.duration ? ` ${t.duration}ms` : ''}</span>
                          )) : <span style={{ color: '#8888aa' }}>-</span>}
                        </td>
                        <td style={{ padding: '4px 8px', fontSize: 10, color: '#8888aa', textAlign: 'right' }}>
                          {job.duration ? `${(job.duration / 1000).toFixed(1)}s` : job.status === 'running' ? '...' : '-'}
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          {job.status === 'running' && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                // Delete the activity entries for this job to "cancel" it
                                const token = sessionStorage.getItem('token');
                                await fetch(`${ORCH_API_MAIN}/ai/activity/${job.id}`, {
                                  method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
                                });
                                setAiJobs(prev => prev.filter(j => j.id !== job.id));
                              }}
                              style={{
                                background: '#ef444420', border: '1px solid #ef444440', borderRadius: 4,
                                color: '#ef4444', fontSize: 9, padding: '2px 6px', cursor: 'pointer',
                              }}
                            >Cancel</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* Tab Bar */}
      <div style={{ ...S.tabBar, borderRadius: '12px 12px 0 0', marginBottom: 0 }}>
        {TAB_NAMES.map(name => {
          const count = tabActivity[name] || 0;
          return (
            <button key={name} style={{
              ...S.tab(activeTab === name),
              color: count > 0 && activeTab !== name ? '#22c55e' : S.tab(activeTab === name).color,
            }} onClick={() => handleSetActiveTab(name)}>
              {name}
              {count > 0 && (
                <span style={{
                  marginLeft: 6, padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                  background: activeTab === name ? '#3b82f640' : '#22c55e30',
                  color: activeTab === name ? '#3b82f6' : '#22c55e',
                  minWidth: 18, display: 'inline-block', textAlign: 'center',
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div style={{ background: '#1a1a2e08', padding: 16, borderRadius: '0 0 12px 12px', border: '1px solid #2a2a4a', borderTop: 'none' }}>
        <div style={{ display: activeTab === 'History' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('History') && (
            <HistoryTab proxyFetch={proxyFetch} activeSessionId={activeSessionId} wsRef={wsRef} scopeEnforced={scopeEnforced} sendToRepeater={sendToRepeater} sendToIntruder={sendToIntruder} engagementId={engagementId}
              onAskAI={(ctx) => { setAiTargetContext(ctx); setAiSidebarOpen(true); }}
            />
          )}
        </div>
        <div style={{ display: activeTab === 'Intercept' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Intercept') && (
            <InterceptTab proxyFetch={proxyFetch} activeSessionId={activeSessionId} wsRef={wsRef} />
          )}
        </div>
        <div style={{ display: activeTab === 'Repeater' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Repeater') && (
            <RepeaterTab proxyFetch={proxyFetch} activeSessionId={activeSessionId} repeaterTabs={repeaterTabs} setRepeaterTabs={setRepeaterTabs} />
          )}
        </div>
        <div style={{ display: activeTab === 'Intruder' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Intruder') && (
            <IntruderTab proxyFetch={proxyFetch} activeSessionId={activeSessionId}
              intruderTabs={intruderTabs} setIntruderTabs={setIntruderTabs} wsRef={wsRef} />
          )}
        </div>
        <div style={{ display: activeTab === 'Site Map' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Site Map') && (
            <SiteMapTab proxyFetch={proxyFetch} activeSessionId={activeSessionId} scopeEnforced={scopeEnforced} wsRef={wsRef}
              onAskAI={(ctx) => { setAiTargetContext(ctx); setAiSidebarOpen(true); }}
            />
          )}
        </div>
        <div style={{ display: activeTab === 'Crawl' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Crawl') && (
            <CrawlTab proxyFetch={proxyFetch} activeSessionId={activeSessionId} wsRef={wsRef} />
          )}
        </div>
        <div style={{ display: activeTab === 'Inputs' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Inputs') && (
            <InputsTab proxyFetch={proxyFetch} activeSessionId={activeSessionId} sendToRepeater={sendToRepeater} sendFormToRepeater={sendFormToRepeater}
              onAskAI={(ctx) => { setAiTargetContext(ctx); setAiSidebarOpen(true); }}
            />
          )}
        </div>
        <div style={{ display: activeTab === 'Scanner' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Scanner') && (
            <ScannerTab proxyFetch={proxyFetch} activeSessionId={activeSessionId} wsRef={wsRef} engagementId={engagementId} />
          )}
        </div>
        <div style={{ display: activeTab === 'Findings' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Findings') && (
            <FindingsTab proxyFetch={proxyFetch} activeSessionId={activeSessionId} engagementId={engagementId} />
          )}
        </div>
        <div style={{ display: activeTab === 'Libraries' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Libraries') && (
            <LibrariesTab proxyFetch={proxyFetch} activeSessionId={activeSessionId} />
          )}
        </div>
        <div style={{ display: activeTab === 'Collaborator' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Collaborator') && (
            <CollaboratorTab activeSessionId={activeSessionId} />
          )}
        </div>
        <div style={{ display: activeTab === 'AI Logs' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('AI Logs') && (
            <AILogsTab activeSessionId={activeSessionId} engagementId={engagementId} />
          )}
        </div>
        <div style={{ display: activeTab === 'Auto Pentest' ? 'block' : 'none' }}>
          {visitedTabsRef.current.has('Auto Pentest') && (
            <AutoPentestTab activeSessionId={activeSessionId} engagementId={engagementId} />
          )}
        </div>
      </div>

      {/* AI Copilot Sidebar */}
      <AICopilotSidebar isOpen={aiSidebarOpen} onToggle={() => setAiSidebarOpen(false)} engagementId={engagementId} activeSessionId={activeSessionId} engagementName={engagementName}
        targetContext={aiTargetContext} clearTargetContext={() => setAiTargetContext(null)}
      />
    </div>
  );
}
